import React, { useEffect, useRef, useState } from 'react';
import { tz, localeDate, fmtDateTime, fmtNumber } from '../../utils/region';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { useStore } from '../../store';
import { zoneCoverage, zoneKey, essayIndexes, mapEntryIndex, mapAltText } from './zoningEssay';
import { elevationVariant, plotTooSmallMin, needsElevationEstimate } from './elevationRules';
import RichTextEditor from '../RichTextEditor';
import { commentToHtml } from '../../utils/richText';

// Due Diligence module (Legal). UI is a faithful replica of the original Samui Law
// Firm Cognito form: dark page, centered white card, blue header band, numbered
// stepper, blue section headings, two-column fields, Roboto Condensed. P1 = report
// list (CRUD, soft-delete) + editor with the Parameters section built.

import Fab from '../Fab';
import { RC } from '../../utils/fonts';
import { menuPill } from '../../tabPalette';
import { propCard, PROP_CARD_BG, PROP_CARD_HOVER } from '../../utils/propertyCard';   // the console's green-and-white bubble, shared with POA / Last Will / Property Form / Legal Matters
import { brandOr } from '../../utils/brand';
import { useFirmContact, loadFirmContacts } from '../../utils/firmContact';
import { useDdContent, setDdContentOverride } from '../../utils/ddPoaContent';   // zoning essay / service links / inspectors (tenant settings)
import { landAreaUnits, landAreaM2, landAreaHint, setCountryFeaturesOverride } from '../../utils/countryFeatures';   // land measure = the country pack's
import DraftBody, { DraftBodyHandle } from '../DraftBody';
import { popupsApi } from '../../utils/api/popups';
import { COUNTRIES } from '../../utils/countries';   // the Country dropdown's list, shared with the Property Form
const DDBLUE = '#1F72C0';
// Selected zoning block: the fill is dark enough to carry white text (4.97:1, WCAG AA), and the
// left bar is darker again so it still reads as a distinct edge against that fill — with the bar
// at the same blue as the fill it would simply vanish.
const ZONE_BAR = '#134A80';   // darker left bar, kept below every fill shade so it stays visible
// DEMO for Calem: the highlighted zone paragraphs step through three progressively lighter blues
// (all still WCAG-AA against white text) so he can pick one. Once chosen, collapse to a single value.
// Calem's call: all highlighted zone blocks use the one lighter blue (still legible with white text).
const ZONE_SHADES = ['#95bfe6', '#95bfe6', '#95bfe6'];   // 2026-07-27: 50% lighter than #2a80ce per user
let zoneHlSeen = 0;   // running count of highlighted blocks this render, to pick the shade
const BLUE = '#3D85C6';
const INK = '#434343';
const DARK = '#363636';
const CARD = 1120;   // 2026-08-05: stretch per Calem (was 940; before that 860) — more content per line

// ── The SCREEN's colours: green and white ────────────────────────────────────
// Calem, report d0b44032, 2nd opening ("Change the color back to green and white but keep the bubbles"). The 1st
// opening had painted this screen the SLF Property pink and turned the list from rows in one box into one bubble per
// report; the bubbles stay, the pink goes. So the chrome round the report — the report number on each row, the status
// filter chips and the floating "+" — is the app's green accent again, var(--accent), and a bubble is white with a
// green edge. The console's own header tabs went back with it (SlfProperty).
// The REPORT itself — the Cognito replica, its blue header band, section headings, radios, links and the PDF/print it
// produces — keeps BLUE and DDBLUE above, untouched by either opening: that is the firm's report house style, not a theme.
// A bubble's white fill, its green edge and the palest-green hover are the whole console's now, not this screen's: they
// moved to utils/propertyCard.ts when Calem asked for the same bubbles on POA, Last Will and the Property Form (report
// 19597a46, 3rd opening), so the five lists share one set of values instead of each repeating the arithmetic.

const STATUSES = ['new', 'pending', 'confirmed', 'sent'] as const;
type Status = typeof STATUSES[number];
const STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  new: { bg: 'rgba(255,255,255,.15)', fg: '#e8e8e8' },
  pending: { bg: '#FEF3C7', fg: '#B45309' },
  confirmed: { bg: '#DCFCE7', fg: '#15803D' },
  sent: { bg: '#DBEAFE', fg: '#1D4ED8' },
};

// Sections are dynamic: the conditional ones appear as tabs only when the matching
// Parameters Yes/No answer is "Yes" — exactly like the original Cognito form.
type DDSection = { key: string; tab: string; title: string; cond?: (p: any) => boolean };
const ALL_SECTIONS: DDSection[] = [
  { key: 'parameters', tab: 'Parameters', title: 'Report Parameters' },
  { key: 'buyer', tab: 'Buyer', title: 'Buyer Details' },
  { key: 'seller', tab: 'Seller', title: 'Seller Details' },
  { key: 'property', tab: 'Property', title: 'Property Details' },
  { key: 'site', tab: 'Site Identification', title: 'Site Identification' },
  { key: 'buildingPermit', tab: 'Building Permit', title: 'Building Permit(s)', cond: p => p.buildingPermit === 'Yes' },
  { key: 'houseBook', tab: 'House Book', title: 'House Book(s)', cond: p => p.houseBooks === 'Yes' },
  { key: 'titledeed', tab: 'Title Deed', title: 'Title Deed(s)' },
  { key: 'access', tab: 'Access', title: 'Access / Servitude to the Property' },
  { key: 'purchaseAgreement', tab: 'Purchase Agreement', title: 'Land Office Purchase Agreement(s)', cond: p => p.purchaseAgreement === 'Yes' },
  { key: 'landChronology', tab: 'Land Chronology', title: 'Land Chronology', cond: p => p.landUpgraded === 'Yes' },
  { key: 'liens', tab: 'Liens/Encumbrances', title: 'Liens / Encumbrances on the Property' },
  { key: 'requirements', tab: 'Seller Requirements', title: 'Seller Requirements' },
  { key: 'comments', tab: 'Additional Comments', title: 'Additional Comments' },
];

const fmtDate = (ts: number) => new Date(ts * 1000).toLocaleDateString(localeDate(), { timeZone: tz(), day: '2-digit', month: '2-digit', year: 'numeric' });

// ── Report list (our own management view; Cognito has no list) ───────────────
function ReportList({ onOpen }: { onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState<any[]>([]);
  // Server-clock skew captured at fetch — the client-send countdowns tick on the SERVER's time
  // (owner 2026-08-18: the DD list shows how long until the client email goes out).
  const skewRef = React.useRef(0);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!rows.some(r => r.client_send_due_at > 0 && !r.client_send_sent_at)) return;
    const h = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(h);
  }, [rows]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true); setErr('');
    try { const d: any = await api.ddList(q, status, 1); if (d.now) skewRef.current = Number(d.now) - Date.now() / 1000; setRows(d.rows || []); setTotal(d.total || 0); }
    catch (e: any) { setErr(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { const h = setTimeout(load, 220); return () => clearTimeout(h); }, [q, status]); // eslint-disable-line

  const create = async () => { try { const r: any = await api.ddCreate(); onOpen(r.id); } catch (e: any) { setErr(e.message); } };
  const del = async (id: string) => { if (!window.confirm(t('dd_delete_confirm'))) return; try { await api.ddDelete(id); setRows(prev => prev.filter(r => r.id !== id)); } catch (e: any) { setErr(e.message); } };

  // The status chips dressed by menuPill, like every other page's filter pills: this module has no top-menu button of
  // its own, so that is the plain green-on-white pill — the lit one solid var(--accent) with white words, the rest
  // see-through with a grey outline. They were pink for report d0b44032 and are green again from its 2nd opening.
  const chip = (v: string, label: string) => (
    <button key={v} onClick={() => setStatus(v)} style={{ padding: '5px 12px', fontSize: 12.5, fontWeight: 600, borderRadius: 20, cursor: 'pointer', fontFamily: RC,
      border: '1px solid', ...menuPill('due_diligence', status === v) }}>{label}</button>
  );
  // The list keeps to POA's column instead of running the window's whole width (Calem, report 59cd9573: "The due
  // diligence page could be exactly the same width as the POA page below"): the same 892 cap POA's own list sets
  // (Poa/index.tsx), centred in the page, so the search row, the status chips and the report bubbles all stop where
  // POA's rows do. border-box keeps 892 the OUTER width, this page's padding included, so the two tabs measure the
  // same; `flex: 1` still governs the HEIGHT — alignSelf is the cross axis of the console's column.
  return (
    <div style={{ flex: 1, overflow: 'auto', padding: '18px 20px', fontFamily: RC, width: '100%', maxWidth: 892, alignSelf: 'center', boxSizing: 'border-box' }}>
      {/* Search and the status chips share ONE row (owner 2026-09-25), and creating a report is the
          floating + at the bottom right that every other list module already uses (Fab.tsx) — this
          module and its Property siblings were the ones still carrying a toolbar "New …" button. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'nowrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 140 }}>
          <i className="ti ti-search" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-tertiary)' }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('dd_search')}
            style={{ width: '100%', padding: '10px 12px 10px 36px', fontSize: 13.5, borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', outline: 'none', boxSizing: 'border-box', fontFamily: RC }} />
        </div>
        {/* Scrolls sideways rather than wrapping, so a narrow window never puts the second row back. */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, maxWidth: '62%', overflowX: 'auto' }}>
          {chip('', t('dd_all'))}
          {STATUSES.map(s => chip(s, t('dd_status_' + s)))}
        </div>
      </div>

      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 8, marginBottom: 12 }}>{err}</div>}
      {/* ONE bubble per report, where the whole list was one bordered box of rows parted by thin grey lines (Calem,
          report d0b44032: "change the following due diligence to bubbles rather than just lines", kept by its 2nd
          opening) — the card Tracking and Jobs draw, in the console's own colours: a white fill, a green edge and 10px
          corners (propCard, utils/propertyCard.ts). The cards stand 10px apart on the page, Tracking's gap, so the page
          shows between them; a hovered card takes the palest green (PROP_CARD_HOVER) instead of the app's grey wash.
          The loading spinner and the "no reports" line wear the card too, so neither is left floating on the page.
          Everything on the row keeps its place: the report number, the buyer / property line, the client-email
          countdown, the status chip and the bin. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" style={{ fontSize: 22 }} /></div>
          : rows.length === 0 ? <div style={{ ...propCard, padding: 36, textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>{t('dd_no_reports')}</div>
          : rows.map(r => {
            const c = STATUS_COLOR[r.status] || STATUS_COLOR.new;
            return (
              <div key={r.id} onClick={() => onOpen(r.id)} style={{ ...propCard, display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', cursor: 'pointer' }}
                onMouseEnter={e => (e.currentTarget.style.background = PROP_CARD_HOVER)} onMouseLeave={e => (e.currentTarget.style.background = PROP_CARD_BG)}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', width: 92, flexShrink: 0 }}>{r.report_no}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{r.buyer_name || r.property_name || <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>{t('dd_untitled')}</span>}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.property_name ? `${r.property_name} · ` : ''}{t('dd_updated')} {fmtDate(r.updated_at)} · {r.updated_by_name}</div>
                </div>
                {r.client_send_due_at > 0 && !r.client_send_sent_at && (() => {
                  const remain = Math.max(0, Math.round(r.client_send_due_at - (Date.now() / 1000 + skewRef.current)));
                  const mm = Math.floor(remain / 60), ss = String(remain % 60).padStart(2, '0');
                  return (
                    <span title="The report will be emailed to the client when this countdown ends — any edit restarts the 60-minute hold; cancel from inside the report."
                      style={{ fontSize: 11, fontWeight: 800, padding: '3px 10px', borderRadius: 12, background: '#FFFBEB', border: '1px solid #F1C232', color: '#92400E', whiteSpace: 'nowrap' }}>
                      📤 {remain > 0 ? `Client email in ${mm}:${ss}` : 'Sending to client…'}
                    </span>
                  );
                })()}
                <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 12, background: r.status === 'new' ? 'var(--c-gray-100)' : c.bg, color: r.status === 'new' ? 'var(--text-secondary)' : c.fg, whiteSpace: 'nowrap' }}>{t('dd_status_' + r.status)}</span>
                <button onClick={e => { e.stopPropagation(); del(r.id); }} title={t('delete')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--text-tertiary)' }}><i className="ti ti-trash" /></button>
              </div>
            );
          })}
      </div>
      {total > rows.length && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)', marginTop: 8, textAlign: 'center' }}>{t('dd_more', { n: total - rows.length })}</div>}
      {/* The "+" in the app's green accent — no colour passed, so Fab falls back to it, as it does on every page with
          no top-menu button of its own. It was pink for report d0b44032 and is green again from its 2nd opening. */}
      <Fab onClick={create} title={t('dd_new_report')} />
    </div>
  );
}

// ── Cognito-style field widgets ──────────────────────────────────────────────
const QLabel = ({ children, required }: { children: React.ReactNode; required?: boolean }) => (
  <div style={{ fontSize: 16, fontWeight: 700, color: INK, marginBottom: 8, fontFamily: RC }}>{children}{required && <span style={{ color: '#D0021B', marginLeft: 3 }}>*</span>}</div>
);
function Radios({ name, options, value, onChange }: { name: string; options: string[]; value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap' }}>
      {options.map(o => (
        <label key={o} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 16, color: INK, fontFamily: RC }}>
          <input type="radio" name={name} checked={value === o} onChange={() => onChange(o)} style={{ width: 17, height: 17, accentColor: BLUE, margin: 0, cursor: 'pointer' }} />
          {o}
        </label>
      ))}
    </div>
  );
}
const Q = ({ label, required, name, options, value, onChange, cols = 24 }: { label: string; required?: boolean; name: string; options: string[]; value: string; onChange: (v: string) => void; cols?: number }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name] || '';
  if (ro) return <ROField label={label} cols={cols}>{value || ''}</ROField>;
  return (
    <Cell cols={cols}>
      <QLabel required={required}>{label}</QLabel>
      <Radios name={name} options={options} value={value} onChange={onChange} />
      <ErrBox msg={err} />
    </Cell>
  );
};
const Divider = () => <hr style={{ border: 'none', borderTop: '1px solid #e3e3e3', margin: '26px 0' }} />;
const SubHead = ({ children }: { children: React.ReactNode }) => <div className="dd-subhead" style={{ fontSize: 28, fontWeight: 400, color: BLUE, fontFamily: RC, margin: '4px 0 20px' }}>{children}</div>;
const navBtn = (primary: boolean): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '11px 20px', fontSize: 15, fontWeight: 700, fontFamily: RC, cursor: 'pointer', borderRadius: 5, border: primary ? 'none' : `1px solid ${BLUE}`, background: primary ? BLUE : '#fff', color: primary ? '#fff' : BLUE });
const toolBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', fontSize: 13.5, fontWeight: 700, fontFamily: RC, cursor: 'pointer', borderRadius: 6, border: `1px solid ${BLUE}`, background: '#fff', color: BLUE };

const ddLink: React.CSSProperties = { color: BLUE, textDecoration: 'none', fontFamily: RC };

// ── "More Information": the firm's own service list + contact line ─────────
// Some reports go to a buyer who is not ours to market to (another firm's client, a report bought
// as a one-off), so the cross-sell block is switchable. INCLUDE is the default — leaving the choice
// blank on an existing report must keep it exactly as it reads today. The control itself is
// internal: it is never rendered in the preview or the client's copy, only the effect is.
// The two choices carry the workspace brand ("Include <brand> Info"); a report stores the label it was
// given, so any stored "Exclude …" — whatever brand it was written under — still hides the block.
const firmInfoOpts = (brandWord: string): [string, string] => [`Include ${brandWord} Info`, `Exclude ${brandWord} Info`];
const firmInfoShown = (p: any): boolean => !/^Exclude\s/.test(String(p?.slfInfo || '').trim());
// The firm's market region as the report phrases it: an island ("Koh …" / "Ko …") is "on", a town "in".
const onPlace = (r: string): string => ` ${/^(koh|ko)\s/i.test(r) ? 'on' : 'in'} ${r}`;
// The legal firm's name and market region inside report sentences (Admin → Companies). Components, so
// the summaries re-render once /api/firm-contact/public has answered.
function LegalFirmName({ start, plain }: { start?: boolean; plain?: boolean }) {
  const name = String(useFirmContact('legal')?.name || '').trim();
  // plain: the Additional Comments statement spells the name without its punctuation (old-form parity)
  if (name) return <>{plain ? name.replace(/[.,]/g, '') : name}</>;
  return <>{start ? 'Our firm' : 'our firm'}</>;
}
function LegalFirmRegion({ fmt }: { fmt: (region: string) => string }) {
  const region = String(useFirmContact('legal')?.marketRegion || '').trim();
  return <>{region ? fmt(region) : ''}</>;
}

// ── Parameters section (1:1 with the Cognito page) ───────────────────────────

// ── Internal sign-off chain ─────────────────────────────────────────────────
// Ploypairin submits → Pariya checks → Calem checks → both offices get a heads-up at once and the
// client email is queued 90 minutes later (any edit inside that window restarts the countdown).
// A reviewer's "No" + reason notifies the other two and cancels any queued send. Ticks are set by
// the act of submitting — never by hand.
function SignoffChain({ reportId }: { reportId: string }) {
  const me = useStore(st => st.me);
  const [st, setSt] = useState<any>(null);
  const [busy, setBusy] = useState(0);
  const [err, setErr] = useState('');
  const [rejectFor, setRejectFor] = useState<number | 0>(0);
  const [reason, setReason] = useState('');
  const load = async () => { try { setSt(await api.ddSignoff(reportId)); } catch (e: any) { setErr(e.message); } };
  useEffect(() => { void load(); }, [reportId]); // eslint-disable-line
  useEffect(() => {   // keep the countdown ticking while a client send is pending
    if (!st?.clientSendDueAt) return;
    const h = setInterval(() => setSt((p: any) => ({ ...p })), 30000);
    return () => clearInterval(h);
  }, [st?.clientSendDueAt]);

  // Calem 2026-08-18: the compiler declares whether contracts work follows this DD — mandatory
  // before stage 1 can be submitted; drives the client email's contracts P.S. (yes = blue block,
  // no = omitted). Local pick until submit; once the report has an answer, changing the radio
  // persists immediately (the email reads it at SEND time).
  const [contractsPick, setContractsPick] = useState('');
  const contractsVal = contractsPick || String(st?.contractsNext || '');
  const pickContracts = async (v: 'yes' | 'no') => {
    setContractsPick(v);
    if (String(st?.contractsNext || '')) {   // already answered on the report → persist the change
      try { setSt(await api.ddContractsNext(reportId, v)); } catch (e: any) { setErr(e.message); }
    }
  };
  const act = async (stage: number, decision: 'yes' | 'no', why = '') => {
    if (stage === 1 && decision === 'yes' && !contractsVal) { setErr('Please answer "Are we Drafting or Reviewing Contracts next?" (Yes / No) first.'); return; }
    setBusy(stage); setErr('');
    try { const r = await api.ddSignoffAct(reportId, stage, decision, why, stage === 1 ? contractsVal : ''); setSt(r); setRejectFor(0); setReason(''); }
    catch (e: any) { setErr(e.message); } finally { setBusy(0); }
  };
  const cancelSend = async () => {
    setBusy(-1); setErr('');
    try { setSt(await api.ddCancelClientSend(reportId)); } catch (e: any) { setErr(e.message); } finally { setBusy(0); }
  };
  // Re-queue after a cancel. Without this the report is stuck: all three stages are already 'yes',
  // so no sign-off button can ever fire the send again.
  const queueSend = async () => {
    setBusy(-2); setErr('');
    try { setSt(await api.ddQueueClientSend(reportId)); } catch (e: any) { setErr(e.message); } finally { setBusy(0); }
  };
  if (!st) return <div style={{ fontSize: 13, color: '#888', fontFamily: RC }}>Loading sign-off…</div>;

  const myEmail = String(me?.email || '').toLowerCase();
  const isAdmin = me?.role === 'admin';
  // "Report Compiled By" is open to anyone with Due Diligence access — the same check the server
  // applies, so the button is never drawn for someone who would then be refused.
  const canDd = isAdmin || (me?.modules || []).includes('due_diligence');
  const due = Number(st.clientSendDueAt) || 0;
  const mins = due ? Math.max(0, Math.round((due * 1000 - Date.now()) / 60000)) : 0;
  const hhmm = due ? new Date(due * 1000).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: RC }}>
      {/* An unnamed line (s.anyone — "Report Compiled By") is signed by whoever compiled the
          report: any member of the team, in their own name. Named lines stay with their owner. */}
      {st.stages.map((s: any) => {
        // `canAct` comes from the server, which computed it with the very rule it will enforce —
        // so a button is never drawn that would come back 403. `blockedMsg` says why when it can't.
        // (The local email match is the fallback for a server that predates canAct.)
        const has = (x: any) => (x.emails || [x.email]).some((e: any) => String(e).toLowerCase() === myEmail);
        const mine = s.canAct !== undefined ? s.canAct : (s.anyone ? canDd : has(s)) || isAdmin;
        // Order-free: each line is signed whenever that check has actually been done.
        const myTurn = mine && s.decision !== 'yes';
        const done = s.decision === 'yes';
        const no = s.decision === 'no';
        return (
          <div key={s.n} style={{ borderLeft: `3px solid ${done ? '#16a34a' : no ? '#B42318' : '#ddd'}`, paddingLeft: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>{s.n}. {s.label}:</div>
            <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {done && <span style={{ fontSize: 13, fontWeight: 700, color: '#16a34a' }}>✔ Yes — {s.byName}{s.onBehalf ? ' (on behalf)' : ''} · {s.at ? fmtDateTime(s.at) : ''}</span>}
              {no && <span style={{ fontSize: 13, fontWeight: 700, color: '#B42318' }}>✘ Changes requested — {s.byName}</span>}
              {!done && !no && <span style={{ fontSize: 13, color: '#888' }}>{s.anyone ? 'Not signed yet — whoever compiled the report signs this' : `Waiting for ${s.person}`}</span>}
              {myTurn && rejectFor !== s.n && (
                <>
                  <button onClick={() => act(s.n, 'yes')} disabled={!!busy}
                    style={{ padding: '5px 16px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 6, background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                    {busy === s.n ? '…' : (s.role === 'compiler' ? 'Submit' : 'Yes')}
                  </button>
                  {(
                    <button onClick={() => { setRejectFor(s.n); setReason(''); }} disabled={!!busy}
                      style={{ padding: '5px 16px', fontSize: 13, fontWeight: 700, border: '1px solid #B42318', borderRadius: 6, background: '#fff', color: '#B42318', cursor: 'pointer' }}>No</button>
                  )}
                </>
              )}
              {/* Say why there is no button, instead of showing a dead line. */}
              {!myTurn && !done && s.blockedMsg && (
                <span style={{ fontSize: 12, color: '#B45309', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '3px 9px' }}>
                  <i className="ti ti-lock" style={{ marginRight: 4 }} />{s.blockedMsg}
                </span>
              )}
            </div>
            {/* Compiler's mandatory contracts question (Calem 2026-08-18) — stage 1 only. Radios
                stay live after signing so the answer can be corrected before the client send. */}
            {s.n === 1 && (
              <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 6, padding: '7px 12px' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: INK }}>
                  Are we Drafting or Reviewing Contracts next?<span style={{ color: '#D0021B' }}> *</span>
                </span>
                {(['yes', 'no'] as const).map(v => (
                  <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, cursor: mine ? 'pointer' : 'default', fontWeight: contractsVal === v ? 700 : 400, color: contractsVal === v ? (v === 'yes' ? '#1D4ED8' : '#B42318') : '#555' }}>
                    <input type="radio" name={`contracts-${reportId}`} checked={contractsVal === v} disabled={!mine} onChange={() => pickContracts(v)} />
                    {v === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
                {contractsVal === 'yes' && <span style={{ fontSize: 11.5, color: '#1D4ED8' }}>Client email will include the contracts P.S. (highlighted in blue)</span>}
                {contractsVal === 'no' && <span style={{ fontSize: 11.5, color: '#B42318' }}>Contracts P.S. will be omitted from the client email</span>}
                {!contractsVal && !mine && <span style={{ fontSize: 11.5, color: '#888' }}>(not answered — legacy report)</span>}
              </div>
            )}
            {no && s.reason && (
              <div style={{ marginTop: 6, padding: '8px 12px', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 6, fontSize: 13, color: '#B42318', whiteSpace: 'pre-wrap' }}>{s.reason}</div>
            )}
            {rejectFor === s.n && (
              <div style={{ marginTop: 8 }}>
                <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} autoFocus
                  placeholder="What needs to be changed? This is emailed to the other two reviewers."
                  style={{ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #ddd', borderRadius: 6, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <button onClick={() => act(s.n, 'no', reason)} disabled={!reason.trim() || !!busy}
                    style={{ padding: '6px 16px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 6, background: '#B42318', color: '#fff', cursor: reason.trim() ? 'pointer' : 'not-allowed', opacity: reason.trim() ? 1 : 0.5 }}>Send back for changes</button>
                  <button onClick={() => setRejectFor(0)} style={{ padding: '6px 14px', fontSize: 13, border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {due > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: '#1D4ED8' }}>
            📤 Will be sent to the client at {hhmm} (in {mins} min). Any edit restarts the 60-minute hold.
          </span>
          <button onClick={cancelSend} disabled={busy === -1}
            style={{ padding: '5px 14px', fontSize: 13, fontWeight: 700, border: '1px solid #B42318', borderRadius: 6, background: '#fff', color: '#B42318', cursor: 'pointer' }}>
            {busy === -1 ? '…' : 'Cancel send'}
          </button>
        </div>
      )}
      {/* All three in, nothing queued, nothing sent — i.e. the send was cancelled. Offer it back. */}
      {st.allPassed && due === 0 && !st.clientSendSentAt && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 14px', background: '#F0FDF4', border: '1px solid #A7E0C0', borderRadius: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: '#15803D' }}>All three sign-offs are in. The client send is not queued.</span>
          <button onClick={queueSend} disabled={busy === -2}
            style={{ padding: '5px 14px', fontSize: 13, fontWeight: 700, border: 'none', borderRadius: 6, background: '#16a34a', color: '#fff', cursor: 'pointer', opacity: busy === -2 ? 0.6 : 1 }}>
            {busy === -2 ? '…' : 'Send to client (60-min hold)'}
          </button>
        </div>
      )}
      {!st.allPassed && (
        <div style={{ fontSize: 12.5, color: '#888' }}>
          The report cannot be sent to the client until all three sign-offs are in.
        </div>
      )}
      {st.clientSendSentAt > 0 && (
        <div style={{ padding: '9px 14px', background: '#E7F6EE', border: '1px solid #A7E0C0', borderRadius: 8, fontSize: 13.5, fontWeight: 700, color: '#15803D' }}>
          ✔ Sent to the client
          {st.clientSendTo ? <> (<span style={{ color: '#DB2777', fontWeight: 800 }}>{st.clientSendTo}</span>)</> : null}
          {' '}on {fmtDateTime(st.clientSendSentAt)}
        </div>
      )}
      {err && <div style={{ color: '#B42318', fontSize: 13 }}>{err}</div>}
    </div>
  );
}

function ParametersSection({ data, set }: { data: any; set: (key: string, value: any) => void }) {
  const roView = React.useContext(DDReadOnly);
  const legalFirm = useFirmContact('legal');   // the report footer's contact line + the market region
  const { services } = useDdContent();          // the firm's service links (tenant setting)
  const infoOpts = firmInfoOpts(brandOr('Firm'));
  const p = data.parameters || {};
  const YN = ['Yes', 'No'];
  const YNY = ['Yes', 'Not Yet'];
  return (
    <>
      {/* Issued date row — read-only report: ONE flowing line, sentence + bold date on the same
          baseline (the editor keeps the label-above-input layout for data entry). */}
      {roView ? (
        <Row>
          <Cell cols={12}><div /></Cell>
          <Cell cols={12}><div style={{ fontSize: 16, color: BLUE, fontFamily: RC, paddingTop: 8 }}>
            This Due Diligence Report was issued: <strong>{fmtLongDate(p.issueDate)}</strong>
          </div></Cell>
        </Row>
      ) : (
        <Row>
          <Cell cols={18}><div style={{ textAlign: 'right', fontSize: 16, color: INK, fontFamily: RC, paddingTop: 28 }}>This Due Diligence Report was issued:</div></Cell>
          <Cell cols={6}><QLabel required>Date</QLabel><IssueDateInput value={p.issueDate} onChange={v => set('issueDate', v)} /></Cell>
        </Row>
      )}
      <Divider />
      <Row>
        <Q cols={12} label="Buying Property From?" required name="buyingPropertyFrom" options={['Company', 'Individual', 'Both']} value={p.buyingPropertyFrom || ''} onChange={v => set('buyingPropertyFrom', v)} />
        <Q cols={12} label="Property Has Building Permit(s)?" required name="buildingPermit" options={YN} value={p.buildingPermit || ''} onChange={v => set('buildingPermit', v)} />
      </Row>
      <Row>
        <Q cols={12} label="Property Has House Book(s)?" required name="houseBooks" options={YN} value={p.houseBooks || ''} onChange={v => set('houseBooks', v)} />
        <Q cols={12} label="Land Has Been Upgraded or Subdivided?" required name="landUpgraded" options={YN} value={p.landUpgraded || ''} onChange={v => set('landUpgraded', v)} />
      </Row>
      <Row>
        <Q cols={12} label={'Property Has "Land Office" Purchase Agreement(s)?'} required name="purchaseAgreement" options={YN} value={p.purchaseAgreement || ''} onChange={v => set('purchaseAgreement', v)} />
        <Q cols={12} label="Property Has (or Requires) Private Road Registration(s)?" required name="privateRoad" options={YN} value={p.privateRoad || ''} onChange={v => set('privateRoad', v)} />
      </Row>
      <Divider />
      <SubHead>More Information</SubHead>
      <p style={{ fontSize: 15, color: INK, lineHeight: 1.6, fontFamily: RC, margin: '0 0 12px' }}>
        A thorough investigation [due diligence] usually takes around 2-3 weeks to complete and most of it will be conducted using resources and documents available from the land and/or building departments{legalFirm?.marketRegion ? onPlace(legalFirm.marketRegion) : ''}. We need to verify that the seller, the property [and the company, if applicable], meet all of the legal requirements prior to you engaging in any formal contractual arrangement.
        {/* The lead-in belongs to the list — without it the paragraph would end on a colon
            introducing nothing (so no list, no lead-in). */}
        {firmInfoShown(p) && services.length ? ' Other services we offer include:' : ''}
      </p>
      {/* Internal control: switches the block below. Never rendered in the preview or the client's
          copy — `Q` would otherwise print the chosen option as a read-only line in the report. */}
      {!roView && (
        <Row>
          <Q cols={24} name="slfInfo" options={infoOpts}
            label={`Include ${brandOr('our')} service list & contact details in this report? (internal use only)`}
            value={firmInfoShown(p) ? infoOpts[0] : infoOpts[1]} onChange={v => set('slfInfo', v)} />
        </Row>
      )}
      {firmInfoShown(p) && (
        <>
          {services.length > 0 && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', fontSize: 15, fontFamily: RC, margin: '0 0 14px' }}>
            {services.map(s => <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" style={ddLink}>{s.name}</a>)}
          </div>}
          <p style={{ fontSize: 15, color: INK, lineHeight: 1.6, fontFamily: RC, margin: '0 0 6px' }}>
            {/* The legal firm's own phone / email / website (Admin → Companies); a value not set is left out. */}
            {[legalFirm?.phone ? <>Phone: {legalFirm.phone}</> : null,
              legalFirm?.email ? <>Email: <a href={`mailto:${legalFirm.email}`} style={ddLink}>{legalFirm.email}</a></> : null,
              legalFirm?.website ? <>Website: <a href={legalFirm.website} target="_blank" rel="noopener noreferrer" style={ddLink}>{legalFirm.website}</a></> : null,
            ].filter(Boolean).map((x, i) => <React.Fragment key={i}>{i ? ' · ' : ''}{x}</React.Fragment>)}
          </p>
        </>
      )}
      <Divider />
      {/* The Internal QA sign-off lives on the LAST page and is internal-only — it is never
          rendered in the client's read-only report. See ReportEditor. */}
      {/* Heading on its own line, as in the original Cognito form. 2026-07-28: slightly smaller
          type, and the same light-blue highlight block as the zoning/summary style. 2026-09-24
          (Calem): heading and paragraph are centred in the box (Cognito had them left-aligned), and
          the heading reads IMPORTANT NOTE — all capitals, no trailing colon. */}
      {/* dd-important: RED heading, WHITE bold body — this ONE block only (Calem 2026-08-10). The
          client report's blanket rule paints every .dd-sum descendant #434343, so the colours are
          set through a class that can out-specify it rather than inline styles. */}
      <div className="dd-important" style={{ fontSize: 17, color: INK, lineHeight: 1.75, fontFamily: RC, background: ZONE_SHADES[0], borderLeft: `4px solid ${ZONE_BAR}`, borderRadius: 6, padding: '22px 26px', margin: '18px 0 0', textAlign: 'center', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
        <b className="dd-important-title" style={{ fontSize: 18, color: '#D0021B' }}>IMPORTANT NOTE</b><br />
        {/* 15px, not the block's 17px: at 17 this paragraph runs to three lines. 16 just fits two,
            but with no margin — a narrower window or one more word tips it back to three. */}
        <span className="dd-important-body" style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>
          Given that this report contains 'PRIVATE and CONFIDENTIAL' information specifically (and ONLY) for the Buyer(s) and/or Lessee(s) stated under "Buyer Details", under NO circumstances shall any other party or parties be granted permission to use this report for their private and/or public use!
        </span>
      </div>
    </>
  );
}

// ── Exact option lists (from the original Cognito form definition) ───────────
const NUM_1_3 = ['One', 'Two', 'Three'];
const NUM_1_2 = ['One', 'Two'];
const ENTITY_TYPE = ['Individual', 'Individual & Company', 'Company'];
const SELLER_IS = ['Owner', 'Co-owner', 'Lessee', 'Superficies', "Owner's Representative"];
const LAND_OR_BUILDINGS = ['Land', 'Building', 'Buildings', 'Land and Building', 'Land and Buildings'];
const LAND_ANDOR_BUILDINGS = ['land only', 'building only', 'land and building', 'land and buildings', 'condominium'];
const NUM_DIRECTORS = ['one', 'two', 'three', 'four'];
const MAX_USAGE = ['10%', '20%', '25%', '30%', '40%', '50%', '60%', '70%', '80%', '90%'];
const LAND_ELEVATION = ['Zone 1 (sea level)', 'Zone 2 (between 0-80m above sea level)', 'Zone 3.1 (between 80-140m above sea level)', 'Zone 3.2 (greater than 140m above sea level)'];
const SLOPE = ['less than 35 degrees (slope)', 'greater than 35 degrees (slope)'];
// Thai companies file their annual return for the CALENDAR financial year, due on or before 31 May
// of the following year — so the most recent year the Revenue Department requires is always the
// PREVIOUS calendar year. Derived here instead of hard-coded, so the report never goes stale.
const beYear = (gy: number) => gy + 543;                      // Gregorian → Buddhist Era
function lastFinancialYear(ref: Date = new Date()) {
  const fy = ref.getFullYear() - 1;
  return { fy, fyBE: beYear(fy), dueYear: fy + 1, dueYearBE: beYear(fy + 1) };
}

const YEARS = Array.from({ length: lastFinancialYear().fy - 2000 }, (_, i) => {
  const y = 2001 + i;
  return `${y} (${beYear(y)})`;
});
const BALANCE_AUDIT = ['Never (less than one year of trading)', ...YEARS];
// Year Established in the live reference starts at 2014 and runs to the current
// year (Gregorian + Buddhist), so a company founded long ago isn't selectable here.
// From 2000 (owner 2026-09-05): United Grace was established 2013 and the old range began at 2014,
// so the import's correct value had no legal option and was dropped.
const YEAR_ESTABLISHED = (() => { const end = Math.max(2025, new Date().getFullYear()); const out: string[] = []; for (let y = 2000; y <= end; y++) out.push(`${y} (${y + 543})`); return out; })();
// ── Suitable For follows the zone (owner 2026-08-10) ────────────────────────
// SUITABLE_FOR is written in zone order: option 1 describes zone 1, option 2 zone 2 … option 12
// zone 12, so the pairing is the option's own index. The ZONING list names some zones by colour
// only ("Yellow Zone", "Dark Green Zone"), hence the colour fallback — and "dark green" must be
// tested BEFORE "green" or it would match the wrong one.
const ZONE_COLOUR_NO: Array<[RegExp, number]> = [
  [/dark\s*green/i, 8], [/yellow/i, 1], [/orange/i, 2], [/red/i, 3], [/purple/i, 5], [/green/i, 6],
];

/** Major zone number of a Zoning selection: "Green Zone 6.3" → 6, "Dark Green Zone" → 8, else 0. */
export function zoneMajor(zoning: string): number {
  const z = String(zoning || '');
  const m = /(\d+)/.exec(z);
  if (m) { const n = Number(m[1]); if (n >= 1 && n <= 12) return n; }
  for (const [re, n] of ZONE_COLOUR_NO) if (re.test(z)) return n;
  return 0;
}

/** The Suitable For wording that goes with a zone — '' when the zone is unknown. */
function suitableForZone(zoning: string): string {
  const n = zoneMajor(zoning);
  return n >= 1 && n <= SUITABLE_FOR.length ? SUITABLE_FOR[n - 1] : '';
}

// ── Standing rule on maximum usage (Pariya, 2026-08-09) ─────────────────────
// Every COLOURED zone is 50% usage as long as the ordinary parameters hold: the land sits in
// elevation Zone 2 (0–80 m) and slopes at less than 35°. RED is deliberately left out until Pariya
// has double-checked it — until then a red-zone property keeps being chosen by hand, so a wrong
// figure can never be written into a report automatically (owner decision, same day).
const AUTO_USAGE_VALUE = '50%';
const AUTO_USAGE_ELEVATION = 'Zone 2 (between 0-80m above sea level)';
const AUTO_USAGE_SLOPE = 'less than 35 degrees (slope)';
const AUTO_USAGE_EXCLUDED_ZONE = /red/i;   // ⚠ remove once Pariya confirms the red zone

/** Do this property's parameters fall under the standing 50% rule? */
function autoUsageApplies(p: any): boolean {
  const zoning = String(p?.zoning || '').trim();
  if (!zoning || AUTO_USAGE_EXCLUDED_ZONE.test(zoning)) return false;
  return String(p?.landElevation || '') === AUTO_USAGE_ELEVATION
      && String(p?.slope || '') === AUTO_USAGE_SLOPE;
}

const ZONING = ['Yellow Zone', 'Yellow Zone 1.1', 'Yellow Zone 1.19', 'Yellow Zone 1.2', 'Yellow Zone 1.25', 'Yellow Zone 1.3', 'Yellow Zone 1.5', 'Yellow Zone 1.6', 'Orange Zone', 'Orange Zone 2.1', 'Orange Zone 2.2', 'Orange Zone 2.3', 'Orange Zone 2.4', 'Red Zone 3.1', 'Purple Zone 5.1', 'Purple Zone 5.2', 'Green Zone', 'Green Zone 6.1', 'Green Zone 6.1 (Beach Front)', 'Green Zone 6.2', 'Green Zone 6.3', 'Green Zone 6.4', 'Green Zone 6.5', 'Green Zone 6.6', 'Green Zone 6.7', 'Green Zone 6.8', 'Dark Green Zone'];
const SUITABLE_FOR = [
  'low-density residential areas and, land in these zones shall be used mainly for residential and tourism purposes',
  'medium-density residential property areas. Land here will be used mainly for residential and tourism purposes',
  'high-density residential and commercial areas. Land here will be used predominately for commerce, residential and tourism purposes',
  'industrial and warehouse area. Land here is to be used primarily for the purpose of industry',
  'specific industrial and warehouse areas. And land in these zones is to be used for the purpose of a specific industry',
  'agricultural purposes, or any activity relating to agriculture, residence and tourism',
  'kept open for public use and for the maintenance of the environment. Land in these zones is to be used only for the purposes of recreation',
  'reserved forest areas, where the land is to be used for the protection and preservation of the forest, wild animals, water resources and any other natural resource',
  'educational institutes. Land here is to be used for educational purposes or any activity relating to education',
  'tourism, fishing and the maintenance of the surrounding environment. Land here is to be used for the purposes of the preservation of the environment',
  'religious purposes. Land in these zones is to be used for the purpose of religion or any activity relating to religion',
  'government offices and public utilities. And the land in these zones is to be used for the purposes of state operations',
];
const COUNT: Record<string, number> = { One: 1, Two: 2, Three: 3, one: 1, two: 2, three: 3, four: 4 };

// ── Cognito-style input widgets ──────────────────────────────────────────────
const inStyle: React.CSSProperties = { width: '100%', padding: '9px 11px', fontSize: 15, fontFamily: RC, border: '1px solid #d6d6d6', background: '#ededed', color: INK, boxSizing: 'border-box', borderRadius: 0 };
// Red outline on a share-dialog box that was left blank when Send was pressed.
const shareErrInput: React.CSSProperties = { border: '1px solid #D0021B', background: '#FFF6F6' };
const hintStyle: React.CSSProperties = { color: '#999', fontSize: 14, fontFamily: RC, padding: '10px 0' };
// 24-column grid row + cell — matches the original Cognito layout (e.g. Name=12, Job ID=6, Email=6).
const Row = ({ children }: { children: React.ReactNode }) => (
  <div className="dd-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(24, 1fr)', columnGap: 24, rowGap: 18, alignItems: 'start', marginBottom: 18 }}>{children}</div>
);
const Cell = ({ cols = 24, children }: { cols?: number; children: React.ReactNode }) => (
  <div className="dd-cell" style={{ gridColumn: `span ${cols}`, minWidth: 0 }}>{children}</div>
);
// Read-only mode (preview / public client view): inputs render their value as
// static text instead of an editable control. Every widget below honours it.
const DDReadOnly = React.createContext(false);
// Per-field validation errors, keyed by field path. Provided by the editor only
// after a navigation attempt; each widget shows its own inline message.
const DDErrors = React.createContext<Record<string, string>>({});
const errBoxStyle: React.CSSProperties = { background: '#D0021B', color: '#fff', fontSize: 13, fontFamily: RC, padding: '8px 12px', borderRadius: 4, marginTop: 6, lineHeight: 1.35 };
const errInput = (e?: string): React.CSSProperties => e ? { borderColor: '#D0021B', background: '#fdecec' } : {};
const ErrBox = ({ msg }: { msg?: string }) => msg ? <div style={errBoxStyle}>{msg}</div> : null;
const roLabel: React.CSSProperties = { fontSize: 14.5, fontWeight: 700, color: '#000', fontFamily: RC, marginBottom: 2 };
const roVal: React.CSSProperties = { padding: '7px 0', fontSize: 15, color: INK, fontFamily: RC, minHeight: 18, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', borderBottom: '1px solid #eee' };
const ROField = ({ label, cols = 24, children }: { label?: string; cols?: number; children?: React.ReactNode }) => (
  <Cell cols={cols}>{label ? <div style={roLabel}>{label}</div> : null}<div style={roVal}>{children != null && children !== '' ? children : <span style={{ color: '#c4c4c4' }}>—</span>}</div></Cell>
);
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const fmtLongDate = (s: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  const mi = m ? Number(m[2]) - 1 : -1;
  return m && MONTH_LONG[mi] ? `${m[3]} ${MONTH_LONG[mi]} ${m[1]}` : String(s || '');
};
const fmtDMY = (s: any) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || '')); return m ? `${m[3]}/${m[2]}/${m[1]}` : (s || ''); };
const Text = ({ label, required, value, onChange, type = 'text', cols = 24, name }: { label: string; required?: boolean; value: any; onChange: (v: string) => void; type?: string; cols?: number; name?: string }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  if (ro) return <ROField label={label} cols={cols}>{value || ''}</ROField>;
  return <Cell cols={cols}><QLabel required={required}>{label}</QLabel><input type={type} value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inStyle, ...errInput(err) }} /><ErrBox msg={err} /></Cell>;
};
// Money shown to the reader always carries thousand separators; the stored value stays a plain
// number string so calculations and the API are unaffected.
// Only the integer part is grouped; the decimal part is passed through exactly as typed so that
// "24208.00" reads "24,208.00" (not "24,208") and a half-typed "24208." keeps its dot.
const thb = (v: any) => {
  const s = String(v ?? '').trim().replace(/,/g, '');
  if (s === '') return '';
  const m = /^(-?)(\d*)(\.\d*)?$/.exec(s);
  if (!m) return String(v ?? '');
  const int = m[2] ? fmtNumber(m[2]) : '';
  return `${m[1]}${int}${m[3] ?? ''}`;
};

const Num = ({ label, required, value, onChange, cols = 24, readOnly, hint, name, dp }: { label: string; required?: boolean; value: any; onChange: (v: string) => void; cols?: number; readOnly?: boolean; hint?: string; name?: string; dp?: number }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  const shown = dp != null && String(value ?? '').trim() !== '' && Number.isFinite(Number(String(value).replace(/,/g, '')))
    ? fmtNumber(String(value).replace(/,/g, ''), dp)
    : thb(value);
  if (ro) return <ROField label={label} cols={cols}>{shown}</ROField>;
  // inputMode=numeric + a text box so the value can be shown grouped (1,234,567). Only digits, a
  // single dot and a leading minus are kept; the value handed back stays comma-free.
  return <Cell cols={cols}><QLabel required={required}>{label}</QLabel><input type="text" inputMode="numeric" value={shown} readOnly={readOnly} onChange={e => onChange(e.target.value.replace(/[^0-9.-]/g, ''))} style={{ ...inStyle, ...(readOnly ? { background: '#e3e3e3', color: '#666', cursor: 'default' } : {}), ...errInput(err) }} />{hint && <div style={{ fontSize: 12, color: '#999', fontFamily: RC, marginTop: 4 }}>{hint}</div>}<ErrBox msg={err} /></Cell>;
};
const Sel = ({ label, required, value, onChange, options, cols = 24, name, note, noteTone = 'info' }: { label: string; required?: boolean; value: any; onChange: (v: string) => void; options: string[]; cols?: number; name?: string; note?: string; noteTone?: 'info' | 'warn' | 'error' }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  // `note` is guidance for the person filling the form (e.g. the standing 50%-usage rule). It never
  // reaches the client report — read-only mode returns before it.
  if (ro) return <ROField label={label} cols={cols}>{value || ''}</ROField>;
  return <Cell cols={cols}><QLabel required={required}>{label}</QLabel>
    <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inStyle, ...errInput(err) }}>
      <option value=""></option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
    {note && <div style={{ marginTop: 5, fontSize: 12, lineHeight: 1.5, fontFamily: RC,
      color: noteTone === 'error' ? '#FFFFFF' : noteTone === 'warn' ? '#9A6700' : '#6b7c8a',
      background: noteTone === 'error' ? '#C0392B' : noteTone === 'warn' ? '#FFF9EC' : 'transparent',
      border: noteTone === 'error' ? '1px solid #A93226' : noteTone === 'warn' ? '1px solid #F0C36D' : 'none',
      borderRadius: noteTone !== 'info' ? 4 : 0, padding: noteTone !== 'info' ? '6px 9px' : 0, fontWeight: noteTone === 'error' ? 700 : undefined }}>
      {noteTone !== 'info' && <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />}{note}
    </div>}
    <ErrBox msg={err} />
  </Cell>;
};
const NameF = ({ label, required, value, onChange, cols = 24, name, inlineLast = false }: { label: string; required?: boolean; value: any; onChange: (v: any) => void; cols?: number; name?: string; inlineLast?: boolean }) => {
  const v = value || {};
  const set = (k: string) => (e: any) => onChange({ ...v, [k]: e.target.value });
  const ro = React.useContext(DDReadOnly); const errs = React.useContext(DDErrors);
  const ef = errs[`${name}.first`] || ''; const el = errs[`${name}.last`] || '';
  if (ro) return <ROField label={label} cols={cols}>{[v.title, v.first, v.last].filter(Boolean).join(' ')}</ROField>;
  // inlineLast: Last joins the same row (used when hiding the email frees the width — Calem 2026-08-05).
  if (inlineLast) return (
    <Cell cols={cols}><QLabel required={required}>{label}</QLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr 1fr', gap: 14 }}>
        <input placeholder="Title" value={v.title || ''} onChange={set('title')} style={inStyle} />
        <input placeholder="First" value={v.first || ''} onChange={set('first')} style={{ ...inStyle, ...errInput(ef) }} />
        <input placeholder="Last" value={v.last || ''} onChange={set('last')} style={{ ...inStyle, ...errInput(el) }} />
      </div>
      <ErrBox msg={ef} /><ErrBox msg={el} />
    </Cell>
  );
  return (
    <Cell cols={cols}><QLabel required={required}>{label}</QLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 14, marginBottom: ef ? 6 : 14 }}>
        <input placeholder="Title" value={v.title || ''} onChange={set('title')} style={inStyle} />
        <input placeholder="First" value={v.first || ''} onChange={set('first')} style={{ ...inStyle, ...errInput(ef) }} />
      </div>
      <ErrBox msg={ef} />
      <input placeholder="Last" value={v.last || ''} onChange={set('last')} style={{ ...inStyle, marginTop: ef ? 14 : 0, ...errInput(el) }} />
      <ErrBox msg={el} />
    </Cell>
  );
};
const EmailF = ({ label, required, value, onChange, cols = 24, name }: { label?: string; required?: boolean; value: any; onChange: (v: string) => void; cols?: number; name?: string }) => (
  <Text label={label || 'Email'} required={required} value={value} onChange={onChange} type="email" cols={cols} name={name} />
);
// `fixedCountry` drops the Country picker and pins the value — used on the Property page, where the
// property is by definition in Thailand. The country is still STORED so the printed report reads
// "…, 84320, Thailand" exactly as before; it just isn't asked for.
// The report's own issue date. Not a generic DateF: it sits inside a bespoke row in
// ParametersSection, so it just needs the same red-outline + message treatment as every other field.
const IssueDateInput = ({ value, onChange }: { value: any; onChange: (v: string) => void }) => {
  const err = React.useContext(DDErrors)['issueDate'] || '';
  return (<>
    <input type="date" value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inStyle, ...errInput(err) }} />
    <ErrBox msg={err} />
  </>);
};

const AddressF = ({ label, required, value, onChange, cols = 24, name, fixedCountry }: { label?: string; required?: boolean; value: any; onChange: (v: any) => void; cols?: number; name?: string; fixedCountry?: string }) => {
  const v = fixedCountry ? { ...(value || {}), country: fixedCountry } : (value || {});
  const set = (k: string) => (e: any) => onChange({ ...v, [k]: e.target.value });
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  if (ro) return <ROField label={label || 'Address'} cols={cols}>{[v.line1, v.city, v.postal, v.country].filter(Boolean).join(', ')}</ROField>;
  return (
    <Cell cols={cols}><QLabel required={required}>{label || 'Address'}</QLabel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <input placeholder="Address Line 1" value={v.line1 || ''} onChange={set('line1')} style={{ ...inStyle, ...errInput(err) }} />
        <div style={{ display: 'grid', gridTemplateColumns: fixedCountry ? 'minmax(0, 1fr) minmax(0, 1.35fr)' : 'minmax(0, 1fr) minmax(0, 1.35fr) minmax(0, 1fr)', gap: 14 }}>
          <input placeholder="City" value={v.city || ''} onChange={set('city')} style={{ ...inStyle, ...errInput(err) }} />
          <input placeholder="Postal / Zip Code" value={v.postal || ''} onChange={set('postal')} style={{ ...inStyle, ...errInput(err) }} />
          {!fixedCountry && (
            <select value={v.country || ''} onChange={set('country')} style={{ ...inStyle, ...errInput(err) }}>
              <option value="">Country</option>
              {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
        </div>
      </div>
      <ErrBox msg={err} />
    </Cell>
  );
};
// reportId flows down so the upload widgets know where to attach files. In the
// public preview, publicToken is set instead — files are read via the public API.
const DDContext = React.createContext<{ reportId: string; publicToken?: string; refreshKey?: number; onFileChange?: () => void }>({ reportId: '' });
const isImg = (m: string) => /^image\//.test(m || '');
const MAX_UPLOAD = 10 * 1024 * 1024; // 10 MB
const fmtSize = (n: number) => n >= 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
const fileIcon = (mime: string) => {
  if (/pdf/.test(mime)) return { icon: 'ti-file-type-pdf', color: '#D0021B' };
  if (/word|msword|document/.test(mime)) return { icon: 'ti-file-type-doc', color: '#2B579A' };
  if (/excel|sheet/.test(mime)) return { icon: 'ti-file-type-xls', color: '#1D6F42' };
  if (isImg(mime)) return { icon: 'ti-photo', color: '#888' };
  return { icon: 'ti-file', color: '#888' };
};
// Small attachment thumbnail — real image preview (public URL, or authed blob),
// otherwise a file-type icon. Matches the original Cognito attachment row.
// Attachments in the READ-ONLY report (Calem 2026-08-06, extended to PDFs 2026-08-08): a uniform
// box so every attachment lines up — no filename, no size. A PDF shows its OWN first page, not a
// generic red icon: the server has no rasteriser (no poppler / ghostscript / ImageMagick), so
// pdf.js renders it in the browser, which also works on the tokenless client link. Clicking opens
// an in-page lightbox (Esc / × / backdrop closes); never a new tab.
// 224×176 = 4× the editor thumbnail (56×44), doubled again on 2026-08-08 so most readers can take
// in a permit or a chanote without opening anything. The PDF page is rendered at this width too
// (pdfPageDataUrl below takes RO_IMG_W), so the bigger box is genuinely sharper, not upscaled.
const RO_IMG_W = 224, RO_IMG_H = 176;
const isPdfMime = (m: string) => /pdf/i.test(m || '');

// pdf.js is loaded on demand — a 1 MB parser has no business in the initial bundle of a report that
// usually has none. The worker ships as a static file so CRA's build needs no special handling.
let pdfLibP: Promise<any> | null = null;
function getPdfLib(): Promise<any> {
  if (!pdfLibP) {
    pdfLibP = import('pdfjs-dist/legacy/build/pdf').then((m: any) => {
      const lib = m.default || m;
      lib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      return lib;
    });
  }
  return pdfLibP;
}

// Render one PDF page to a data-URL at roughly `targetW` CSS pixels wide (×2 for retina).
async function pdfPageDataUrl(data: ArrayBuffer, pageNo: number, targetW: number): Promise<{ url: string; pages: number }> {
  const pdfjs = await getPdfLib();
  // ⛔ isEvalSupported: false (review 2026-11-03): utils/pdfRender.ts says why. An uploaded PDF is whatever somebody uploaded, and this reader runs on the app's own origin.
  const doc = await pdfjs.getDocument({ data: data.slice(0), isEvalSupported: false }).promise;
  const page = await doc.getPage(pageNo);
  const base = page.getViewport({ scale: 1 });
  const scale = Math.min(4, Math.max(0.2, (targetW * 2) / base.width));
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no 2d context');
  await page.render({ canvasContext: ctx, viewport }).promise;
  const url = canvas.toDataURL('image/jpeg', 0.9);
  const pages = doc.numPages;
  try { doc.destroy(); } catch { /* best effort */ }
  return { url, pages };
}

const Spinner = ({ size = 34, light = false }: { size?: number; light?: boolean }) => (
  <span style={{
    display: 'inline-block', width: size, height: size, borderRadius: '50%',
    border: `${Math.max(2, Math.round(size / 12))}px solid ${light ? 'rgba(255,255,255,.28)' : 'rgba(0,0,0,.14)'}`,
    borderTopColor: light ? '#fff' : '#888', animation: 'ddSpin .8s linear infinite',
  }} />
);

function FileImageLarge({ f, publicToken }: { f: any; publicToken?: string }) {
  const pdf = isPdfMime(f.mime);
  const [src, setSrc] = useState('');            // thumbnail (image URL, or rendered PDF page 1)
  const [thumbBusy, setThumbBusy] = useState(true);
  const [open, setOpen] = useState(false);
  const [bigBusy, setBigBusy] = useState(false); // click → render/load the large view
  const [big, setBig] = useState<string[]>([]);  // PDF: one data-URL per page
  const bufRef = React.useRef<ArrayBuffer | null>(null);

  // Fetch once; images use the URL directly, PDFs keep the bytes to render from.
  useEffect(() => {
    let live = true; let obj = '';
    setThumbBusy(true); setSrc(''); setBig([]); bufRef.current = null;
    const url = publicToken ? api.ddPublicFileUrl(publicToken, f.id) : '';
    if (!pdf) {
      if (url) { setSrc(url); setThumbBusy(false); return; }
      api.ddFileObjectUrl(f.id).then((u: string) => { if (live) { obj = u; setSrc(u); setThumbBusy(false); } else URL.revokeObjectURL(u); })
        .catch(() => { if (live) setThumbBusy(false); });
      return () => { live = false; if (obj) setTimeout(() => URL.revokeObjectURL(obj), 2000); };
    }
    (async () => {
      try {
        const href = url || await api.ddFileObjectUrl(f.id);
        if (!url) obj = href;
        const buf = await (await fetch(href)).arrayBuffer();
        if (!live) return;
        bufRef.current = buf;
        const { url: thumb } = await pdfPageDataUrl(buf, 1, RO_IMG_W);
        if (live) { setSrc(thumb); setThumbBusy(false); }
      } catch { if (live) setThumbBusy(false); }
    })();
    return () => { live = false; if (obj) setTimeout(() => URL.revokeObjectURL(obj), 2000); };
  }, [f.id, publicToken, pdf]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [open]);

  // The click itself shows a spinner and locks out repeat clicks — rendering a big PDF page is not
  // instant, and without feedback people click again and again (firm rule: slow buttons spin).
  const openBig = async () => {
    if (bigBusy) return;
    if (!pdf) { setOpen(true); return; }
    if (big.length) { setOpen(true); return; }
    if (!bufRef.current) return;
    setBigBusy(true);
    try {
      const first = await pdfPageDataUrl(bufRef.current, 1, 1000);
      const urls = [first.url];
      setBig(urls); setOpen(true);
      // Remaining pages stream in behind the first — the reader sees page 1 immediately.
      for (let n = 2; n <= Math.min(first.pages, 20); n++) {
        const r = await pdfPageDataUrl(bufRef.current, n, 1000);
        urls.push(r.url); setBig([...urls]);
      }
    } catch { /* leave it closed rather than show a broken frame */ }
    finally { setBigBusy(false); }
  };

  const boxStyle: React.CSSProperties = {
    display: 'block', width: RO_IMG_W, height: RO_IMG_H, objectFit: 'cover', borderRadius: 5,
    outline: '1px solid #e3e3e3', cursor: bigBusy ? 'wait' : 'zoom-in', marginTop: 6, background: '#fff',
  };
  if (thumbBusy || !src) {
    return (
      <div style={{ ...boxStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f4f4f4' }}>
        {thumbBusy ? <Spinner size={22} /> : <i className="ti ti-file-text" style={{ fontSize: 26, color: '#b9b9b9' }} />}
      </div>
    );
  }
  return (
    <>
      <div style={{ position: 'relative', width: RO_IMG_W, marginTop: 6 }}>
        <img src={src} alt="" onClick={openBig} style={{ ...boxStyle, marginTop: 0 }} />
        {bigBusy && (
          <div style={{ position: 'absolute', inset: 0, borderRadius: 5, background: 'rgba(255,255,255,.72)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner size={24} />
          </div>
        )}
      </div>
      {open && (
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 10090, background: 'rgba(0,0,0,0.82)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', overflow: 'auto', padding: pdf ? '24px 0' : 0 }}>
          {pdf ? (
            <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center', cursor: 'default' }}>
              {big.map((u, i) => <img key={i} src={u} alt="" style={{ maxWidth: '92vw', maxHeight: big.length > 1 ? 'none' : '92vh', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.5)', background: '#fff' }} />)}
              {bigBusy && <Spinner size={30} light />}
            </div>
          ) : (
            <LightboxImage src={src} />
          )}
          <button onClick={() => setOpen(false)} title="Close (Esc)"
            style={{ position: 'fixed', top: 18, right: 22, width: 40, height: 40, borderRadius: 999, border: 'none', background: 'rgba(255,255,255,0.92)', color: '#333', fontSize: 20, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <i className="ti ti-x" />
          </button>
        </div>
      )}
    </>
  );
}

// The full-size image can still be downloading when the lightbox opens (the client link streams it
// fresh), so it spins until the bitmap is actually decoded rather than flashing an empty frame.
function LightboxImage({ src }: { src: string }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <>
      {!loaded && <div style={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size={40} light /></div>}
      <img src={src} alt="" onClick={e => e.stopPropagation()} onLoad={() => setLoaded(true)}
        style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 6, boxShadow: '0 8px 40px rgba(0,0,0,0.5)', cursor: 'default', opacity: loaded ? 1 : 0, transition: 'opacity .15s' }} />
    </>
  );
}
function FileThumb({ f, publicToken }: { f: any; publicToken?: string }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let live = true; let obj = '';
    if (!isImg(f.mime)) return;
    if (publicToken) { setSrc(api.ddPublicFileUrl(publicToken, f.id)); return; }
    api.ddFileObjectUrl(f.id).then((u: string) => { if (live) { obj = u; setSrc(u); } else URL.revokeObjectURL(u); }).catch(() => {});
    return () => { live = false; if (obj) setTimeout(() => URL.revokeObjectURL(obj), 2000); };
  }, [f.id, publicToken]);
  const box: React.CSSProperties = { width: 56, height: 44, borderRadius: 3, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f0f0', overflow: 'hidden' };
  if (isImg(f.mime) && src) return <div style={box}><img src={src} alt={f.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>;
  const ic = fileIcon(f.mime);
  return <div style={box}><i className={`ti ${ic.icon}`} style={{ fontSize: 24, color: ic.color }} /></div>;
}
const UploadF = ({ label, required, path, cols = 24, multi = false }: { label: string; required?: boolean; path: string; cols?: number; multi?: boolean }) => {
  const { reportId, publicToken, refreshKey, onFileChange } = React.useContext(DDContext);
  const ro = React.useContext(DDReadOnly);
  const [files, setFiles] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [msg, setMsg] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    let live = true;
    const load = publicToken ? api.ddPublicFiles(publicToken, path) : (reportId ? api.ddFiles(reportId, path) : Promise.resolve({ files: [] }));
    load.then((r: any) => { if (live) setFiles(r.files || []); }).catch(() => {});
    return () => { live = false; };
  }, [reportId, publicToken, path, refreshKey]);
  const view = (f: any) => { if (publicToken) window.open(api.ddPublicFileUrl(publicToken, f.id), '_blank'); else api.ddFileView(f.id).catch(() => {}); };
  const download = (f: any) => { if (publicToken) { const a = document.createElement('a'); a.href = api.ddPublicFileUrl(publicToken, f.id); a.download = f.filename || 'file'; a.click(); } else api.ddFileDownload(f.id, f.filename).catch(() => {}); };
  const doUpload = async (list: FileList | null) => {
    if (!list || !list.length || !reportId) return;
    setMsg('');
    if (!multi) {
      if (files.length >= 1) { setMsg('Only one attachment is allowed. Remove the current file first.'); return; }
      const f = list[0];                                  // single attachment only
      if (f.size > MAX_UPLOAD) { setMsg(`"${f.name}" is ${fmtSize(f.size)} — the maximum is 10 MB.`); return; }
      setBusy(true);
      try { const r: any = await api.ddUpload(reportId, path, f); setFiles([r]); onFileChange?.(); }
      catch (e: any) { setMsg(e?.message || 'Upload failed'); }
      finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
      return;
    }
    // multi: upload every chosen file (sequentially), appending to the list; skip any > 10 MB.
    const chosen = Array.from(list);
    const oversize = chosen.filter(f => f.size > MAX_UPLOAD);
    const valid = chosen.filter(f => f.size <= MAX_UPLOAD);
    if (oversize.length) setMsg(`${oversize.map(f => `"${f.name}"`).join(', ')} exceeded 10 MB and ${oversize.length > 1 ? 'were' : 'was'} skipped.`);
    if (!valid.length) { if (fileRef.current) fileRef.current.value = ''; return; }
    setBusy(true);
    try {
      const uploaded: any[] = [];
      for (const f of valid) { const r: any = await api.ddUpload(reportId, path, f); uploaded.push(r); }
      setFiles(prev => [...prev, ...uploaded]); onFileChange?.();
    } catch (e: any) { setMsg(e?.message || 'Upload failed'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const del = async (id: string) => { try { await api.ddDeleteFile(id); setFiles(prev => prev.filter(x => x.id !== id)); setMsg(''); onFileChange?.(); } catch { /* ignore */ } };
  // Attachment row — thumbnail (image preview / file-type icon), name link, size,
  // download, and (when editing) remove. Same shape as the original Cognito form.
  const fileRow = (f: any, canDelete: boolean) => (
    <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '8px 0', fontFamily: RC, marginTop: 6 }}>
      <FileThumb f={f} publicToken={publicToken} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <button onClick={() => view(f)} style={{ display: 'block', maxWidth: '100%', textAlign: 'left', border: 'none', background: 'none', color: BLUE, cursor: 'pointer', fontFamily: RC, fontSize: 15, padding: 0, textDecoration: 'underline', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.filename}>{f.filename}</button>
        <div style={{ color: '#9a9a9a', fontSize: 12.5, marginTop: 2 }}>{fmtSize(f.size || 0)}</div>
      </div>
      {/* dd-noprint: a download arrow means nothing on paper. */}
      <button className="dd-noprint" onClick={() => download(f)} title="Download" style={{ border: 'none', background: 'none', color: '#555', cursor: 'pointer', flexShrink: 0, fontSize: 20 }}><i className="ti ti-download" /></button>
      {canDelete && <button className="dd-noprint" onClick={() => del(f.id)} title="Remove" style={{ border: 'none', background: 'none', color: '#E24B4A', cursor: 'pointer', flexShrink: 0, fontSize: 20 }}><i className="ti ti-circle-x" /></button>}
    </div>
  );
  // Read-only (preview / client view): attachment rows, download only. An OPTIONAL slot nobody used
  // disappears entirely (Calem 2026-08-05: Site Photos 3-6 etc.) — an empty "—" row is noise on the
  // client's report; required slots keep the dash so a legacy gap stays visible to staff.
  if (ro) {
    if (!files.length && !required) return null;
    return (
      <Cell cols={cols}><div style={roLabel}>{label}</div>
        {files.length === 0 ? <div style={roVal}><span style={{ color: '#c4c4c4' }}>—</span></div>
          : files.map(f => (isImg(f.mime) || isPdfMime(f.mime))
            ? <FileImageLarge key={f.id} f={f} publicToken={publicToken} />
            : fileRow(f, false))}
      </Cell>
    );
  }
  return (
    <Cell cols={cols}><QLabel required={required}>{label}</QLabel>
      {(multi || files.length === 0) && (
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files); }}
          style={{ border: `2px dashed ${drag ? BLUE : '#cfcfcf'}`, background: drag ? '#f0f6fc' : '#fafafa', padding: '18px', textAlign: 'center', color: '#9a9a9a', fontSize: 13, fontFamily: RC, cursor: 'pointer', marginTop: multi && files.length ? 6 : 0 }}>
          <i className="ti ti-cloud-upload" style={{ fontSize: 22, display: 'block', marginBottom: 6 }} />{busy ? 'Uploading…' : (multi && files.length ? 'Add more files (or drag & drop here)' : 'Browse Files (or drag & drop here)')}
          <div style={{ fontSize: 12, color: '#b0b0b0', marginTop: 6 }}>{multi ? 'Maximum 10 MB each · multiple files allowed' : 'Maximum 10 MB · one file only'}</div>
        </div>
      )}
      <input ref={fileRef} type="file" multiple={multi} style={{ display: 'none' }} onChange={e => doUpload(e.target.files)} />
      {files.map(f => fileRow(f, true))}
      {msg && <div style={{ marginTop: 6, fontSize: 13, color: '#fff', background: '#D0021B', padding: '7px 11px', borderRadius: 4, fontFamily: RC }}>{msg}</div>}
    </Cell>
  );
};
const Block = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="dd-block" style={{ marginTop: 24 }}>
    <div className="dd-block-title" style={{ fontSize: 24, fontWeight: 400, color: BLUE, fontFamily: RC, marginBottom: 18 }}>{title}</div>
    {children}
    <Divider />
  </div>
);

// ── Required-field validation (mirrors the original Cognito required map) ────
const isEmpty = (v: any) => v == null || (typeof v === 'string' && !v.trim());
const addrEmpty = (a: any) => !a || (isEmpty(a.line1) && isEmpty(a.city) && isEmpty(a.postal) && isEmpty(a.country));
function entityCounts(o: any, numKey: string, typeKey: string) {
  const type = o[typeKey] || ''; const showInd = type.includes('Individual'); const showCo = type.includes('Company');
  const both = showInd && showCo; const count = COUNT[o[numKey]] || 0;
  // `indExtra` = individual blocks the operator added by hand (co-owners on one title deed, say).
  // It deliberately does NOT touch "Number of Sellers" — that answer describes the transaction, while
  // this just adds another person's details to fill in. Counted here so the render AND the validation
  // agree; anything derived from indN (required fields, upload gates) follows automatically.
  const extra = showInd ? Math.max(0, Number(o.indExtra) || 0) : 0;
  return { indN: (both ? 1 : (showInd ? count : 0)) + extra, coN: both ? 1 : (showCo ? count : 0) };
}

// Per-field validation keyed by field path (matches each widget's `name`), with
// format rules: required not-empty, Job ID 5–7 chars, valid email, Name first/last.
function computeFieldErrors(key: string, data: any, filePaths?: Set<string> | null): Record<string, string> {
  const e: Record<string, string> = {};
  const txt = (v: any, k: string, label: string) => { if (isEmpty(v)) e[k] = `${label} is required.`; };
  // A required file counts as missing only once we actually know what has been uploaded (filePaths
  // loaded) — before that we don't flag it, so a freshly-opened page never shows false errors.
  const fileReq = (path: string, label: string) => { if (filePaths && !filePaths.has(path)) e[path] = `${label} is required.`; };
  const nm = (v: any, k: string) => { if (isEmpty(v?.first)) e[`${k}.first`] = 'First is required.'; if (isEmpty(v?.last)) e[`${k}.last`] = 'Last is required.'; };
  const addr = (v: any, k: string, label = 'Address') => { if (addrEmpty(v)) e[k] = `${label} is required.`; };
  const jobId = (v: any, k: string) => { if (isEmpty(v)) e[k] = 'Job ID is required.'; else { const s = String(v).trim(); if (s.length < 5 || s.length > 7) e[k] = 'Job ID must be between 5 and 7 characters.'; } };
  const email = (v: any, k: string) => { if (isEmpty(v)) e[k] = 'Email is required.'; else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim())) e[k] = 'Email must be formatted as name@address.xyz.'; };
  if (key === 'parameters') {
    const p = data.parameters || {};
    // NOTE: compiledBy / thaiChecked / englishChecked are deliberately NOT validated. The QA
    // sign-off is the chain (SignoffChain → dd_signoffs) on the LAST page, and it gates SENDING
    // the report to the client — it can never gate page navigation, because the whole report has
    // to be filled in before there is anything to review.
    // The date the report itself bears — it is printed at the top of the client copy.
    txt(p.issueDate, 'issueDate', 'Date');
    (['buyingPropertyFrom', 'buildingPermit', 'houseBooks', 'landUpgraded', 'purchaseAgreement', 'privateRoad'] as const)
      .forEach(k => { if (isEmpty(p[k])) e[k] = 'This field is required.'; });
  } else if (key === 'buyer') {
    const b = data.buyer || {};
    // These two drive how many buyer blocks render. Left blank, entityCounts returns 0 and the
    // entire page validates clean — letting a report reach the client stage with no buyer and no
    // buyer email (which is exactly who the finished report gets sent to).
    txt(b.numberOfBuyers, 'numberOfBuyers', 'Number of Buyers'); txt(b.buyerType, 'buyerType', 'Buyer Type');
    const { indN, coN } = entityCounts(b, 'numberOfBuyers', 'buyerType');
    for (let i = 0; i < indN; i++) { const s = (b.individuals || [])[i] || {}; const P = `buyer.ind.${i}`; nm(s.name, `${P}.name`); jobId(s.jobId, `${P}.jobId`); if (!String(s.showEmail || '').startsWith('Do Not')) email(s.email, `${P}.email`); addr(s.address, `${P}.address`); }
    for (let i = 0; i < coN; i++) { const s = (b.companies || [])[i] || {}; const P = `buyer.co.${i}`; txt(s.companyName, `${P}.companyName`, 'Company Name'); jobId(s.jobId, `${P}.jobId`); email(s.email, `${P}.email`); addr(s.address, `${P}.address`, 'Company Address'); }
  } else if (key === 'seller') {
    const sl = data.seller || {};
    txt(sl.numberOfSellers, 'numberOfSellers', 'Number of Sellers'); txt(sl.sellerType, 'sellerType', 'Seller Type');
    const { indN, coN } = entityCounts(sl, 'numberOfSellers', 'sellerType');
    // Capacity lives per individual now; the section-level answer is only asked (and required) when
    // the sale has no individual seller at all. Reports written before the per-person field existed
    // carry only the section value — those stay valid.
    if (indN === 0 && isEmpty(sl.sellerIs)) e['sellerIs'] = 'Seller Is? is required.';
    for (let i = 0; i < indN; i++) {
      const s = (sl.individuals || [])[i] || {}; const P = `seller.ind.${i}`;
      nm(s.name, `${P}.name`); txt(s.idCard, `${P}.idCard`, 'ID Card'); addr(s.address, `${P}.address`); fileReq(`${P}.idcard`, 'ID Card (upload)');
      txt(s.landOrBuildings, `${P}.landOrBuildings`, 'Land or Building(s)');
      if (isEmpty(s.sellerIs) && isEmpty(sl.sellerIs)) e[`${P}.sellerIs`] = 'Seller Is? is required.';
    }
    for (let i = 0; i < coN; i++) { const s = (sl.companies || [])[i] || {}; const P = `seller.co.${i}`; txt(s.companyName, `${P}.companyName`, 'Company Name'); txt(s.taxId, `${P}.taxId`, 'Tax ID'); txt(s.registeredCapital, `${P}.registeredCapital`, 'Registered Capital'); addr(s.address, `${P}.address`); txt(s.balanceAudit, `${P}.balanceAudit`, 'Balance Sheet and Audit'); txt(s.yearEstablished, `${P}.yearEstablished`, 'Year Established'); fileReq(`${P}.docs`, 'Company Documents');
      txt(s.landOrBuildings, `${P}.landOrBuildings`, 'Land or Building(s)');
      txt(s.numberOfDirectors, `${P}.numberOfDirectors`, 'Number of Directors'); txt(s.signStamp, `${P}.signStamp`, 'Sign & Stamp');
      // Every director the operator said exists must actually be named — "Number of Directors: three"
      // with two blank rows is a company record that reads as complete and is not.
      for (let d = 0; d < (COUNT[s.numberOfDirectors] || 0); d++) nm((s.directors || [])[d], `${P}.directors.${d}`);
    }
  } else if (key === 'property') {
    const p = data.property || {};
    txt(p.name, 'property.name', 'Name'); addr(p.address, 'property.address');
    txt(p.landAndorBuildings, 'property.landAndorBuildings', 'Land and/or Building(s)');
    txt(p.zoning, 'property.zoning', 'Zoning'); txt(p.suitableFor, 'property.suitableFor', 'Suitable For');
    txt(p.maximumUsage, 'property.maximumUsage', 'Maximum Usage');
    txt(p.landElevation, 'property.landElevation', 'Land Elevation APPROX'); txt(p.slope, 'property.slope', 'Slope (in Degrees) APPROX');
    // Old-form parity: a zoning/suitability mismatch is a hard error, not a hint ("Incorrect
    // suitability for zoning"), and it blocks Next exactly as Cognito blocked submission.
    {
      const sfWant = suitableForZone(p.zoning || '');
      if (sfWant && String(p.suitableFor || '').trim() && p.suitableFor !== sfWant) {
        e['property.suitableFor'] = 'Incorrect suitability for zoning';
      }
    }
    landAreaUnits().forEach(u => txt(p[u.key], `property.${u.key}`, u.label));
    if (isEmpty(p.sameSize)) e['sameSize'] = "Same Size as Buyer's Plot? is required.";
    if (p.sameSize === 'No') txt(p.m2Buyer, 'property.m2Buyer', "m² (Buyer's Plot)");
    fileReq('property.map', 'Property Map');
    // 2026-08-13 correction: the old form DOES have a Land Elevation (Estimate) upload — it is just
    // hidden unless the elevation names 140m (Zone 3.1 / 3.2), which is why the 2026-07-26 parity
    // check missed it. Required exactly when visible, like the original.
    if (needsElevationEstimate(p.landElevation)) fileReq('property.elevationEstimate', 'Land Elevation (Estimate)');
  } else if (SECTIONS[key]) {
    const spec = SECTIONS[key]; const sd = data[key] || {};
    const walk = (rows: FSpec[][], dd: any, prefix: string) => rows.forEach(row => row.forEach(f => {
      if (f.show && !f.show(dd)) return;
      const k = `${prefix}.${f.k}`;
      if (f.t === 'file') { if (f.req) fileReq(k, f.label); return; }   // files: required unless not uploaded
      if (!f.req) return;
      const v = dd[f.k];
      if (f.t === 'name') nm(v, k);
      else if (f.t === 'addr') addr(v, k, f.label);
      else if (isEmpty(v)) e[k] = `${f.label} is required.`;
    }));
    if (spec.top) walk(spec.top, sd, key);
    if (spec.bottom) walk(spec.bottom, sd, key);
    if (spec.instance) {
      const gate = spec.countGate ? spec.countGate(sd) : true;
      const n = gate ? (WORDNUM[String(sd.count || '').toLowerCase()] || 0) : 0;
      const items = sd.items || [];
      for (let i = 0; i < n; i++) walk(spec.instance(i), items[i] || {}, `${key}.${i}`);
    }
  }
  return e;
}

// ── Buyer section ────────────────────────────────────────────────────────────
const SHOW_EMAIL_OPTS = ['Show Email (Default)', 'Do Not Show Email'];
function BuyerSection({ buyer, patch }: { buyer: any; patch: (p: any) => void }) {
  const roView = React.useContext(DDReadOnly);
  const count = COUNT[buyer.numberOfBuyers] || 0;
  const type = buyer.buyerType || '';
  const showInd = type.includes('Individual'); const showCo = type.includes('Company');
  const both = showInd && showCo;                       // "Individual & Company" → exactly 1 + 1
  const indN = both ? 1 : (showInd ? count : 0);
  const coN = both ? 1 : (showCo ? count : 0);
  const setItem = (arrKey: string, i: number, p: any) => { const arr = (buyer[arrKey] || []).slice(); while (arr.length <= i) arr.push({}); arr[i] = { ...arr[i], ...p }; patch({ [arrKey]: arr }); };
  const ind = (i: number) => (buyer.individuals || [])[i] || {};
  const co = (i: number) => (buyer.companies || [])[i] || {};
  return (
    <>
      <Row>
        <Sel cols={12} required name="numberOfBuyers" label="Number of Buyers" options={NUM_1_3} value={buyer.numberOfBuyers} onChange={v => patch({ numberOfBuyers: v })} />
        <Sel cols={12} required name="buyerType" label="Buyer Type" options={ENTITY_TYPE} value={buyer.buyerType} onChange={v => patch({ buyerType: v })} />
      </Row>
      {Array.from({ length: indN }).map((_, i) => {
        // Whether the buyer's email prints on the report (internal switch, Calem 2026-08-05).
        // hidden = the choice itself; in the EDITOR it hides the email box on the spot (the radio
        // stays in its slot to turn it back); in the REPORT it also re-justifies the row — the name
        // takes the freed width and Job ID shifts right.
        const hidden = String(ind(i).showEmail || '').startsWith('Do Not');
        const hideEmailRo = roView && hidden;
        return (
        <Block key={'ind' + i} title={`Buyer Details (Individual${indN > 1 ? ' ' + (i + 1) : ''})`}>
          <Row>
            <NameF cols={hideEmailRo ? 12 : (hidden ? 14 : 12)} inlineLast={hidden && !roView} required label="Name" name={`buyer.ind.${i}.name`} value={ind(i).name} onChange={v => setItem('individuals', i, { name: v })} />
            <Text cols={hideEmailRo ? 12 : (hidden ? 4 : 6)} required label="Job ID" name={`buyer.ind.${i}.jobId`} value={ind(i).jobId} onChange={v => setItem('individuals', i, { jobId: v })} />
            {!hideEmailRo && (
              <Cell cols={6}>
                {!hidden && <EmailF cols={24} required name={`buyer.ind.${i}.email`} value={ind(i).email} onChange={v => setItem('individuals', i, { email: v })} />}
                {/* Internal control, tucked under the email box — never rendered in the preview or
                    the client's copy. */}
                {!roView && (
                  <div style={{ marginTop: hidden ? 0 : 8 }}>
                    <Q cols={24} name={`buyer.ind.${i}.showEmail`} options={SHOW_EMAIL_OPTS}
                      label="Show email in report"
                      value={ind(i).showEmail || SHOW_EMAIL_OPTS[0]} onChange={v => setItem('individuals', i, { showEmail: v })} />
                  </div>
                )}
              </Cell>
            )}
          </Row>
          <Row><AddressF cols={24} required label="Address" name={`buyer.ind.${i}.address`} value={ind(i).address} onChange={v => setItem('individuals', i, { address: v })} /></Row>
        </Block>
        );
      })}
      {Array.from({ length: coN }).map((_, i) => (
        <Block key={'co' + i} title={`Buyer Details (Company${coN > 1 ? ' ' + (i + 1) : ''})`}>
          <Row>
            <Text cols={12} required label="Company Name" name={`buyer.co.${i}.companyName`} value={co(i).companyName} onChange={v => setItem('companies', i, { companyName: v })} />
            <Text cols={6} required label="Job ID" name={`buyer.co.${i}.jobId`} value={co(i).jobId} onChange={v => setItem('companies', i, { jobId: v })} />
            <EmailF cols={6} required name={`buyer.co.${i}.email`} value={co(i).email} onChange={v => setItem('companies', i, { email: v })} />
          </Row>
          <Row><AddressF cols={24} required label="Company Address" name={`buyer.co.${i}.address`} value={co(i).address} onChange={v => setItem('companies', i, { address: v })} /></Row>
        </Block>
      ))}
      {indN + coN === 0 && <div style={hintStyle}>Select the number of buyers and buyer type above.</div>}
    </>
  );
}

// ── Seller Summary — auto-generated paragraph from the entered seller data ───
const ddName = (n: any) => [n?.title, n?.first, n?.last].filter(Boolean).join(' ');
const ddAddr = (a: any) => [a?.line1, a?.line2, a?.city, a?.postal, a?.country].filter(Boolean).join(', ');
const joinAnd = (xs: string[]) => xs.length <= 1 ? (xs[0] || '') : xs.slice(0, -1).join(', ') + ' and ' + xs[xs.length - 1];
const ddPara: React.CSSProperties = { margin: '0 0 12px' };
// Each title deed pulled out as its own indented, well-spaced block so the deed number and size
// stand clear of the surrounding prose.
const ddDeedItem: React.CSSProperties = { margin: '20px 0 20px 45px', lineHeight: 1.75 };
// Pulled-out call-to-action lines inside a Note (blue, indented, roomy) — the firm's DD-report style.
const ddSurveyNote: React.CSSProperties = { margin: '22px 0 22px 45px', lineHeight: 1.75, color: DDBLUE, fontFamily: RC };
// Same font/size/colour as the surrounding report body (Calem 2026-07-27: notes must not look
// smaller/greyer than the paragraphs above them) — only the bold "Note:" prefix distinguishes it.
const ddNote: React.CSSProperties = { margin: '0 0 12px', lineHeight: 1.75 };
const ddSumHead: React.CSSProperties = { fontSize: 22, fontWeight: 400, color: BLUE, fontFamily: RC, margin: '0 0 12px' };
function SellerSummary({ seller }: { seller: any }) {
  const { indN, coN } = entityCounts(seller, 'numberOfSellers', 'sellerType');
  if (indN + coN === 0) return null;
  const inds = seller.individuals || []; const cos = seller.companies || [];
  const B = (x: any) => <strong>{x || ''}</strong>;
  // Each owner gets its own headed block: "Seller Summary (Building)" / "(Land)"
  // for a company (taking its Land or Building(s) value), "Seller Summary" for an
  // individual — exactly like the original Cognito calculated-text sections.
  // Blocks are ordered Land first, Building(s) under (Calem 2026-07-28) — regardless of whether
  // the land seller is the individual or the company.
  const groups: { rank: number; node: React.ReactNode }[] = [];
  const lobRank = (v: any): number => /^land/i.test(String(v || '')) ? 0 : /building/i.test(String(v || '')) ? 1 : 2;
  for (let i = 0; i < coN; i++) {
    const c = cos[i] || {};
    const lob = c.landOrBuildings || 'property';
    const dirs = (c.directors || []).slice(0, COUNT[c.numberOfDirectors] || 0).map(ddName).filter(Boolean);
    groups.push({ rank: lobRank(c.landOrBuildings), node:
      <div key={'co' + i} style={{ marginBottom: 20 }}>
        <div style={ddSumHead} className="dd-sum-head">Seller Summary{c.landOrBuildings ? ` (${c.landOrBuildings})` : ''}</div>
        <p style={ddPara}>The seller, and current owner of the {B(lob)} is {B(c.companyName)}, established in {B(c.yearEstablished)}, with their head office registered at; {ddAddr(c.address)}, holding Tax ID Number: {B(c.taxId)} and a registered capital of {B(thb(c.registeredCapital) + ' THB')}. {c.companyName || ''} currently has {B(c.numberOfDirectors)} director(s) being; ({B(joinAnd(dirs))}) and {B(c.signStamp)} director(s) are required to sign and stamp for the authorization of company changes.</p>
        <p style={ddPara}>According to the available records at the Department of Business Development (DBD), the last End of Year Balance Sheet and Audit submitted by {B(c.companyName)} was {B(c.balanceAudit)}.</p>
        {(() => { const fyi = lastFinancialYear(); return (
          <p style={ddNote}><strong>Note 1:</strong> the most recent end of year submission required by the Revenue Department is {fyi.fy} ({fyi.fyBE}), which is due on or before May 31, {fyi.dueYear} ({fyi.dueYearBE}).</p>
        ); })()}
        {(() => {
          // Only the bracket that actually applies to THIS company's registered capital is shown —
          // the other bracket is noise on the report and invites the reader to apply the wrong rate.
          const cap = Number(String(c.registeredCapital ?? '').replace(/,/g, ''));
          const small = !Number.isFinite(cap) || cap <= 5000000;   // unknown → show the common (≤5M) case
          const bullet: React.CSSProperties = { margin: '4px 0 4px 26px', lineHeight: 1.9, color: DDBLUE };
          return (
            <div style={ddNote}>
              <strong>Note 2:</strong> corporate tax rates are dictated by the paid-up registered capital level of the company, as follows:
              <div className="dd-sum-point" style={{ margin: '8px 0 0 14px', lineHeight: 1.9, color: DDBLUE, fontWeight: 700 }}>
                {small ? '5,000,000 THB in paid-up capital or less:' : 'More than 5,000,000 THB in paid-up capital:'}
              </div>
              {small ? (<>
                <div className="dd-sum-point" style={bullet}>• &lt; 300,000 THB = 0%</div>
                <div className="dd-sum-point" style={bullet}>• &gt; 300,000 THB &lt; 3,000,000 THB = 15%</div>
                <div className="dd-sum-point" style={bullet}>• &gt; 3,000,000 THB = 20%</div>
              </>) : (
                <div className="dd-sum-point" style={bullet}>• &gt; 0 THB = 20%</div>
              )}
            </div>
          );
        })()}
      </div>,
    });
  }
  // Only people who actually have a name reach the summary. Adding an individual block creates an
  // empty slot that would otherwise render as a bare ", residing at; , holding Identification
  // Number(s): ." clause in the client-facing report the moment the block appears.
  const namedInd = Array.from({ length: indN }, (_, i) => inds[i] || {})
    .filter((s: any) => !!ddName(s.name));
  const shownInd = namedInd.length;
  // Each person's capacity in brackets after their name — "Mr Somchai (Lessee)". Falls back to the
  // single answer at the top of the section when that individual has not been given their own.
  const nameWithRole = (s: any) => {
    const role = String(s?.sellerIs || seller.sellerIs || '').trim();
    return role ? `${ddName(s.name)} (${role})` : ddName(s.name);
  };
  if (shownInd > 0) {
    const indPara = shownInd === 1
      ? (() => { const s = namedInd[0]; return <p style={ddPara}>The seller, and current owner of the property is {B(nameWithRole(s))}, with the Thai owner residing at; {B(ddAddr(s.address))}, holding Identification Number(s): {B(s.idCard)}.</p>; })()
      : <p style={ddPara}>The sellers, and current owners of the property are {namedInd.map((s: any, i: number) => {
          return <React.Fragment key={i}>{i > 0 ? (i === shownInd - 1 ? ' and ' : ', ') : ''}{B(nameWithRole(s))}, residing at; {B(ddAddr(s.address))}, holding Identification Number(s): {B(s.idCard)}</React.Fragment>;
        })}.</p>;
    // Individual sellers carry a "Land or Building(s)" value too — the heading shows it exactly
    // like the company blocks: "Seller Summary (Land)" (user 2026-07-28). Distinct values joined
    // when multiple individuals sell different things.
    const indLob = Array.from(new Set(inds.slice(0, indN).map((s: any) => String(s?.landOrBuildings || '').trim()).filter(Boolean))) as string[];
    groups.push({ rank: lobRank(indLob[0]), node:
      <div key="ind" style={{ marginBottom: 20 }}>
        <div style={ddSumHead} className="dd-sum-head">Seller Summary{indLob.length ? ` (${indLob.join(' & ')})` : ''}</div>
        {indPara}
        <p style={ddNote}><strong>Note:</strong> If the seller details do not match those of the seller specified in this report then, the <strong>Seller Agreement</strong> front page (parties involved) and rear page (parties signatures) will be attached above.</p>
      </div>,
    });
  }
  const ordered = groups.map((g, i) => ({ ...g, i })).sort((a, b) => a.rank - b.rank || a.i - b.i).map(g => g.node);
  return (
    <div className="dd-sum" style={{ marginTop: 18, paddingTop: 8, fontSize: 15, color: INK, lineHeight: 1.7, fontFamily: RC }}>{ordered}</div>
  );
}

// ── Seller section ───────────────────────────────────────────────────────────
function SellerSection({ seller, patch }: { seller: any; patch: (p: any) => void }) {
  const type = seller.sellerType || '';
  const showInd = type.includes('Individual');
  // The client report and the preview render this same section read-only (patch is a no-op there),
  // so the add / remove buttons must not appear: they would do nothing, and a "Remove this
  // individual" button on a report the client is reading is alarming on its own.
  const ro = React.useContext(DDReadOnly);
  // Single source of truth with the validator — including the hand-added individual blocks.
  const { indN, coN } = entityCounts(seller, 'numberOfSellers', 'sellerType');
  const extra = Math.max(0, Number(seller.indExtra) || 0);
  const baseIndN = indN - extra;                        // blocks implied by Number of Sellers
  const addIndividual = () => patch({ indExtra: extra + 1 });
  // Removing drops that person's captured details too, so a deleted block cannot leave orphan data
  // behind that still fails validation invisibly.
  const removeIndividual = (i: number) => {
    const arr = (seller.individuals || []).slice();
    if (i < arr.length) arr.splice(i, 1);
    patch({ indExtra: Math.max(0, extra - 1), individuals: arr });
  };
  const setItem = (arrKey: string, i: number, p: any) => { const arr = (seller[arrKey] || []).slice(); while (arr.length <= i) arr.push({}); arr[i] = { ...arr[i], ...p }; patch({ [arrKey]: arr }); };
  const ind = (i: number) => (seller.individuals || [])[i] || {};
  const co = (i: number) => (seller.companies || [])[i] || {};
  return (
    <>
      <Row>
        <Sel cols={12} required name="numberOfSellers" label="Number of Sellers" options={NUM_1_2} value={seller.numberOfSellers} onChange={v => patch({ numberOfSellers: v })} />
        <Sel cols={12} required name="sellerType" label="Seller Type" options={ENTITY_TYPE} value={seller.sellerType} onChange={v => patch({ sellerType: v })} />
      </Row>
      {/* Asked once at section level ONLY when there is no individual seller (company-only sale).
          As soon as individuals exist each of them carries their own capacity above their block, and
          showing this too was simply the same question twice. */}
      {!showInd && (
        <Row><Q cols={24} label="Seller Is?" required name="sellerIs" options={SELLER_IS} value={seller.sellerIs || ''} onChange={v => patch({ sellerIs: v })} /></Row>
      )}
      {Array.from({ length: indN }).map((_, i) => (
        <React.Fragment key={'ind' + i}>
        {/* Capacity is asked BEFORE the person's details (owner's request): each individual can hold the
            property differently — one the owner, another the lessee or superficiary. Defaults to the
            single answer at the top of the section until it is changed here. */}
        <Row><Q cols={24} label="Seller Is?" required name={`seller.ind.${i}.sellerIs`} options={SELLER_IS}
          value={ind(i).sellerIs || seller.sellerIs || ''} onChange={v => setItem('individuals', i, { sellerIs: v })} /></Row>
        <Block title={`Seller Details (Individual${indN > 1 ? ' ' + (i + 1) : ''})`}>
          <Row>
            <NameF cols={18} required label="Name" name={`seller.ind.${i}.name`} value={ind(i).name} onChange={v => setItem('individuals', i, { name: v })} />
            <Text cols={6} required label="ID Card" name={`seller.ind.${i}.idCard`} value={ind(i).idCard} onChange={v => setItem('individuals', i, { idCard: v })} />
          </Row>
          <Row>
            <AddressF cols={18} required label="Address" name={`seller.ind.${i}.address`} value={ind(i).address} onChange={v => setItem('individuals', i, { address: v })} />
            <Sel cols={6} required name={`seller.ind.${i}.landOrBuildings`} label="Land or Building(s)" options={LAND_OR_BUILDINGS} value={ind(i).landOrBuildings} onChange={v => setItem('individuals', i, { landOrBuildings: v })} />
          </Row>
          <Row><UploadF cols={24} required multi label="ID Card" path={`seller.ind.${i}.idcard`} /></Row>
          {i >= baseIndN && !ro && (
            <div style={{ textAlign: 'right', marginTop: 6 }}>
              <button type="button" onClick={() => removeIndividual(i)}
                style={{ border: '1px solid #d6d6d6', background: '#fff', color: '#c0392b', fontFamily: RC, fontSize: 14, padding: '5px 12px', cursor: 'pointer' }}>
                Remove this individual
              </button>
            </div>
          )}
        </Block>
        </React.Fragment>
      ))}
      {/* Individual sellers only: another person's details WITHOUT changing "Number of Sellers". */}
      {showInd && !ro && (
        <div style={{ margin: '4px 0 18px' }}>
          <button type="button" onClick={addIndividual}
            style={{ border: '1px solid #1a73e8', background: '#fff', color: '#1a73e8', fontFamily: RC, fontSize: 15, fontWeight: 600, padding: '8px 16px', cursor: 'pointer' }}>
            + Add another Individual
          </button>
        </div>
      )}
      {Array.from({ length: coN }).map((_, i) => (
        <Block key={'co' + i} title={`Seller Details (Company${coN > 1 ? ' ' + (i + 1) : ''})`}>
          <Row>
            <Text cols={12} required label="Company Name" name={`seller.co.${i}.companyName`} value={co(i).companyName} onChange={v => setItem('companies', i, { companyName: v })} />
            <Text cols={6} required label="Tax ID" name={`seller.co.${i}.taxId`} value={co(i).taxId} onChange={v => setItem('companies', i, { taxId: v })} />
            <Num cols={6} required label="Registered Capital" name={`seller.co.${i}.registeredCapital`} value={co(i).registeredCapital} onChange={v => setItem('companies', i, { registeredCapital: v })} />
          </Row>
          <Row>
            <AddressF cols={18} required label="Address" name={`seller.co.${i}.address`} value={co(i).address} onChange={v => setItem('companies', i, { address: v })} />
            <Sel cols={6} required name={`seller.co.${i}.landOrBuildings`} label="Land or Building(s)" options={LAND_OR_BUILDINGS} value={co(i).landOrBuildings} onChange={v => setItem('companies', i, { landOrBuildings: v })} />
          </Row>
          <Row>
            <Sel cols={12} required name={`seller.co.${i}.numberOfDirectors`} label="Number of Directors" options={NUM_DIRECTORS} value={co(i).numberOfDirectors} onChange={v => setItem('companies', i, { numberOfDirectors: v })} />
            <Sel cols={12} required name={`seller.co.${i}.signStamp`} label="Sign &amp; Stamp" options={NUM_DIRECTORS} value={co(i).signStamp} onChange={v => setItem('companies', i, { signStamp: v })} />
          </Row>
          <Row>
            {Array.from({ length: COUNT[co(i).numberOfDirectors] || 0 }).map((__, d) => (
              <NameF cols={12} key={d} required name={`seller.co.${i}.directors.${d}`} label={`Director ${d + 1}`} value={(co(i).directors || [])[d]} onChange={v => { const ds = (co(i).directors || []).slice(); while (ds.length <= d) ds.push({}); ds[d] = v; setItem('companies', i, { directors: ds }); }} />
            ))}
          </Row>
          <Row>
            <Sel cols={12} required label="Balance Sheet and Audit" name={`seller.co.${i}.balanceAudit`} options={BALANCE_AUDIT} value={co(i).balanceAudit} onChange={v => setItem('companies', i, { balanceAudit: v })} />
            <Sel cols={12} required label="Year Established" name={`seller.co.${i}.yearEstablished`} options={YEAR_ESTABLISHED} value={co(i).yearEstablished} onChange={v => setItem('companies', i, { yearEstablished: v })} />
          </Row>
          <Row><UploadF cols={24} required multi label="Company Documents" path={`seller.co.${i}.docs`} /></Row>
        </Block>
      ))}
      <SellerSummary seller={seller} />
      {indN + coN === 0 && <div style={hintStyle}>Select the number of sellers and seller type above.</div>}
    </>
  );
}

// ── Property section ─────────────────────────────────────────────────────────
// Renders **bold** segments inside a calculated-text string.
function richText(s: string): React.ReactNode[] {
  return s.split('**').map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : <React.Fragment key={i}>{part}</React.Fragment>);
}
const fmt2 = (x: any) => { const n = parseFloat(x); return isNaN(n) ? (x || '') : fmtNumber(n, 2); };
// Property Summary — the calculated text + Zoning Map + Adopted Zoning Regulations
// essay shown at the bottom of the Property page in the original Cognito form.
// The zoning map with an optional property-location pin. In the editor (onPin given, not read-only)
// clicking the map drops/moves the pin; the position is stored as a fraction of the image (0..1) so
// it survives any resize. In the client report it is static. The pin is OPTIONAL — never required.
function ZoningMap({ src, alt, pin, onPin }: { src: string; alt: string; pin?: { x: number; y: number } | null; onPin?: (p: { x: number; y: number } | null) => void }) {
  const ro = React.useContext(DDReadOnly);
  const editable = !ro && !!onPin;
  const boxRef = React.useRef<HTMLDivElement>(null);
  const place = (e: React.MouseEvent) => {
    if (!editable || !boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / r.height));
    onPin!({ x: Math.round(x * 1000) / 1000, y: Math.round(y * 1000) / 1000 });
  };
  const has = pin && Number.isFinite(pin.x) && Number.isFinite(pin.y);
  return (
    <div style={{ margin: '8px 0 14px', textAlign: 'center' }}>
      {editable && <div style={{ fontSize: 12.5, color: '#6b7c8d', marginBottom: 6, fontFamily: RC }}>
        <i className="ti ti-map-pin" style={{ marginRight: 4 }} />Click the map to mark the property location{has ? ' — click again to move it.' : ' (optional).'}
      </div>}
      {/* outline (not border) for the frame: it paints outside the box, so getBoundingClientRect and
          the pin's left/top % both resolve against the exact same image box — no 1px edge drift. */}
      <div ref={boxRef} onClick={place} style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0, cursor: editable ? 'crosshair' : 'default', outline: '1px solid #e3e3e3' }}>
        <img src={src} alt={alt} style={{ maxWidth: '100%', display: 'block' }} />
        {has && (
          <span style={{ position: 'absolute', left: `${pin!.x * 100}%`, top: `${pin!.y * 100}%`, transform: 'translate(-50%, -100%)', pointerEvents: 'none' }}>
            <svg width="28" height="36" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 0C5.4 0 0 5.4 0 12c0 8 12 20 12 20s12-12 12-20C24 5.4 18.6 0 12 0z" fill="#D0021B" stroke="#fff" strokeWidth="1.6" />
              <circle cx="12" cy="12" r="4.4" fill="#fff" />
            </svg>
          </span>
        )}
      </div>
      {editable && has && (
        <div style={{ marginTop: 6 }}>
          <button type="button" onClick={ev => { ev.stopPropagation(); onPin!(null); }}
            style={{ fontSize: 12, fontFamily: RC, border: '1px solid #d6d6d6', background: '#fff', color: '#B42318', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
            <i className="ti ti-x" style={{ marginRight: 3 }} />Clear pin
          </button>
        </div>
      )}
    </div>
  );
}
function PropertySummary({ p, onPin }: { p: any; onPin?: (pin: { x: number; y: number } | null) => void }) {
  const roView = React.useContext(DDReadOnly);
  // The zoning map + essay are the tenant's (Admin setting, services/ddPoaContent.ts); none set → none shown.
  const { zoning } = useDdContent();
  const essay = zoning.paragraphs;
  const zoneCov = zoneCoverage(essay);
  const { adopted: A, zones: ZS, interpretation: Z } = essayIndexes(essay);
  const mapAlt = mapAltText(essay);
  const b = (x: any) => <strong>{x || ''}</strong>;
  return (
    <div style={sumWrap} className="dd-sum-plain">
      {/* The opening Property Summary is a blue card with a white heading like every other summary
          (Calem 2026-07-28); the zoning essay below keeps its own two cards. */}
      {(() => {
        const min = plotTooSmallMin(p.landElevation, p.slope, p.m2);
        if (!min) return null;
        // Old form _p10/_p11, verbatim (including "to small" / "evelation" — owner wants parity).
        return (
          <div style={{ textAlign: 'center', color: '#B42318', border: '1.5px solid #F4B4AE', background: '#FEF3F2', borderRadius: 8, padding: '12px 16px', margin: '0 0 14px', fontSize: 13, lineHeight: 1.6 }}>
            <div style={{ fontWeight: 800, letterSpacing: '.02em' }}>IMPORTANT NOTE</div>
            <div>There's a possibility that this plot of land may be to small @ <strong>{Number(p.m2 || 0).toFixed(2)}m2</strong>. The minimum plot size may be <strong>{min}m2</strong>, per the building regulations for the land evelation: <strong>{p.landElevation}</strong>. As we can only check evelation using a phone application, it would be best to have this confirmed by a surveyor!</div>
          </div>
        );
      })()}
      <div className="dd-sum">
      <div style={ddSumHead} className="dd-sum-head">Property Summary</div>
      <p style={ddPara}>The {b(p.landAndorBuildings)} ({p.name || ''}), is located at {ddAddr({ ...(p.address || {}), country: (p.address || {}).country || 'Thailand' })}. The land is, or was at the time that the building permit was issued, zoned as {b(p.zoning)} thus, making it suitable for {p.suitableFor || ''}. The land has a maximum usage or "building footprint" of {b(p.maximumUsage)}, meaning, that buildings may occupy no more than {b(p.maximumUsage)} of the available land plot being; {landAreaUnits().map(u => `${fmt2(p[u.key])} ${u.label}`).join(', ')}{landAreaUnits().length ? ' or ' : ''}{b(fmt2(p.m2) + 'm2')}.</p>
      {/* Elevation × slope paragraph — the old Cognito form's six verbatim variants
          (elevationRules.ts). Zone 1 (sea level) has no variant, so no paragraph — old-form parity
          (owner 2026-08-13). Variant #3 interpolates the Maximum Usage into its condition b). */}
      {(() => {
        const v = elevationVariant(p.landElevation, p.slope);
        if (!v) return null;
        return (<>
          <p style={ddPara}>As the land is {b(p.landElevation)} {v.connector} {b(p.slope)}, {v.intro}</p>
          {v.conditions.map((c, i) => (
            <p key={i} className="dd-sum-point" style={{ ...ddNote, margin: '0 0 4px 32px' }}>
              <strong>{String.fromCharCode(97 + i)})</strong> {c.replace('{MaximumUsage}', String(p.maximumUsage || ''))}
            </p>
          ))}
          {v.conditions.length > 0 && <div style={{ height: 8 }} />}
        </>);
      })()}
      <p style={ddNote}><strong>Note 1:</strong> if there is land involved in the transfer (sale and purchase) then, the title cannot be "foreign freehold", therefore, the legal ownership structure can only be via a <strong>"juristic Thai person"</strong>, being either a) or b) to c) as stated below;</p>
      {/* dd-sum-point: the client report renders every indented summary line white + bold — these
          three must match the deed/permit/survey items (owner 2026-08-09). */}
      <p className="dd-sum-point" style={{ ...ddNote, margin: '0 0 4px 32px' }}>a) a Thai individual with a Thai identification card or</p>
      <p className="dd-sum-point" style={{ ...ddNote, margin: '0 0 4px 32px' }}>b) a registered Thai company/partnership with legitimate Thai shareholders/partners to</p>
      <p className="dd-sum-point" style={{ ...ddNote, margin: '0 0 12px 32px' }}>c) a registered lease (for up to 30 years) to the foreigner, from either a) or b) above</p>
      <p style={ddNote}>either "juristic Thai person" above may purchase and own land only or land with buildings upon it.</p>
      <p style={ddNote}><strong>Note 2:</strong> it would be considered standard practice for the building(s) that reside on the land to be purchased and owned by the same "juristic Thai person" thus making it easier to sell the property when/if the time comes.</p>
      </div>
      {/* A map with no paragraph naming it is shown above the essay. */}
      {zoning.mapImage && mapEntryIndex(essay) < 0 && <ZoningMap src={zoning.mapImage} alt={mapAlt} pin={p.mapPin} onPin={onPin} />}
      {(() => { zoneHlSeen = 0; return null; })()}
      {(() => {
        // Only the SELECTED zone's paragraphs stay black/bold; the other zones are muted to grey so
        // the reader's eye lands on the rules that actually apply. The intro and everything from the
        // "Zoning Interpretation" heading down are never muted.
        const renderEntry = (e: any, i: number, inCard: boolean) => {
          const zk = zoneKey(p.zoning || '');
          const inZoneBlock = zk && ZS >= 0 && i >= ZS && (Z < 0 || i < Z);
          const cov = zoneCov[i];
          const mine = !!cov && cov.covers.includes(zk) && !cov.excludes.includes(zk);
          // Paragraphs flagged `always` are never greyed — the flag travels with the essay text in the
          // tenant's setting (first tenant: see the note in ops/seed-first-tenant.d/residual-ddpoa.js).
          const forced = !!e.always;
          const muted = inZoneBlock && !mine && !forced;
          // The applicable zone stays IN the blue text block like the rest of the report — it is set
          // apart by weight, not by a white panel. It used to flip to a white card inside the blue
          // card, which read as a foreign element against every other page (Calem 2026-08-09). The
          // editor (white essay) keeps its classic light-blue highlight, where blue-on-white works.
          const mineInBlue = inCard && roView;
          const mineBg = ZONE_SHADES[Math.min(zoneHlSeen++, ZONE_SHADES.length - 1)];
          return (
          <React.Fragment key={i}>
            {e.h
              ? <div style={{ ...ddSumHead, marginTop: inCard ? 4 : 18, ...(e.t.includes('Zoning Map') ? { textAlign: 'center' as const } : {}) }} className={inCard ? 'dd-sum-head' : undefined}>{richText(e.t)}</div>
              : <p className={muted ? 'dd-zone-muted' : (mineInBlue && mine ? 'dd-zone-mine' : undefined)}
                   style={{ ...ddNote, fontSize: inCard ? undefined : 13.5, color: muted ? '#9a9a9a' : INK, fontWeight: muted ? 400 : undefined,
                     // In the client report the applicable zone is plain block text, bolded only.
                     ...(mine && mineInBlue ? { fontWeight: 700 } : {}),
                     // Full weight, but never the "this is your zone" highlight panel — it is not.
                     ...(forced && !mine ? { fontWeight: 700 } : {}),
                     ...(mine && !mineInBlue ? { background: mineBg, color: INK, borderLeft: `4px solid ${ZONE_BAR}`, padding: '10px 14px', borderRadius: 4, WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } : {}) }}>{richText(e.t)}</p>}
            {zoning.mapImage && e.t.includes('Zoning Map') && (
              <ZoningMap src={zoning.mapImage} alt={mapAlt} pin={p.mapPin} onPin={onPin} />
            )}
          </React.Fragment>
          );
        };
        if (A < 0 || Z < 0 || Z <= A || ZS <= A || ZS >= Z) return essay.map((e, i) => renderEntry(e, i, false));
        // Two blue cards (client report): "Adopted Zoning Regulations …" and "Zoning Interpretation"
        // — same light-blue box + white heading as every summary (user 2026-07-28).
        return (<>
          {essay.slice(0, A).map((e, i) => renderEntry(e, i, false))}
          {/* THREE cards, deliberately: the preamble (objectives, policy, "the zones are as
              follows:") is its OWN block and is never restyled; the twelve zones are their own
              block, where the applicable zone is emphasised and the rest greyed; the interpretation
              closes it (Calem 2026-08-09). Splitting them also stops the preamble being dragged
              into the per-zone highlighting.
              dd-sum-long: the zone block is the ~4-page part. `break-inside: avoid` is simply
              ignored by the browser on a block taller than a sheet, so it splits anyway — the class
              squares its corners in print so the continuation reads as one band instead of a
              second, half-drawn box. Every other summary card fits and stays whole. */}
          <div className="dd-sum" style={{ marginTop: 18 }}>{essay.slice(A, ZS).map((e, i) => renderEntry(e, A + i, true))}</div>
          <div className="dd-sum dd-sum-long" style={{ marginTop: 18 }}>{essay.slice(ZS, Z).map((e, i) => renderEntry(e, ZS + i, true))}</div>
          <div className="dd-sum" style={{ marginTop: 18 }}>{essay.slice(Z).map((e, i) => renderEntry(e, Z + i, true))}</div>
        </>);
      })()}
    </div>
  );
}

function PropertySection({ property, patch }: { property: any; patch: (p: any) => void }) {
  const p = property; const s = (k: string) => (v: string) => patch({ [k]: v });
  // Changing zoning / elevation / slope re-evaluates the standing 50% rule. It only ever fills a
  // BLANK usage or one this rule set itself (`maximumUsageAuto`) — the moment someone picks a value
  // by hand that choice sticks, and an already-issued report is never rewritten.
  const sRule = (k: string) => (v: string) => {
    const next = { ...p, [k]: v };
    const out: any = { [k]: v };
    const auto = autoUsageApplies(next);
    const mayFill = !String(p.maximumUsage || '').trim() || p.maximumUsageAuto === true;
    if (auto && mayFill) { out.maximumUsage = AUTO_USAGE_VALUE; out.maximumUsageAuto = true; }
    // Picking a zone also fills Suitable For, on the same "blank or previously auto" rule.
    if (k === 'zoning') {
      const sf = suitableForZone(v);
      const mayFillSf = !String(p.suitableFor || '').trim() || p.suitableForAuto === true;
      if (sf && mayFillSf) { out.suitableFor = sf; out.suitableForAuto = true; }
    }
    patch(out);
  };
  // A hand-picked wording sticks — the zone rule stops touching it.
  const sSuitable = (v: string) => patch({ suitableFor: v, suitableForAuto: false });
  const sfExpected = suitableForZone(p.zoning || '');
  const sfMismatch = !!sfExpected && !!String(p.suitableFor || '').trim() && p.suitableFor !== sfExpected;
  // A hand-picked value clears the auto flag so the rule stops touching it.
  const sUsage = (v: string) => patch({ maximumUsage: v, maximumUsageAuto: false });
  const ruleOn = autoUsageApplies(p);
  const usageMatches = String(p.maximumUsage || '') === AUTO_USAGE_VALUE;
  // m² is auto-calculated from the country's land units (Thailand: Rai/Ngan/Tarangwa, 1 Rai = 1,600 m², etc.);
  // a country without units types m² directly.
  const units = landAreaUnits();
  const sDim = (k: string) => (v: string) => { const merged = { ...p, [k]: v }; patch({ [k]: v, m2: m2of(merged) }); };
  return (
    <>
      <Row>
        <Text cols={12} required label="Name" name="property.name" value={p.name} onChange={s('name')} />
        <Sel cols={12} required name="property.landAndorBuildings" label="Land and/or Building(s)" options={LAND_ANDOR_BUILDINGS} value={p.landAndorBuildings} onChange={s('landAndorBuildings')} />
      </Row>
      <Row><AddressF cols={24} required label="Address" name="property.address" fixedCountry="Thailand" value={p.address} onChange={v => patch({ address: v })} /></Row>
      <Row>
        <Sel cols={12} required name="property.zoning" label="Zoning" options={ZONING} value={p.zoning} onChange={sRule('zoning')} />
        <Sel cols={12} required name="property.suitableFor" label="Suitable For" options={SUITABLE_FOR} value={p.suitableFor} onChange={sSuitable}
          note={sfMismatch ? 'Incorrect suitability for zoning' : ''}
          noteTone={sfMismatch ? 'error' : 'info'} />
      </Row>
      <Row>
        <Sel cols={12} required name="property.maximumUsage" label="Maximum Usage" options={MAX_USAGE} value={p.maximumUsage} onChange={sUsage}
          note={ruleOn ? (usageMatches
            ? 'Set to 50% under the standing rule — Zone 2 (0–80 m), below 35° slope, coloured zone (Aug 2026).'
            : 'Note: under the standing rule these parameters give 50% (Zone 2, below 35° slope, coloured zone). Left as chosen — change it only if this property really is an exception.') : ''}
          noteTone={ruleOn && !usageMatches ? 'warn' : 'info'} />
        <Sel cols={12} required name="property.landElevation" label="Land Elevation APPROX" options={LAND_ELEVATION} value={p.landElevation} onChange={sRule('landElevation')} />
      </Row>
      <Row>
        <Sel cols={12} required name="property.slope" label="Slope (in Degrees) APPROX" options={SLOPE} value={p.slope} onChange={sRule('slope')} />
        {units.map(u => <Num key={u.key} cols={Math.floor(12 / units.length)} required label={u.label} name={`property.${u.key}`} value={p[u.key]} onChange={sDim(u.key)} />)}
      </Row>
      <Row>
        <Num cols={12} required label="m²" value={p.m2} onChange={s('m2')} readOnly={units.length > 0} dp={2} hint={units.length ? `${landAreaHint()} (auto-calculated)` : undefined} />
        <Q cols={6} required label="Same Size as Buyer's Plot?" name="sameSize" options={['Yes', 'No']} value={p.sameSize || ''} onChange={s('sameSize')} />
        {p.sameSize === 'No' && <Num cols={6} required label="m² (Buyer's Plot)" name="property.m2Buyer" value={p.m2Buyer} onChange={s('m2Buyer')} />}
      </Row>
      {/* Property Map always; Land Elevation (Estimate) appears only when the elevation names 140m
          (Zone 3.1 / 3.2) — the old form's conditional field, missed by the 2026-07-26 parity pass
          because nobody had a high-elevation plot selected. Required exactly when visible. */}
      <Row>
        <UploadF cols={needsElevationEstimate(p.landElevation) ? 12 : 24} required multi label="Property Map" path="property.map" />
        {needsElevationEstimate(p.landElevation) && <UploadF cols={12} required multi label="Land Elevation (Estimate)" path="property.elevationEstimate" />}
      </Row>
      <PropertySummary p={p} onPin={pin => patch({ mapPin: pin })} />
    </>
  );
}

// ── Remaining sections (P3) — declarative replica of the Cognito form ────────
// Every section below is described by a spec (labels/options/required/cols pulled
// 1:1 from the original form definition) and rendered by one generic engine, so
// the layout, dropdowns, required-stars and validation all match the original.
const DateF = ({ label, required, value, onChange, cols = 24, name, readOnly, hint }: { label: string; required?: boolean; value: any; onChange: (v: string) => void; cols?: number; name?: string; readOnly?: boolean; hint?: string }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  if (ro) return <ROField label={label} cols={cols}>{fmtDMY(value)}</ROField>;
  return <Cell cols={cols}><QLabel required={required}>{label}</QLabel><input type="date" value={value || ''} readOnly={readOnly} onChange={e => onChange(e.target.value)} style={{ ...inStyle, ...(readOnly ? { background: '#e3e3e3', color: '#666' } : {}), ...errInput(err) }} />{hint && <div style={{ fontSize: 12, color: '#999', fontFamily: RC, marginTop: 4 }}>{hint}</div>}<ErrBox msg={err} /></Cell>;
};
const RichF = ({ label, required, value, onChange, cols = 24, name, hint }: { label: string; required?: boolean; value: any; onChange: (v: string) => void; cols?: number; name?: string; hint?: string }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  // commentToHtml sanitises stored HTML and upgrades legacy plain text — never render raw.
  if (ro) return <ROField label={label} cols={cols}><div className="rte-render" dangerouslySetInnerHTML={{ __html: commentToHtml(String(value || '')) }} /></ROField>;
  return (
    <Cell cols={cols}>{label ? <QLabel required={required}>{label}</QLabel> : null}
      <div style={errInput(err).borderColor ? { outline: '1px solid #D0021B', borderRadius: 9 } : undefined}>
        <RichTextEditor value={String(value || '')} onChange={onChange} minHeight={150} extras />
      </div>
      {hint && <div style={{ fontSize: 12, color: '#999', fontFamily: RC, marginTop: 4 }}>{hint}</div>}
      <ErrBox msg={err} />
    </Cell>
  );
};
const TextAreaF = ({ label, required, value, onChange, cols = 24, name, hint }: { label: string; required?: boolean; value: any; onChange: (v: string) => void; cols?: number; name?: string; hint?: string }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  if (ro) return <ROField label={label} cols={cols}>{value || ''}</ROField>;
  return <Cell cols={cols}><QLabel required={required}>{label}</QLabel><textarea rows={5} value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inStyle, resize: 'vertical', ...errInput(err) }} />{hint && <div style={{ fontSize: 12, color: '#999', fontFamily: RC, marginTop: 4 }}>{hint}</div>}<ErrBox msg={err} /></Cell>;
};
const RadioF = ({ label, required, value, onChange, options, cols = 24, name }: { label: string; required?: boolean; value: any; onChange: (v: string) => void; options: string[]; cols?: number; name?: string }) => {
  const ro = React.useContext(DDReadOnly); const err = React.useContext(DDErrors)[name || ''] || '';
  if (ro) return <ROField label={label} cols={cols}>{value || ''}</ROField>;
  // Few options → inline; many → aligned grid columns (matches the original layout, no ragged wrapping).
  const many = options.length > 4;
  const wrap: React.CSSProperties = many
    ? { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '10px 18px', paddingTop: 6 }
    : { display: 'flex', flexWrap: 'wrap', gap: '8px 22px', paddingTop: 6 };
  return (
    <Cell cols={cols}><QLabel required={required}>{label}</QLabel>
      <div style={wrap}>
        {options.map(o => (
          <label key={o} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 15, fontFamily: RC, color: INK, cursor: 'pointer', lineHeight: 1.35 }}>
            <input type="radio" checked={value === o} onChange={() => onChange(o)} style={{ accentColor: BLUE, marginTop: 3, flexShrink: 0 }} />{o}
          </label>
        ))}
      </div>
      <ErrBox msg={err} />
    </Cell>
  );
};

// Option lists (verbatim from the form definition).
const WORDNUM: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
const YES_NO = ['Yes', 'No'];
const NUM_1_3L = ['one', 'two', 'three'];
const NUM_1_5L = ['one', 'two', 'three', 'four', 'five'];
const NUM_1_6C = ['One', 'Two', 'Three', 'Four', 'Five', 'Six'];
const NUM_1_7C = ['One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven'];
const NUM_1_12L = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];
// Site Identification → Conducted By: the tenant's inspectors (setting dd_site_inspectors, services/ddPoaContent.ts)
// then the product's own "Third Party". One array, refilled IN PLACE by syncInspectors() when the setting has
// loaded, because the static SECTIONS spec and the import schema (DDOC_OPTIONS) both hold this reference.
const THIRD_PARTY = 'Third Party';
const CONDUCTED_BY: string[] = [THIRD_PARTY];
function syncInspectors(names: string[]): void {
  const next = [...names.filter(n => n && n !== THIRD_PARTY), THIRD_PARTY];
  if (next.length === CONDUCTED_BY.length && next.every((n, i) => n === CONDUCTED_BY[i])) return;
  CONDUCTED_BY.splice(0, CONDUCTED_BY.length, ...next);
}
const PERMIT_BUILDINGS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty', 'thirty nine', 'fifty five', 'swimming pool'];
const PERMIT_LEVELS = ['no', '1', '1.5', '2', '2.5', '3', '3.5', '4'];
const PERMIT_ACTUAL_BUILDINGS = ['no', 'Still Under Construction', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'fifty five'];
const PERMIT_ACTUAL_LEVELS = ['no (under construction)', 'undetermined', '1', '1.5', '2', '2.5', '3', '3.5', '4'];
const PERMIT_USE = ['living and renting', 'living only', 'renting only', 'living and commercial', 'condominium', 'hotel', 'swimming', 'residential house and pool'];
const DEED_TYPE_3 = ['Chanote', 'Nor Sor Sam Gor', 'Por Tor Bor 5'];
const PLOT_UTIL = ['Internal Road', 'Common Area (Gym, Pool etc)', 'Plot (for Lease)', 'Plot (for Sale)', 'Condo (for Sale)'];
const RIGHTS = ['"ownership" over the land, to deal with or to use it to the exclusion of others', '"ownership" over the condominium, to deal with or to use it to the exclusion of others', '"possession" over the land, to possess the land and use the benefit of the land as an owner', '"occupation" over the land, to occupy the land (has been issued a tax number and has paid tax) for the use of the land'];
const ACCESS_DEED_TYPE = ['Chanote', 'Nor Sor 3 Gor', 'Por Tor Bor 5'];
const CONNECT = ['a government road', 'a private *registered* road', 'a private *unregistered* road', 'a private *registered* soi', 'a private *unregistered* soi', 'this property'];
const START_ROAD = ['a major arterial government road', 'a government road'];
const COLORS = ['Blue', 'Green', 'Orange', 'Pink', 'Yellow', 'Purple', 'Brown', 'Red', 'Grey', 'White'];
const REG_BY = ['Individual', 'Company'];
const PREV_OWNER = ['individual', 'company'];
const PURCHASE_OF = ['land only', 'building only', 'land and building', 'land and buildings'];
const LIEN_TYPE = ['mortgage', 'kai fak', 'lease', 'superficies'];
const LENDERS = ['Bangkok Bank', 'Siam Commerical Bank', 'Thanachart Bank', 'Krungsri Bank', 'CIMB Bank', 'Krung Thai Bank'];
const LC_ORIGINAL_TYPE = ['Chanote', 'Nor Sor Sam Gor', 'Por Tor Bor 5', 'Sor Kor 1'];
const LC_TYPE = ['subdivide', 'upgrade', 'combine'];
const LC_UPGRADED = ['Chanote', 'Nor Sor 3 Gor'];
const LC_SUBDIVIDED = ['Chanote', 'Nor Sor 3 Gor', 'Por Tor Bor 5'];
const REQUIREMENTS = ['a mortgage', 'a Kai Fak', 'unregistered access', 'no building permit', 'no house book', 'a de-registered company', 'company settlements (tax, profit etc)', 'a deceased owner', 'a deceased director', 'a non-compliant building', 'a building encroachment', 'land to be subdivided', 'land to be upgraded', 'a registered lease or superficies', 'specific building regulations', 'register condominium title deed'];
// Standard "Further Details" wording for a Seller Requirement, lifted VERBATIM from the firm's own
// past Due Diligence reports (only obvious spelling slips corrected — see the note to the team).
// Requirements the firm has never written up are deliberately absent: the operator writes those,
// rather than have the system invent legal wording. {property} comes from the Property page.
// Everything here is only a starting point — the box stays fully editable.
// Additional Comments (Calem's wording, 2026-07-24). The opening is now a FIXED statement (not an
// editable box) that names the property automatically; only the Remarks box is editable. Colour
// coding: "Seller Requirements" = blue, "Remarks" = orange, matching the two summary headings.
const CMT_BLUE = '#1F72C0';     // Seller Requirements
const CMT_ORANGE = '#C2410C';   // Remarks
const COMMENTS_REMARKS = '<p><strong>Remark 1:</strong></p><ul><li></li></ul>';
const REQ_DETAILS: Record<string, string> = {
  'a mortgage': 'The Seller should (as is normally required) remove/clear the lien/mortgage from the property prior to or on the day of ownership transfer of the property',
  'no house book': 'The Seller should provide a tabien baan (house book) for the property once the building is at or near completion (if not already)',
  'no building permit': 'Prior to the developer commencing construction of the Building, they must obtain an approved Building Permit from the Tessaban',
  'a registered lease or superficies': "The developer must register a thirty (30) year sub-lease on the buyer/lessee's land being; {property}",
  'register condominium title deed': 'The developer must apply for the condominium license once the project is complete and issue a Chanote (title deed) for {property}',
};
const reqDetailText = (req: any, propertyName: any): string =>
  (REQ_DETAILS[String(req || '')] || '').replace('{property}', String(propertyName || '').trim() || 'the property');
const hasReqDetail = (req: any): boolean => REQ_DETAILS[String(req || '')] !== undefined;
const DIFFICULTY = ['easy', 'easy-medium', 'medium', 'medium-difficult', 'difficult'];

type FType = 'text' | 'num' | 'sel' | 'date' | 'name' | 'addr' | 'file' | 'radio' | 'textarea' | 'rich';
type FSpec = { k: string; label: string; t: FType; opts?: string[]; cols: number; req?: boolean; ro?: boolean | ((o: any) => boolean); hint?: string; show?: (o: any) => boolean; multi?: boolean; dp?: number; internal?: boolean };
type SectionSpec = { top?: FSpec[][]; countGate?: (o: any) => boolean; itemLabel?: string; instanceTitle?: (i: number, item?: any) => string; instance?: (i: number) => FSpec[][]; bottom?: FSpec[][]; emptyHint?: string; derive?: (o: any, root?: any, changedKey?: string) => any };
const atLeast = (n: number) => (d: any) => (WORDNUM[String(d.count || '').toLowerCase()] || 0) >= n;

const liensYes = (d: any) => d.liens === 'Yes';
// Calem 2026-09-05: "if Duration is 'indefinite' we don't need a date field" — the Duration text is
// the signal (that's also exactly what the old-system import writes there). The expiryTerm value
// from the short-lived dropdown is still honoured for any row that set it.
const lienIndef = (d: any) => d.expiryTerm === 'Indefinite' || /^\s*indef/i.test(String(d.duration || ''));
// Access deed/registration fields are conditional on the road being connected to
// (verbatim from the original): "registered" matches a private registered OR
// unregistered road/soi (both contain "registered"); "*registered*" only matches
// the *registered* options; "*government*" matches the public-road case.
const accReg = (d: any) => (d.connectingTo || '').includes('registered');
const accRegStar = (d: any) => (d.connectingTo || '').includes('*registered*');
const accGovStar = (d: any) => (d.connectingTo || '').includes('*government*');
const addMonthsMinusDay = (iso: any, months: any): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').slice(0, 10));
  const n = Math.round(Number(String(months ?? '').replace(/[^0-9.-]/g, '')));
  if (!m || !Number.isFinite(n) || n <= 0) return '';
  const y = Number(m[1]), mo = Number(m[2]) - 1, day = Number(m[3]);
  const lastOfTarget = new Date(Date.UTC(y, mo + n + 1, 0)).getUTCDate();
  const d = new Date(Date.UTC(y, mo + n, Math.min(day, lastOfTarget)));
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const PERMIT_EXPIRY = ['One year after issue', 'Two years after issue', 'Other (choose date)'];
const addYearsMinusDay = (iso: any, years: number): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').slice(0, 10));
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]) + years, Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
};
const m2Text = (v: any): string => {
  const n = Number(String(v ?? '').replace(/,/g, ''));
  return String(v ?? '').trim() !== '' && Number.isFinite(n) ? fmtNumber(n, 2) : String(v ?? '');
};
const m2of = (o: any) => landAreaM2(o);   // the country pack's land units → m² (utils/countryFeatures.ts)
const SECTIONS: Record<string, SectionSpec> = {
  site: {
    top: [
      [{ k: 'conductedBy', label: 'Conducted By', t: 'sel', opts: CONDUCTED_BY, cols: 12, req: true }, { k: 'date', label: 'Date', t: 'date', cols: 12, req: true }],
      [{ k: 'sitePhoto1', label: 'Site Photo 1', t: 'file', cols: 12, req: true }, { k: 'sitePhoto2', label: 'Site Photo 2', t: 'file', cols: 12, req: true }],
      [{ k: 'sitePhoto3', label: 'Site Photo 3', t: 'file', cols: 12 }, { k: 'sitePhoto4', label: 'Site Photo 4', t: 'file', cols: 12 }],
      [{ k: 'sitePhoto5', label: 'Site Photo 5', t: 'file', cols: 12 }, { k: 'sitePhoto6', label: 'Site Photo 6', t: 'file', cols: 12 }],
    ],
  },
  buildingPermit: {
    top: [[{ k: 'count', label: 'Number of Permits', t: 'sel', opts: NUM_1_3L, cols: 12, req: true }]],
    itemLabel: 'Permit', instanceTitle: i => `Building Permit ${i + 1}`,
    instance: () => [
      [{ k: 'issueDate', label: 'Issue Date', t: 'date', cols: 6, req: true }, { k: 'expiryTerm', label: 'Permit Term', t: 'sel', opts: PERMIT_EXPIRY, cols: 6, req: true, internal: true },
       { k: 'expiryDate', label: 'Expiry Date', t: 'date', cols: 6, req: true, ro: o => o.expiryTerm === PERMIT_EXPIRY[0] || o.expiryTerm === PERMIT_EXPIRY[1] },
       { k: 'permitNumber', label: 'Permit Number', t: 'text', cols: 6, req: true }],
      [{ k: 'buildingsPermitted', label: 'Buildings Permitted', t: 'sel', opts: PERMIT_BUILDINGS, cols: 8, req: true }, { k: 'levelsPermitted', label: 'Levels Permitted', t: 'sel', opts: PERMIT_LEVELS, cols: 8, req: true }, { k: 'm2Permitted', label: 'm2 Permitted', t: 'num', cols: 8, req: true }],
      [{ k: 'numberOfActualBuildings', label: 'Number of Actual Buildings', t: 'sel', opts: PERMIT_ACTUAL_BUILDINGS, cols: 8, req: true }, { k: 'actualLevels', label: 'Actual Levels', t: 'sel', opts: PERMIT_ACTUAL_LEVELS, cols: 8, req: true }, { k: 'permittedUse', label: 'Permitted Use', t: 'sel', opts: PERMIT_USE, cols: 8, req: true }],
      [{ k: 'issuedTo', label: 'Issued To', t: 'text', cols: 12, req: true }, { k: 'buildingPermit', label: 'Building Permit', t: 'file', cols: 12, req: true }],
    ],
    derive: m => {
      const yrs = m.expiryTerm === PERMIT_EXPIRY[0] ? 1 : m.expiryTerm === PERMIT_EXPIRY[1] ? 2 : 0;
      return yrs ? { expiryDate: addYearsMinusDay(m.issueDate, yrs) } : {};
    },
  },
  houseBook: {
    top: [[{ k: 'count', label: 'Number', t: 'sel', opts: NUM_1_6C, cols: 12, req: true }]],
    itemLabel: 'House Book', instanceTitle: i => `House Book ${i + 1}`,
    instance: i => [
      [{ k: 'number', label: 'Number', t: 'text', cols: 12, req: true }, { k: 'issued', label: 'Issued', t: 'date', cols: 12, req: true }],
      [{ k: 'address', label: 'Address', t: 'addr', cols: 24, req: true }],
      [{ k: 'houseBook', label: `House Book ${i + 1}`, t: 'file', cols: 24, req: true }],
    ],
  },
  titledeed: {
    top: [[{ k: 'count', label: 'Number', t: 'sel', opts: NUM_1_7C, cols: 12, req: true }]],
    itemLabel: 'Title Deed', instanceTitle: i => `Title Deed ${i + 1}`,
    instance: i => [
      [{ k: 'type', label: 'Type', t: 'sel', opts: DEED_TYPE_3, cols: 8, req: true }, { k: 'plotUtilization', label: 'Plot Utilization', t: 'sel', opts: PLOT_UTIL, cols: 8, req: true }, { k: 'number', label: 'Number', t: 'text', cols: 8, req: true }],
      ...(landAreaUnits().length ? [landAreaUnits().map(u => ({ k: u.key, label: u.label, t: 'text', cols: Math.floor(24 / landAreaUnits().length), req: true }) as FSpec)] : []),
      [{ k: 'rights', label: 'Rights', t: 'sel', opts: RIGHTS, cols: 12, req: true }, landAreaUnits().length ? { k: 'm2', label: 'm2', t: 'num', cols: 12, ro: true, dp: 2, hint: `${landAreaHint()} (auto-calculated)` } : { k: 'm2', label: 'm2', t: 'num', cols: 12, dp: 2 }],
      [{ k: 'titleDeed', label: `Title Deed ${i + 1}`, t: 'file', cols: 24, req: true, multi: true }],
    ],
    derive: m => (landAreaUnits().length ? { m2: m2of(m) } : {}),   // no land units → m² is typed, never overwritten
  },
  access: {
    top: [[{ k: 'count', label: 'Access Roads From the Main Government Road', t: 'sel', opts: NUM_1_12L, cols: 12, req: true }, { k: 'roadMap', label: 'Road Map', t: 'file', cols: 12, req: true }]],
    itemLabel: 'Road', instanceTitle: i => `Access Road ${i + 1}`,
    instance: i => [
      i === 0
        ? [{ k: 'startGovernmentRoad', label: 'Start - Goverment Road', t: 'sel', opts: START_ROAD, cols: 6, req: true }, { k: 'governmentRoadName', label: 'Government Road Name', t: 'text', cols: 6, req: true }, { k: 'colorCode', label: 'Color Code', t: 'sel', opts: COLORS, cols: 6, req: true }, { k: 'connectingTo', label: 'Connecting To', t: 'sel', opts: CONNECT, cols: 6, req: true }]
        : [{ k: 'connectingFrom', label: 'Connecting From', t: 'sel', opts: CONNECT, cols: 6, req: true }, { k: 'roadSoiName', label: 'Road/Soi Name', t: 'text', cols: 6, req: true }, { k: 'colorCode', label: 'Color Code', t: 'sel', opts: COLORS, cols: 6, req: true }, { k: 'connectingTo', label: 'Connecting To', t: 'sel', opts: CONNECT, cols: 6, req: true }],
      [{ k: 'deedType', label: 'Deed Type', t: 'sel', opts: ACCESS_DEED_TYPE, cols: 12, req: true, show: accReg }, { k: 'deedNumber', label: 'Deed Number', t: 'text', cols: 12, req: true, show: accReg }],
      [{ k: 'registeredBy', label: 'Registered By', t: 'sel', opts: REG_BY, cols: 12, req: true, show: accRegStar }, { k: 'registeredOn', label: 'Registered On', t: 'date', cols: 12, req: true, show: accRegStar }],
      [{ k: 'name', label: 'Name', t: 'name', cols: 12, req: true, show: d => accRegStar(d) && (d.registeredBy || '').includes('Individual') }, { k: 'idCard', label: 'ID Card', t: 'text', cols: 12, req: true, show: d => accRegStar(d) && (d.registeredBy || '').includes('Individual') }],
      [{ k: 'company', label: 'Company', t: 'text', cols: 12, req: true, show: d => accRegStar(d) && (d.registeredBy || '').includes('Company') }, { k: 'taxId', label: 'Tax ID', t: 'text', cols: 12, req: true, show: d => accRegStar(d) && (d.registeredBy || '').includes('Company') }],
      [{ k: 'access', label: 'Access', t: 'file', cols: 12, req: true, show: accReg }, { k: 'privateRoadRegistration', label: 'Private Road Registration', t: 'file', cols: 12, req: true, show: accRegStar }],
      [{ k: 'publicRoadRegistration', label: 'Public Road Registration', t: 'file', cols: 12, req: true, show: accGovStar }],
    ],
  },
  purchaseAgreement: {
    top: [[{ k: 'count', label: 'How Many Agreements', t: 'sel', opts: NUM_1_3L, cols: 12, req: true }]],
    itemLabel: 'Agreement', instanceTitle: i => `Purchase Agreement ${i + 1}`,
    instance: () => [
      [{ k: 'previousOwner', label: 'Previous Owner', t: 'sel', opts: PREV_OWNER, cols: 12, req: true }, { k: 'issueDate', label: 'Issue Date', t: 'date', cols: 6, req: true }, { k: 'titleDeedNumber', label: 'Title Deed Number', t: 'text', cols: 6, req: true }],
      [{ k: 'companyName', label: 'Company Name', t: 'text', cols: 12, req: true, show: d => (d.previousOwner || '').includes('company') }, { k: 'taxId', label: 'Tax ID', t: 'text', cols: 12, req: true, show: d => (d.previousOwner || '').includes('company') }],
      [{ k: 'name', label: 'Name', t: 'name', cols: 12, req: true, show: d => (d.previousOwner || '').includes('individual') }, { k: 'idCard', label: 'ID Card', t: 'text', cols: 12, req: true, show: d => (d.previousOwner || '').includes('individual') }],
      [{ k: 'purchaseOf', label: 'Purchase Of', t: 'sel', opts: PURCHASE_OF, cols: 12, req: true }, { k: 'purchasePrice', label: 'Purchase Price', t: 'num', cols: 12, req: true }],
      [{ k: 'purchaseAgreement', label: 'Purchase Agreement (Land Office)', t: 'file', cols: 12, req: true, multi: true }],
    ],
  },
  landChronology: {
    top: [[{ k: 'count', label: 'How Many Upgrades/Subdivisions', t: 'sel', opts: NUM_1_5L, cols: 12, req: true }, { k: 'originalType', label: 'Original Type', t: 'sel', opts: LC_ORIGINAL_TYPE, cols: 6, req: true }, { k: 'originalNumber', label: 'Original Number', t: 'text', cols: 6, req: true }]],
    itemLabel: 'Change', instanceTitle: (i, it) => `${i + 1}. Land ${it && it.type ? it.type + 'd' : 'Chronology'}`,
    instance: () => [
      [{ k: 'type', label: 'Type', t: 'sel', opts: LC_TYPE, cols: 6, req: true }, { k: 'upgradedTo', label: 'Upgraded To', t: 'sel', opts: LC_UPGRADED, cols: 6, req: true, show: d => d.type === 'upgrade' }, { k: 'subdividedTo', label: 'Subdivided To', t: 'sel', opts: LC_SUBDIVIDED, cols: 6, req: true, show: d => d.type === 'subdivide' }, { k: 'combinedTo', label: 'Combined To', t: 'sel', opts: LC_UPGRADED, cols: 6, req: true, show: d => d.type === 'combine' }],
      [{ k: 'number', label: 'Number', t: 'text', cols: 6, req: true }, { k: 'ownerAtTheTime', label: 'Owner (at the time)', t: 'sel', opts: REG_BY, cols: 12, req: true }, { k: 'issueDate', label: 'Issue Date', t: 'date', cols: 6, req: true }],
      [{ k: 'companyName', label: 'Company Name', t: 'text', cols: 18, req: true, show: d => d.ownerAtTheTime === 'Company' }, { k: 'taxId', label: 'Tax ID', t: 'text', cols: 6, req: true, show: d => d.ownerAtTheTime === 'Company' }],
      [{ k: 'name', label: 'Name', t: 'name', cols: 18, req: true, show: d => d.ownerAtTheTime === 'Individual' }],
    ],
    bottom: [[
      { k: 'doc1', label: 'Upgrade/Subdivide/Combine 1', t: 'file', cols: 12, req: true, show: atLeast(1), multi: true },
      { k: 'doc2', label: 'Upgrade/Subdivide/Combine 2', t: 'file', cols: 12, req: true, show: atLeast(2), multi: true },
      { k: 'doc3', label: 'Upgrade/Subdivide/Combine 3', t: 'file', cols: 12, req: true, show: atLeast(3), multi: true },
      { k: 'doc4', label: 'Upgrade/Subdivide/Combine 4', t: 'file', cols: 12, req: true, show: atLeast(4), multi: true },
      { k: 'doc5', label: 'Upgrade/Subdivide/Combine 5', t: 'file', cols: 12, req: true, show: atLeast(5), multi: true },
    ]],
  },
  liens: {
    top: [
      [{ k: 'liens', label: 'Liens & Encumbrances', t: 'radio', opts: YES_NO, cols: 12, req: true }, { k: 'type', label: 'Type', t: 'sel', opts: LIEN_TYPE, cols: 12, req: true, show: liensYes }],
      // Only a MORTGAGE is held by one of the banks — a lease / kai fak / superficies is held by a
      // person or company, whose name goes in "Lender's Name". The summary already splits on this
      // (mortgage → lender, otherwise → lendersName); forcing a bank on every type was writing
      // "Lender: Bangkok Bank" onto leases and blocking the page until one was picked.
      [{ k: 'lender', label: 'Lender (bank)', t: 'sel', opts: LENDERS, cols: 12, req: true, show: d => liensYes(d) && d.type === 'mortgage' }, { k: 'duration', label: 'Duration (months)', t: 'text', cols: 12, show: liensYes, hint: 'A number of months — or "indefinite" for a lien with no end date.' }],
      [{ k: 'lendersName', label: "Lender's Name", t: 'name', cols: 12, req: true, show: d => liensYes(d) && !!d.type && d.type !== 'mortgage' }, { k: 'amount', label: 'Amount', t: 'num', cols: 12, req: true, show: liensYes }],
      // Indefinite lien (Calem 2026-09-05): typing "indefinite" in Duration IS the signal — the
      // Expiry Date disappears and stops being required. That is also exactly what the old-system
      // import writes into Duration, so imported superficies rows come out clean by themselves.
      [{ k: 'registrationDate', label: 'Registration Date', t: 'date', cols: 12, req: true, show: liensYes },
       { k: 'expiryDate', label: 'Expiry Date', t: 'date', cols: 12, req: true, show: d => liensYes(d) && !lienIndef(d),
         ro: o => !!addMonthsMinusDay(o.registrationDate, o.duration),
         hint: 'Auto-calculated from the registration date and duration — clear Duration to enter a date by hand, or type "indefinite" in Duration for a lien with no end date.' }],
      [{ k: 'mortgageAgreement', label: 'Mortgage Agreement', t: 'file', cols: 12, req: true, show: d => liensYes(d) && d.type === 'mortgage' }, { k: 'leaseAgreement', label: 'Lease Agreement', t: 'file', cols: 12, req: true, show: d => liensYes(d) && d.type === 'lease' }],
      [{ k: 'kaiFakAgreement', label: 'Kai Fak Agreement', t: 'file', cols: 12, req: true, show: d => liensYes(d) && d.type === 'kai fak' }, { k: 'superficiesAgreement', label: 'Superficies Agreement', t: 'file', cols: 12, req: true, show: d => liensYes(d) && d.type === 'superficies' }],
    ],
    derive: o => { const e = addMonthsMinusDay(o.registrationDate, o.duration); return e ? { expiryDate: e } : {}; },
  },
  requirements: {
    top: [[{ k: 'gate', label: 'Seller Requirements', t: 'radio', opts: YES_NO, cols: 6, req: true }, { k: 'count', label: 'How Many', t: 'sel', opts: NUM_1_5L, cols: 6, req: true, show: d => d.gate === 'Yes' }]],
    countGate: d => d.gate === 'Yes',
    itemLabel: 'Requirement', instanceTitle: i => `Requirement ${i + 1}`,
    instance: () => [
      [{ k: 'requirement', label: 'Requirement', t: 'radio', opts: REQUIREMENTS, cols: 18, req: true }, { k: 'difficultyLevel', label: 'Difficulty Level', t: 'sel', opts: DIFFICULTY, cols: 6, req: true }],
      [{ k: 'furtherDetails', label: 'Further Details', t: 'textarea', cols: 24, hint: 'Pre-filled with the firm\u2019s standard wording for the selected requirement \u2014 edit freely. Requirements with no standard wording are left blank for you to write.' }],
    ],
    // `furtherDetailsAuto` tracks who owns the box. An explicit flag (rather than string-matching the
    // wording) is what keeps this safe: picking a requirement with NO standard wording CLEARS the box
    // instead of leaving the previous requirement's legal text behind, and an operator edit locks the
    // box for good \u2014 including a box they deliberately emptied.
    derive: (o, root, k) => {
      if (k === 'furtherDetails') return { furtherDetailsAuto: false };   // operator now owns it
      if (k === 'requirement') {
        if (o.furtherDetailsAuto === false) return {};                    // hands off \u2014 operator wrote it
        return { furtherDetails: reqDetailText(o.requirement, root?.property?.name), furtherDetailsAuto: true };
      }
      return {};
    },
  },
  // Rendered by the custom CommentsSection. The opening is a fixed statement and Remarks are
  // optional ("any/all FURTHER remarks"), so there is nothing to validate on this page.
  comments: {
    top: [],
  },
};

// ── Auto-generated bottom-of-page summaries (the calculated text blocks in the
// original Cognito form — heading + intro + enumerated items + static notes) ──
const sb = (x: any) => <strong>{x === 0 ? '0' : (x || '')}</strong>;
const LET = ['a', 'b', 'c', 'd', 'e', 'f', 'g'];
const sumWrap: React.CSSProperties = { marginTop: 24, paddingTop: 16, borderTop: '1px solid #ececec', fontSize: 15, color: INK, lineHeight: 1.75, fontFamily: RC };
function SumBox({ title, children }: { title: string; children: React.ReactNode }) {
  return <div style={sumWrap} className="dd-sum"><div style={ddSumHead} className="dd-sum-head">{title}</div>{children}</div>;
}
function itemsOf(d: any, gate = true) { const n = gate ? (WORDNUM[String(d.count || '').toLowerCase()] || 0) : 0; const arr = d.items || []; return Array.from({ length: n }).map((_, i) => arr[i] || {}); }
// The a) b) c)… Seller Requirement lines. Shared so the copy embedded in Additional Comments is
// always word-for-word identical to the one on the Seller Requirements page.
function sellerReqLines(d: any): React.ReactNode[] {
  return itemsOf(d, true).map((r: any, i: number) => (
    <p key={i} className="dd-sum-point" style={ddDeedItem}><strong>{LET[i]})</strong> the property currently has "{sb(r.requirement)}" which will be required prior to purchase. This requirement should be {sb(r.difficultyLevel)} to complete. By "{r.difficultyLevel || ''}" to complete, we mean; {r.furtherDetails || ''}.</p>
  ));
}

const SUMM: Record<string, (d: any) => React.ReactNode> = {
  site: d => (
    <SumBox title="Site Identification Summary">
      <p style={ddPara}>Although the property was NOT surveyed by <LegalFirmName />, a visual site identification was conducted by {sb(d.conductedBy)} on {sb(fmtLongDate(d.date))}, of the site "as is" today, and these photographs have been included above for your reference.</p>
      {/* Matches the firm's established DD-report treatment: the caveat itself reads as normal body
          text, and the two actions the buyer must take are pulled out as indented blue paragraphs
          with the survey types underlined — that is what makes them impossible to skim past. */}
      <p style={ddNote}><strong>Note 1:</strong> although we carry out a visual inspection of this due diligence <strong>DOES NOT</strong> include any building and/or land surveys so, please consider that;</p>
      <p className="dd-sum-point" style={ddSurveyNote}><strong>a)</strong> to be certain that the building(s) is/are within the required parameters of the permit, the plot, and/or structurally sound, you'll need to engage a third party specialist to complete a <strong style={{ textDecoration: 'underline' }}>building survey</strong> on your behalf and/or;</p>
      <p className="dd-sum-point" style={ddSurveyNote}><strong>b)</strong> to be certain that the land has been correctly pegged out (the boundaries are 100% correct), determine site contours, elevation or soil type and stability, you'll need to engage a third party specialist to complete a <strong style={{ textDecoration: 'underline' }}>land survey</strong> on your behalf.</p>
      <p style={ddNote}><strong>Note 2:</strong> if you have visited the site on or prior to {sb(fmtLongDate(d.date))}, and the above photographs <strong>do not look recognizable</strong> then, please inform us immediately upon receipt of this report!</p>
    </SumBox>
  ),
  buildingPermit: d => {
    const its = itemsOf(d); const n = its.length;
    return <SumBox title="Building Permit Summary">
      <p style={ddPara}>A builing permit must be issued from the Tessaban for any/all structures to be legally constructed on the land. For this property, there {n > 1 ? 'have' : 'has'} been {sb(d.count)} building permit{n > 1 ? 's' : ''} issued as follows;</p>
      {its.map((p, i) => (<React.Fragment key={i}>
        <p className="dd-sum-point" style={ddDeedItem}><strong>{LET[i]})</strong> Permit Number - {sb(p.permitNumber)}, was issued to {sb(p.issuedTo)} on {sb(fmtLongDate(p.issueDate))} and expires on {sb(fmtLongDate(p.expiryDate))}. This building permit stipulates a permitted use as "{sb(p.permittedUse)}" and allows for {sb(p.buildingsPermitted)} structure(s) of {sb(p.levelsPermitted)} level(s) with a maximum total footprint of {sb(p.m2Permitted ? thb(p.m2Permitted) + ' m2' : '')}.</p>
        <p style={ddNote}><strong>Note 1{n > 1 ? LET[i] : ''}:</strong> the above building permit is/was valid until {fmtLongDate(p.expiryDate)} and, providing construction has commenced, and there is evidence of such, prior to {fmtLongDate(p.expiryDate)} then, there should be no issues in gaining final approval once construction of the structure(s) are finished.</p>
      </React.Fragment>))}
      <p style={ddNote}><strong>Note 2:</strong> there's currently {sb(its[0]?.numberOfActualBuildings)} physical / actual building(s) of {sb(its[0]?.actualLevels)} level(s) on the property at the time that this report was being compiled.</p>
    </SumBox>;
  },
  houseBook: d => {
    const its = itemsOf(d); const n = its.length;
    return <SumBox title="House Book Summary">
      <p style={ddPara}>{sb(d.count)} house book{n === 1 ? '' : 's'} {n === 1 ? 'has' : 'have'} been issued for {n === 1 ? 'a building' : 'buildings'} on this property.</p>
      {its.map((h, i) => (<p key={i} className="dd-sum-point" style={ddDeedItem}><strong>{LET[i]})</strong> House Book number: {sb(h.number)}, issued on {sb(fmtLongDate(h.issued))} for {sb(ddAddr(h.address))}{i < n - 1 ? ' and;' : '.'}</p>))}
      <p style={ddNote}><strong>Note:</strong> a house book or blue book or "tabien ban" (in Thai) will be issued per sellable space and typically gets issued once a building is near to or has reached completion.</p>
    </SumBox>;
  },
  titledeed: d => {
    const its = itemsOf(d); const n = its.length;
    return <SumBox title="Title Deed Summary">
      <p style={ddPara}>{sb(d.count)} title deed{n === 1 ? '' : 's'} {n === 1 ? 'is' : 'are'} included as part of this property purchase.</p>
      {its.map((td, i) => (<p key={i} className="dd-sum-point" style={ddDeedItem}><strong>{LET[i]})</strong> {sb(`${td.type || ''}: ${td.number || ''}`)} which gives the holder {sb(td.rights)}. {td.type || ''}: {td.number || ''} is {landAreaUnits().map(u => `${td[u.key] || ''} ${u.label}`).join(', ')}{landAreaUnits().length ? ' or ' : ''}{sb(m2Text(td.m2 || m2of(td)) + ' m2')} in total size{i < n - 1 ? ' and;' : '.'}</p>))}
      {landAreaUnits().length > 0 && <p style={ddNote}><strong>Note 1:</strong>{landAreaUnits().map((u, i, all) => (
        <React.Fragment key={u.key}>{i === 0 ? ' ' : i === all.length - 1 ? ' and ' : ', '}{i > 0 && i === all.length - 1 ? '' : '1 '}{u.label} = <strong>{fmtNumber(u.m2)}m2</strong></React.Fragment>))}.</p>}
      <p style={ddNote}><strong>Note 2:</strong> the "Title Deed" is proof of ownership, possession or right of use over the land. If the person/entity is the original owner/possesser of the property then, their details will appear on the front page of the title deed. If however, they are not the original owner then, their details will be recorded on the rear of the title deed.</p>
    </SumBox>;
  },
  access: d => {
    const its = itemsOf(d); const n = its.length;
    return <SumBox title="Access/Servitude Summary">
      <p style={ddPara}>There's {sb(d.count)} roads (moo) and/or streets (soi) that {n > 1 ? 'combine together to make' : 'make'} the access road leading into the property. They are as follows:</p>
      {its.map((s, i) => {
        const last = i === n - 1;
        const from = i === 0 ? <>from {sb(s.startGovernmentRoad)} ({sb(s.governmentRoadName)})</> : <>from {sb(s.roadSoiName)}</>;
        return <p key={i} className="dd-sum-point" style={ddDeedItem}><strong>{i + 1})</strong> {from} you turn {last ? 'directly into' : 'into'} {sb(s.connectingTo)}{last ? '.' : <> ({sb(its[i + 1]?.roadSoiName)}) then;</>}</p>;
      })}
      <p style={ddNote}><strong>Note:</strong> private roads, if mentioned above, should be registered at the Land Office<LegalFirmRegion fmt={r => ` (${r})`} />, thus ensuring that the buyer has legal access to the property they are interested in purchasing.</p>
      <div style={ddNote}><div style={{ fontWeight: 700, marginBottom: 4 }}>About Servitude</div>Servitude is for a part the Thai legal equivalent of an English easement. It is a non-possessory interest in land. Servitude is governed by the Civil and Commercial code sections 1387 to 1401. Section 1387: 'An immovable property may be subject to a servitude by virtue of which the owner of such property is bound, for the benefit of another immovable property, to suffer certain act affecting his property or to refrain from exercising certain rights inherent in his ownership'. Servitude usually involves two or more separate properties/plots of land, one of which is burdened and the other benefited by the servitude. The burdened parcel is called the servient property and the benefited parcel the dominant property. Servitude can involve several kinds of benefits and burdens, but usually it involves the use of neighboring well, the use of an access road over adjoining land plots, laying irrigation ditches, laying pipelines or utilities over neighboring plots. A registered right of servitude is an important right in case a plot of land is surrounded by other plots without direct access to a public road. In this case a right of servitude registered over adjoining plots guarantees uninterrupted access to dominant property.</div>
    </SumBox>;
  },
  purchaseAgreement: d => {
    const its = itemsOf(d); const n = its.length;
    return <SumBox title="Purchase Agreement (Land Office) Summary">
      <p style={ddPara}>There {n > 1 ? 'are' : 'is'} {sb(d.count)} Purchase Agreement{n > 1 ? 's' : ''} issued by the Land Office showing;</p>
      {its.map((a, i) => {
        const who = a.previousOwner === 'company' ? a.companyName : ddName(a.name);
        const idn = a.previousOwner === 'company' ? a.taxId : a.idCard;
        return <p key={i} className="dd-sum-point" style={ddDeedItem}><strong>{LET[i]})</strong> that the previous owner; {sb(who)}, holding ID Number: {sb(idn)} sold the {sb(a.purchaseOf)}, (on Title Deed Number: {a.titleDeedNumber || ''}) to the current owner/seller on {sb(fmtLongDate(a.issueDate))} for the sale/purchase price of {sb(thb(a.purchasePrice))} <strong>THB</strong>{i < n - 1 ? ' and;' : '.'}</p>;
      })}
      <p style={ddNote}><strong>Note:</strong> In addition to the private sale and purchase agreement between the parties the Land Office will require the seller and buyer to sign the official government's sale of land and house contract form in Thai only. This is separate from the private sale and purchase agreement containing all the terms and conditions of the sale and purchase.</p>
    </SumBox>;
  },
  landChronology: d => {
    const its = itemsOf(d); const n = its.length;
    const target = (c: any) => c.type === 'upgrade' ? c.upgradedTo : c.type === 'subdivide' ? c.subdividedTo : c.type === 'combine' ? c.combinedTo : '';
    const who = (c: any) => c.ownerAtTheTime === 'Company' ? c.companyName : ddName(c.name);
    return <SumBox title="Land Chronology Summary">
      <p style={ddPara}>Over time, a plot of land may have been upgraded and/or subdivided numerous times and, this plot has been upgraded and/or subdivided {sb(d.count)} time{n === 1 ? '' : 's'}, as shown below:</p>
      {its.map((c, i) => (<p key={i} className="dd-sum-point" style={ddDeedItem}><strong>{LET[i]})</strong> {i === 0
        ? <>the original plot of land ({sb(d.originalType)} - {sb(d.originalNumber)}) was {sb((c.type || '') + 'd')} to {sb(target(c))} - {sb(c.number)}, by {sb(who(c))}, on {sb(fmtLongDate(c.issueDate))}</>
        : <>the land was {sb((c.type || '') + 'd')} to {sb(target(c))} - {sb(c.number)}, by {sb(who(c))} on {sb(fmtLongDate(c.issueDate))}</>}{i < n - 1 ? ' then;' : '.'}</p>))}
      <p style={ddNote}><strong>Note:</strong> for your reference the above information should always be recorded on the rear of the Title Deed.</p>
    </SumBox>;
  },
  liens: d => {
    if (d.liens !== 'Yes') return <SumBox title="Liens & Encumbrances Summary"><p style={ddPara}>The records available from the Land Office and/or the Tessaban<LegalFirmRegion fmt={r => ` in ${r}`} />, indicate that this property is clear of any/all liens and encumbrances.</p></SumBox>;
    const from = d.type === 'mortgage' ? d.lender : ddName(d.lendersName);
    const w = d.type === 'mortgage' ? 'Mortgage' : d.type === 'kai fak' ? 'Kai Fak' : d.type === 'lease' ? 'Lease' : d.type === 'superficies' ? 'Superficies' : '';
    return <SumBox title="Liens & Encumbrances Summary">
      <p style={ddPara}>The records available from the Land Office and/or the Tessaban<LegalFirmRegion fmt={r => ` in ${r}`} />, indicate that this property has a {sb(d.type)} for {sb(thb(d.amount))} <strong>THB</strong> from {sb(from)} for {lienIndef(d) ? <strong>an indefinite term</strong> : <>{sb(d.duration)} months</>} which was registered on {sb(fmtLongDate(d.registrationDate))}.</p>
      <p style={ddNote}><strong>Note:</strong> {d.type === 'kai fak'
        ? <>this <strong>Kai Fak</strong> must be dispersed (cleared) in full prior to, or on the day of, expiry otherwise, the property will be transferred to {ddName(d.lendersName)}.</>
        : <>this <strong>{w}</strong> must be dispersed (cleared) in full prior to, or on the day of, the freehold transfer.</>}</p>
    </SumBox>;
  },
  requirements: d => {
    if (d.gate !== 'Yes') return <SumBox title="Seller Requirement Summary"><p style={ddPara}><LegalFirmName start /> has discovered no obstacles which would prevent the buyer from proceeding with the purchase of the property.</p></SumBox>;
    const its = itemsOf(d, true);
    return <SumBox title="Seller Requirement Summary">
      <p style={ddPara}>The property has {sb(d.count)} requirement(s) to complete. These requirement(s) should not necessarily prevent the buyer from proceeding with the purchase of the property however, they should be completed on or prior to transfer or final payment:</p>
      {sellerReqLines(d)}
      <p style={ddNote}><strong>Note:</strong> the rectification difficulty rating we apply is based on our past experiences and cannot neccessarily be considered a guarantee of future results thus, the rating we apply acts only as a guide for the buyer.</p>
    </SumBox>;
  },
};
function SectionSummary({ sectionKey, d }: { sectionKey: string; d: any }) {
  const fn = SUMM[sectionKey]; if (!fn) return null;
  return <>{fn(d || {})}</>;
}

function GField({ spec, data, set, pathPrefix }: { spec: FSpec; data: any; set: (k: string, v: any) => void; pathPrefix: string }) {
  const readOnly = React.useContext(DDReadOnly);
  // `internal` fields are data-entry helpers only — they drive other values but say nothing the
  // client needs, so they are dropped from the read-only report and the client's public page.
  if (spec.internal && readOnly) return null;
  if (spec.show && !spec.show(data)) return null;
  const v = data[spec.k]; const on = (x: any) => set(spec.k, x);
  const isRo = typeof spec.ro === 'function' ? spec.ro(data) : !!spec.ro;
  const nm = `${pathPrefix}.${spec.k}`;
  const c = { label: spec.label, required: spec.req, cols: spec.cols, name: nm };
  switch (spec.t) {
    case 'text': return <Text {...c} value={v} onChange={on} />;
    case 'num': return <Num {...c} value={v} onChange={on} readOnly={isRo} hint={spec.hint} dp={spec.dp} />;
    case 'sel': return <Sel {...c} options={spec.opts || []} value={v} onChange={on} />;
    case 'date': return <DateF {...c} value={v} onChange={on} readOnly={isRo} hint={spec.hint} />;
    case 'name': return <NameF {...c} value={v} onChange={on} />;
    case 'addr': return <AddressF {...c} value={v} onChange={on} />;
    case 'radio': return <RadioF {...c} options={spec.opts || []} value={v} onChange={on} />;
    case 'textarea': return <TextAreaF {...c} value={v} onChange={on} hint={spec.hint} />;
    case 'rich': return <RichF {...c} value={v} onChange={on} hint={spec.hint} />;
    case 'file': return <UploadF label={spec.label} required={spec.req} cols={spec.cols} path={`${pathPrefix}.${spec.k}`} multi={spec.multi} />;
    default: return null;
  }
}
function renderRows(rows: FSpec[][], data: any, set: (k: string, v: any) => void, pathPrefix: string) {
  return rows.map((row, ri) => {
    const vis = row.filter(f => !f.show || f.show(data));
    if (!vis.length) return null;
    return <Row key={ri}>{vis.map(f => <GField key={f.k} spec={f} data={data} set={set} pathPrefix={pathPrefix} />)}</Row>;
  });
}
function GenericSection({ sectionKey, value, patch, root }: { sectionKey: string; value: any; patch: (p: any) => void; root?: any }) {
  const spec = SECTIONS[sectionKey]; const d = value || {};
  const items: any[] = d.items || [];
  const der = spec.derive || ((o: any) => o);
  const setTop = (k: string, v: any) => { const merged = { ...d, [k]: v }; patch({ [k]: v, ...der(merged, root, k) }); };
  const setItem = (i: number) => (k: string, v: any) => { const arr = items.slice(); while (arr.length <= i) arr.push({}); arr[i] = { ...arr[i], [k]: v, ...der({ ...arr[i], [k]: v }, root, k) }; patch({ items: arr }); };
  // Auto-filled Seller-Requirement wording embeds the property name, which lives on another page.
  // When that name changes later, refresh the still-auto items so the {property} substitution never
  // goes stale (only the operator-owned boxes, furtherDetailsAuto === false, are left alone).
  const propName = root?.property?.name;
  React.useEffect(() => {
    if (sectionKey !== 'requirements') return;
    let changed = false;
    const next = items.map((it: any) => {
      if (!it || it.furtherDetailsAuto === false || !hasReqDetail(it.requirement)) return it;
      const std = reqDetailText(it.requirement, propName);
      if (it.furtherDetails === std) return it;
      changed = true; return { ...it, furtherDetails: std, furtherDetailsAuto: true };
    });
    if (changed) patch({ items: next });
  }, [propName]); // eslint-disable-line react-hooks/exhaustive-deps
  const gate = spec.countGate ? spec.countGate(d) : true;
  const n = spec.instance ? (gate ? (WORDNUM[String(d.count || '').toLowerCase()] || 0) : 0) : 0;
  return (
    <>
      {spec.top && renderRows(spec.top, d, setTop, sectionKey)}
      {spec.instance && Array.from({ length: n }).map((_, i) => (
        <Block key={i} title={spec.instanceTitle ? spec.instanceTitle(i, items[i] || {}) : ''}>
          {renderRows(spec.instance(i), items[i] || {}, setItem(i), `${sectionKey}.${i}`)}
        </Block>
      ))}
      {spec.instance && n === 0 && gate && <div style={hintStyle}>Select a quantity above to add details.</div>}
      {spec.bottom && renderRows(spec.bottom, d, setTop, sectionKey)}
      <SectionSummary sectionKey={sectionKey} d={d} />
    </>
  );
}

// ── Report editor — Cognito replica ──────────────────────────────────────────
// ── OCR auto-fill: build the field schema for a section and merge results ────
type OcrSpec = { path: string; label: string; type?: string; options?: string[] };
function sectionOcrSchema(sectionKey: string): OcrSpec[] {
  const F = (path: string, label: string, type?: string, options?: string[]): OcrSpec => ({ path, label, type, options });
  const YESNO = ['Yes', 'No'];
  if (sectionKey === 'parameters') return [
    F('issueDate', 'Date this Due Diligence Report was issued', 'date'),
    F('buyingPropertyFrom', 'Buying Property From? (the seller is a Company, an Individual, or Both)', undefined, ['Company', 'Individual', 'Both']),
    F('buildingPermit', 'Property Has Building Permit(s)?', undefined, YESNO),
    F('houseBooks', 'Property Has House Book(s)?', undefined, YESNO),
    F('landUpgraded', 'Land Has Been Upgraded or Subdivided?', undefined, YESNO),
    F('purchaseAgreement', 'Property Has "Land Office" Purchase Agreement(s)?', undefined, YESNO),
    F('privateRoad', 'Property Has (or Requires) Private Road Registration(s)?', undefined, YESNO),
  ];
  if (sectionKey === 'buyer') {
    const person = (i: number): OcrSpec[] => [
      F(`individuals.${i}.name.title`, `Buyer ${i + 1} title (Mr/Mrs/Ms/Dr)`), F(`individuals.${i}.name.first`, `Buyer ${i + 1} first/given name`), F(`individuals.${i}.name.last`, `Buyer ${i + 1} last/family name`),
      F(`individuals.${i}.email`, `Buyer ${i + 1} email address`), F(`individuals.${i}.jobId`, `Buyer ${i + 1} Job ID (internal 5-7 character job number)`),
      F(`individuals.${i}.address.line1`, `Buyer ${i + 1} street address (line 1)`), F(`individuals.${i}.address.city`, `Buyer ${i + 1} city/town`), F(`individuals.${i}.address.postal`, `Buyer ${i + 1} postal/zip code`), F(`individuals.${i}.address.country`, `Buyer ${i + 1} country`, undefined, COUNTRIES),
    ];
    const co = (i: number): OcrSpec[] => [
      F(`companies.${i}.companyName`, `Buyer company ${i + 1} name`), F(`companies.${i}.email`, `Buyer company ${i + 1} email`), F(`companies.${i}.jobId`, `Buyer company ${i + 1} Job ID (internal 5-7 character job number)`),
      F(`companies.${i}.address.line1`, `Buyer company ${i + 1} address line 1`), F(`companies.${i}.address.city`, `Buyer company ${i + 1} city`), F(`companies.${i}.address.postal`, `Buyer company ${i + 1} postal code`), F(`companies.${i}.address.country`, `Buyer company ${i + 1} country`, undefined, COUNTRIES),
    ];
    return [
      F('buyerType', 'Buyer type (Individual / Company)', undefined, ENTITY_TYPE), F('numberOfBuyers', 'Number of buyers', undefined, NUM_1_3),
      ...person(0), ...person(1), ...person(2), ...co(0), ...co(1), ...co(2),
    ];
  }
  if (sectionKey === 'seller') {
    const person = (i: number): OcrSpec[] => [
      F(`individuals.${i}.name.title`, `Seller ${i + 1} title`), F(`individuals.${i}.name.first`, `Seller ${i + 1} first name`), F(`individuals.${i}.name.last`, `Seller ${i + 1} last name`),
      F(`individuals.${i}.sellerIs`, `Seller ${i + 1} capacity (Owner / Co-owner / Lessee / Superficies / Owner's Representative)`, undefined, SELLER_IS),
      F(`individuals.${i}.idCard`, `Seller ${i + 1} Thai ID-card or passport number`),
      F(`individuals.${i}.address.line1`, `Seller ${i + 1} address line 1`), F(`individuals.${i}.address.city`, `Seller ${i + 1} city`), F(`individuals.${i}.address.postal`, `Seller ${i + 1} postal code`), F(`individuals.${i}.address.country`, `Seller ${i + 1} country`, undefined, COUNTRIES),
      F(`individuals.${i}.landOrBuildings`, `Seller ${i + 1} — selling Land or Building(s)?`, undefined, LAND_OR_BUILDINGS),
    ];
    const co = (i: number): OcrSpec[] => [
      F(`companies.${i}.companyName`, `Seller company ${i + 1} name`), F(`companies.${i}.taxId`, `Seller company ${i + 1} tax ID (13 digits)`), F(`companies.${i}.registeredCapital`, `Seller company ${i + 1} registered capital (THB)`, 'num'), F(`companies.${i}.yearEstablished`, `Seller company ${i + 1} year established`, undefined, YEAR_ESTABLISHED),
      F(`companies.${i}.address.line1`, `Seller company ${i + 1} address line 1`), F(`companies.${i}.address.city`, `Seller company ${i + 1} city`), F(`companies.${i}.address.postal`, `Seller company ${i + 1} postal code`), F(`companies.${i}.address.country`, `Seller company ${i + 1} country`, undefined, COUNTRIES),
      F(`companies.${i}.numberOfDirectors`, `Seller company ${i + 1} — number of directors`, undefined, NUM_DIRECTORS),
      F(`companies.${i}.signStamp`, `Seller company ${i + 1} — how many directors must sign & stamp to authorise company changes`, undefined, NUM_DIRECTORS),
      F(`companies.${i}.balanceAudit`, `Seller company ${i + 1} — balance sheet & audit status (a year, or "Never (less than one year of trading)")`, undefined, BALANCE_AUDIT),
      F(`companies.${i}.landOrBuildings`, `Seller company ${i + 1} — selling Land or Building(s)?`, undefined, LAND_OR_BUILDINGS),
      F(`companies.${i}.directors.0.title`, `Seller company ${i + 1} director 1 title`), F(`companies.${i}.directors.0.first`, `Seller company ${i + 1} director 1 first name`), F(`companies.${i}.directors.0.last`, `Seller company ${i + 1} director 1 last name`),
      F(`companies.${i}.directors.1.title`, `Seller company ${i + 1} director 2 title`), F(`companies.${i}.directors.1.first`, `Seller company ${i + 1} director 2 first name`), F(`companies.${i}.directors.1.last`, `Seller company ${i + 1} director 2 last name`),
      F(`companies.${i}.directors.2.title`, `Seller company ${i + 1} director 3 title`), F(`companies.${i}.directors.2.first`, `Seller company ${i + 1} director 3 first name`), F(`companies.${i}.directors.2.last`, `Seller company ${i + 1} director 3 last name`),
      F(`companies.${i}.directors.3.title`, `Seller company ${i + 1} director 4 title`), F(`companies.${i}.directors.3.first`, `Seller company ${i + 1} director 4 first name`), F(`companies.${i}.directors.3.last`, `Seller company ${i + 1} director 4 last name`),
    ];
    return [
      F('sellerType', 'Seller type (Individual / Company)', undefined, ENTITY_TYPE), F('numberOfSellers', 'Number of sellers', undefined, NUM_1_2),
      F('sellerIs', "Seller Is? (Owner, Co-owner, Lessee, Superficies, or the Owner's Representative)", undefined, SELLER_IS),
      // Up to six individual sellers: "Number of Sellers" caps at two, but operators can add further
      // co-owners by hand, and auto-extraction has to reach them too.
      ...person(0), ...person(1), ...person(2), ...person(3), ...person(4), ...person(5), ...co(0), ...co(1),
    ];
  }
  if (sectionKey === 'property') return [
    F('name', 'Property name'),
    F('address.line1', 'Property address line 1'), F('address.city', 'Property city'), F('address.postal', 'Property postal code'),   // no country — the property is always in Thailand
    F('landAndorBuildings', 'Land and/or Building(s) — what is being purchased', undefined, LAND_ANDOR_BUILDINGS),
    F('zoning', 'Zoning', undefined, ZONING), F('suitableFor', 'Suitable For (what the zoning permits the land to be used for)', undefined, SUITABLE_FOR),
    F('maximumUsage', 'Maximum Usage (percentage)', undefined, MAX_USAGE),
    F('landElevation', 'Land Elevation APPROX (zone)', undefined, LAND_ELEVATION), F('slope', 'Slope (in Degrees) APPROX', undefined, SLOPE),
    ...landAreaUnits().map(u => F(u.key, `Land size — ${u.label}${u.hint ? ` (${u.hint})` : ''}`, 'num')), F('m2', 'Land size — m² (square metres, total)', 'num'),
    F('sameSize', "Same Size as Buyer's Plot?", undefined, YES_NO), F('m2Buyer', "m² of the Buyer's plot (only when not the same size)", 'num'),
  ];
  // Additional Comments: the opening statement is computed, so only the free-text Remarks import.
  if (sectionKey === 'comments') return [
    F('remarks', 'Additional Comments — Remarks (any further remarks, observations, or notes text)'),
  ];
  const spec = SECTIONS[sectionKey]; if (!spec) return [];
  const out: OcrSpec[] = [];
  const add = (rows: FSpec[][], prefix: string, suffix = '') => rows.forEach(row => row.forEach(f => {
    if (f.t === 'file') return;
    if (f.t === 'name') { out.push(F(`${prefix}${f.k}.title`, `${f.label}${suffix} title`)); out.push(F(`${prefix}${f.k}.first`, `${f.label}${suffix} first name`)); out.push(F(`${prefix}${f.k}.last`, `${f.label}${suffix} last name`)); }
    else if (f.t === 'addr') { (['line1', 'city', 'postal'] as const).forEach(p => out.push(F(`${prefix}${f.k}.${p}`, `${f.label}${suffix} ${p}`))); out.push(F(`${prefix}${f.k}.country`, `${f.label}${suffix} country`, undefined, COUNTRIES)); }
    else out.push(F(`${prefix}${f.k}`, `${f.label}${suffix}`, f.t, f.opts));
  }));
  if (spec.top) add(spec.top, '');
  // Three instances, each label tagged with its ordinal, so a repeated block (road 2, requirement 3 …)
  // maps to ITS OWN fields instead of only the first block ever being importable.
  const cfLen = ((spec.top || []).flat().find(f => f.k === 'count') as any)?.opts?.length || 3;
  if (spec.instance) for (let i = 0; i < Math.max(3, cfLen); i++) add(spec.instance(i), `items.${i}.`, ` (${spec.itemLabel || 'item'} ${i + 1})`);
  return out;
}
function setPath(obj: any, path: string, val: any) {
  const parts = path.split('.'); let cur = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const k = parts[i]; const nextIdx = /^\d+$/.test(parts[i + 1]);
    if (cur[k] == null || typeof cur[k] !== 'object') cur[k] = nextIdx ? [] : {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = val;
}
function getPath(obj: any, path: string): any {
  return path.split('.').reduce((cur: any, k: string) => (cur == null ? undefined : cur[k]), obj);
}
// After filling a section, make sure the instance/owner it belongs to actually renders (the count +
// type gates). Shared by the per-section OCR and the whole-report source-document fill.
function ensureCountsTypes(sectionKey: string, obj: any): any {
  const anyFilled = (o: any) => o && Object.values(o).some(x => x && typeof x === 'object' ? Object.values(x).some(Boolean) : Boolean(x));
  if (sectionKey === 'buyer' || sectionKey === 'seller') {
    const indF = anyFilled((obj.individuals || [])[0]); const coF = anyFilled((obj.companies || [])[0]);
    const tKey = sectionKey === 'buyer' ? 'buyerType' : 'sellerType'; const nKey = sectionKey === 'buyer' ? 'numberOfBuyers' : 'numberOfSellers';
    if (!obj[tKey] && (indF || coF)) obj[tKey] = coF && !indF ? 'Company' : 'Individual';
    if (!obj[nKey] && (indF || coF)) obj[nKey] = 'One';
  } else if (SECTIONS[sectionKey]?.instance && anyFilled((obj.items || [])[0]) && !obj.count) {
    const cf = (SECTIONS[sectionKey].top || []).flat().find(f => f.k === 'count');
    if (cf?.opts?.length) obj.count = cf.opts[0];
  }
  return obj;
}
function applyOcr(sectionKey: string, base: any, values: Record<string, string>): any {
  const obj = JSON.parse(JSON.stringify(base || {}));
  Object.entries(values).forEach(([p, v]) => { if (v != null && v !== '') setPath(obj, p, v); });
  return ensureCountsTypes(sectionKey, obj);
}

// ── Source-document extraction (#31): one internal upload on page 1 fills the whole report ──────────
// Each document type targets only the sections it can realistically speak to, so a big misattributing
// schema is never sent. sectionOcrSchema paths are section-local; we prefix them here.
const DDOC_TYPES: { key: string; label: string; sections: string[] }[] = [
  { key: 'reservation', label: 'Reservation Agreement', sections: ['buyer', 'property', 'purchaseAgreement'] },
  { key: 'spa', label: 'Sale & Purchase Agreement', sections: ['buyer', 'seller', 'property', 'purchaseAgreement'] },
  { key: 'lease', label: 'Lease Agreement', sections: ['buyer', 'seller', 'property', 'liens'] },
  { key: 'titledeed', label: 'Title Deed / Chanote', sections: ['property', 'titledeed', 'landChronology'] },
  // ID / passport is ONE person, so the operator says whose — it must not be written to both parties.
  { key: 'buyer_id', label: 'Buyer ID / Passport', sections: ['buyer'] },
  { key: 'seller_id', label: 'Seller ID / Passport', sections: ['seller'] },
];
const ddocLabel = (key: string) => DDOC_TYPES.find(d => d.key === key)?.label || key;
// Combined, section-prefixed schema for the given sections (used per uploaded document).
function ocrSchemaForSections(sections: string[]): OcrSpec[] {
  const out: OcrSpec[] = [];
  sections.forEach(k => sectionOcrSchema(k).forEach(f => out.push({ ...f, path: `${k}.${f.path}`, label: `${ALL_SECTIONS.find(s => s.key === k)?.tab || k}: ${f.label}` })));
  return out;
}
// Fill the whole report from extracted {sectionKey.path: value}. FILL-EMPTY-ONLY: never overwrites a
// value the operator already entered; any real disagreement is returned as a conflict for review.
// A dropdown/option field may ONLY receive one of its exact option strings. An extracted near-miss
// ("Yellow" for a zoning code, "USA" for a country, "2015" for "2015 (2558)") is dropped, not stored
// — otherwise it would show as a blank <select> yet still print verbatim on the client report.
function coerceEnum(fullPath: string, v: string): string | null {
  const opts = DDOC_OPTIONS[fullPath];
  if (!opts || !opts.length) return v;
  const t = v.trim();
  return opts.find(o => o === t) || opts.find(o => o.toLowerCase() === t.toLowerCase()) || null;
}
function applyWholeReport(data: any, values: Record<string, string>, overwrite = false): { data: any; applied: { path: string; value: string }[]; conflicts: { path: string; current: string; incoming: string }[]; dropped: number } {
  const next = JSON.parse(JSON.stringify(data || {}));
  const applied: { path: string; value: string }[] = [];
  const conflicts: { path: string; current: string; incoming: string }[] = [];
  const touched = new Set<string>(); let dropped = 0;
  Object.entries(values).forEach(([full, raw]) => {
    if (raw == null || String(raw).trim() === '') return;
    const dot = full.indexOf('.'); if (dot < 0) return;
    const coerced = coerceEnum(full, String(raw));
    if (coerced == null) { dropped++; return; }   // enum value not in the option set — never store it
    const v = coerced;
    const section = full.slice(0, dot); const path = full.slice(dot + 1);
    if (!next[section] || typeof next[section] !== 'object') next[section] = {};
    const cur = getPath(next[section], path);
    const empty = cur == null || String(cur).trim() === '';
    if (empty || overwrite) { if (!empty && String(cur).trim() === String(v).trim()) return; setPath(next[section], path, v); applied.push({ path: full, value: v }); touched.add(section); }
    else if (String(cur).trim() !== String(v).trim()) { conflicts.push({ path: full, current: String(cur), incoming: v }); }
  });
  touched.forEach(section => { next[section] = ensureCountsTypes(section, next[section]); });
  return { data: next, applied, conflicts, dropped };
}

// Additional Comments = an opening paragraph, then the Seller Requirements summary auto-copied from
// that page, then the operator's remarks. Two editable boxes with the summary rendered read-only
// between them (editor), and the same three parts composed in order (client report). The summary is
// never typed by hand, so it can never drift from the Seller Requirements page.
// The fixed opening statement. Not editable — it names the property automatically and colour-codes
// its two cross-references so they line up with the blue "Seller Requirements:" / orange "Remarks:"
// headings below.
function commentsStatement(root: any): React.ReactNode {
  const propName = String(root?.property?.name || '').trim();
  return (
    <p style={ddPara}>
      Other than the aforementioned "<span style={{ color: CMT_BLUE, fontWeight: 700 }}>Seller Requirements</span>" described in this report, as well as any/all further "<span style={{ color: CMT_ORANGE, fontWeight: 700 }}>Remarks</span>" below, <LegalFirmName plain /> could not find any other obstacles, available on public record, for the buyer/lessee to purchase and/or lease{propName ? <> this specific property being; <strong>{propName}</strong></> : <> this specific property</>}.
    </p>
  );
}
function CommentsSection({ value, patch, root }: { value: any; patch: (p: any) => void; root: any }) {
  const ro = React.useContext(DDReadOnly);
  const d = value || {};
  const reqData = root?.requirements || {};
  const hasReqs = reqData.gate === 'Yes' && itemsOf(reqData, true).length > 0;
  const set = (k: string) => (v: string) => patch({ [k]: v });

  // Seed the remarks box once, on first open (never over existing text). The opening is a computed
  // statement now, so nothing is seeded for it.
  React.useEffect(() => {
    if (ro) return;
    if (!String(d.remarks || '').trim()) patch({ remarks: COMMENTS_REMARKS });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sellerHead = <p style={{ ...ddPara, color: CMT_BLUE, fontWeight: 700, margin: '20px 0 8px' }}>Seller Requirements:</p>;
  const remarksHead = <p style={{ ...ddPara, color: CMT_ORANGE, fontWeight: 700, margin: '20px 0 8px' }}>Remarks:</p>;
  const summaryLines = hasReqs
    ? sellerReqLines(reqData)
    : <p style={ddNote}><LegalFirmName start /> has discovered no obstacles which would prevent the buyer from proceeding with the purchase of the property.</p>;

  if (ro) {
    // Match the rest of the report exactly: 15px / 1.75, report font. Only the remarks HTML needs
    // the rte-render list styling, so it gets the report-sized (rte-lg) variant.
    return (
      <div style={{ fontFamily: RC, fontSize: 15, color: INK, lineHeight: 1.75 }}>
        {commentsStatement(root)}
        {sellerHead}
        {summaryLines}
        {String(d.remarks || '').trim() && <>{remarksHead}<div className="rte-render rte-lg" style={{ marginLeft: 45 }} dangerouslySetInnerHTML={{ __html: commentToHtml(String(d.remarks || '')) }} /></>}
      </div>
    );
  }
  return (
    <>
      <div style={{ margin: '0 0 18px', padding: '12px 16px', background: '#f6f9fc', border: '1px dashed #cdddee', borderRadius: 8, fontFamily: RC, fontSize: 15, color: INK, lineHeight: 1.6 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7c8d', marginBottom: 6 }}>Opening statement + Seller Requirements summary — fixed wording, inserted automatically (not editable)</div>
        {commentsStatement(root)}
        {sellerHead}
        <DDReadOnly.Provider value={true}>{summaryLines}</DDReadOnly.Provider>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: CMT_ORANGE, fontFamily: RC, margin: '0 0 8px' }}>Remarks:</div>
      <Row><RichF label="" name="comments.remarks" value={d.remarks} onChange={set('remarks')}
        hint="Any further remarks — shown under an orange “Remarks:” heading in the report. Leave the default if there are none." /></Row>
    </>
  );
}

// ── Read-only full-report view — reuses every section component in read-only
// mode. Used by the in-app Preview and by the public client link. ────────────
function DDView({ data, reportId, publicToken }: { data: any; reportId?: string; publicToken?: string }) {
  const params = data.parameters || {};
  const visible = ALL_SECTIONS.filter(s => !s.cond || s.cond(params));
  const noop = () => { /* read-only */ };
  // One continuous webpage, but PDF-style (user 2026-07-28): every section renders as its own
  // white "sheet" on a grey backdrop, stacked vertically — scroll through them like a PDF viewer.
  // The pill bar is a jump-to navigation (smooth scroll), not a pager.
  const jump = (i: number) => document.getElementById(`dd-page-${i}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // "Return to Menu" floating button (Calem 2026-07-28): shows once the reader scrolls away from
  // the pill menu (or jumped via a pill) and snaps back to the top. Hidden on print via .dd-pager.
  const [showMenuBtn, setShowMenuBtn] = useState(false);
  // Which section the reader is IN right now (drives the Previous/Next labels): the last section
  // whose top has crossed the upper third of the viewport. ≤21 sections, so a plain loop per
  // scroll event is cheap.
  const [cur, setCur] = useState(0);
  // The report scrolls on the WINDOW on the public client page, but inside an overflow:auto overlay
  // in the staff preview — walk up from the report root to whichever ancestor actually scrolls, and
  // listen there. Without this the floating buttons never appear in the preview at all.
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const pillsRef = React.useRef<HTMLDivElement | null>(null);
  const scrollerRef = React.useRef<HTMLElement | null>(null);   // null = window
  useEffect(() => {
    let el = rootRef.current ? rootRef.current.parentElement : null;
    let found: HTMLElement | null = null;
    while (el) {
      const st = getComputedStyle(el);
      if (/(auto|scroll)/.test(st.overflowY) && el.scrollHeight > el.clientHeight + 10) { found = el; break; }
      el = el.parentElement;
    }
    scrollerRef.current = found;
    const target: any = found || window;
    const on = () => {
      const scrolled = found ? found.scrollTop : window.scrollY;
      // Hide the hopper ONLY while the pill menu itself is on screen. The old fixed 300px gate
      // swallowed the whole button bar when "Previous" landed on the first section (its top can sit
      // inside 300px), which read as "backwards navigation stopped working" (Calem 2026-08-05).
      const pills = pillsRef.current ? pillsRef.current.getBoundingClientRect() : null;
      const viewTop = found ? found.getBoundingClientRect().top : 0;
      setShowMenuBtn(pills ? pills.bottom < viewTop + 8 : scrolled > 300);
      const base = found ? found.getBoundingClientRect().top : 0;
      // Current section = the one CONTAINING the reading line (a FIXED 90px below the top, past the
      // preview's sticky toolbar). The old rule — "last section whose top crossed 35% of the
      // viewport" — depended on screen height: on tall monitors a SHORT section (Liens) fit entirely
      // above the line, so "Previous" kept resolving to the section below it and upward navigation
      // jammed (Calem 2026-08-07). A fixed offset behaves identically on every machine.
      const line = base + 90;
      let c = 0;
      for (let i = 0; i < visible.length; i++) {
        const sec = document.getElementById(`dd-page-${i}`);
        if (!sec) continue;
        const r = sec.getBoundingClientRect();
        if (r.top <= line) c = i;                       // fallback: last one whose top crossed
        if (r.top <= line && r.bottom > line) { c = i; break; }   // the one containing the line wins
      }
      setCur(c);
    };
    target.addEventListener('scroll', on, { passive: true });
    on();
    return () => target.removeEventListener('scroll', on);
  }, [visible.length]);   // eslint-disable-line
  const pill: React.CSSProperties = {
    padding: '6px 12px', borderRadius: 999, border: '1px solid #d5dde5', background: '#fff',
    color: INK, fontSize: 12.5, fontWeight: 700, fontFamily: RC, cursor: 'pointer', whiteSpace: 'nowrap',
  };
  return (
    <DDReadOnly.Provider value={true}>
      <DDContext.Provider value={{ reportId: reportId || '', publicToken }}>
        <div className="dd-client-report" ref={rootRef}>
        <div className="dd-pager" ref={pillsRef} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '14px 4px' }}>
          {visible.map((s, i) => (
            <button key={s.key} onClick={() => jump(i)} style={pill}>{i + 1}. {s.tab}</button>
          ))}
        </div>
        {showMenuBtn && (() => {
          // Section-hopper (Calem 2026-08-05): Previous/Next carry the NEIGHBOUR section's name, so
          // the reader always knows where the jump lands; first/last section drops the dead arrow.
          const fab: React.CSSProperties = { padding: '11px 18px', borderRadius: 999, border: 'none', background: BLUE, color: '#fff', fontWeight: 700, fontFamily: RC, fontSize: 13.5, boxShadow: '0 4px 14px rgba(20,40,70,.35)', cursor: 'pointer', whiteSpace: 'nowrap' };
          return (
            <div className="dd-pager" style={{ position: 'fixed', right: 22, bottom: 26, zIndex: 60, display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 8, maxWidth: 'calc(100vw - 44px)' }}>
              {cur > 0 && <button onClick={() => jump(cur - 1)} style={fab}>↑ {visible[cur - 1].tab}</button>}
              {cur < visible.length - 1 && <button onClick={() => jump(cur + 1)} style={fab}>↓ {visible[cur + 1].tab}</button>}
              <button onClick={() => (scrollerRef.current || window).scrollTo({ top: 0, behavior: 'smooth' })} style={fab}>↑ Main Menu</button>
            </div>
          );
        })()}
        {visible.map((s, i) => (
          <div key={s.key} id={`dd-page-${i}`} className="dd-section">
            <SubHead>{s.title}</SubHead>
            {s.key === 'parameters' ? <ParametersSection data={data} set={noop} />
              : s.key === 'buyer' ? <BuyerSection buyer={data.buyer || {}} patch={noop} />
              : s.key === 'seller' ? <SellerSection seller={data.seller || {}} patch={noop} />
              : s.key === 'property' ? <PropertySection property={data.property || {}} patch={noop} />
              : s.key === 'comments' ? <CommentsSection value={data.comments || {}} patch={noop} root={data} />
              : <GenericSection sectionKey={s.key} value={data[s.key] || {}} patch={noop} root={data} />}
            <Divider />
            <div className="dd-sheet-no" style={{ textAlign: 'center', fontSize: 11.5, color: '#9aa6b2', fontFamily: RC, marginTop: 18 }}>— {i + 1} / {visible.length} —</div>
          </div>
        ))}
        </div>
      </DDContext.Provider>
    </DDReadOnly.Provider>
  );
}

// ── Public client page (/dd/view/:token) — no login required ─────────────────
export function DDPublicPage() {
  const { token } = useParams();
  const [report, setReport] = useState<any>(null);
  const [state, setState] = useState<'load' | 'ok' | 'err'>('load');
  useEffect(() => {
    if (!token) { setState('err'); return; }
    // The firm's name / region (sentences of the report) are fetched alongside, so the first paint has them.
    Promise.all([api.ddPublicGet(token), loadFirmContacts()])
      .then(([r]: any[]) => { setCountryFeaturesOverride(r?.countryFeatures); setDdContentOverride(r?.ddContent || { zoning: { mapImage: '', paragraphs: [] }, services: [] }); setReport(r); setState('ok'); })
      .catch(() => setState('err'));
    return () => setDdContentOverride(null);
  }, [token]);
  const statusLabel = report ? ({ new: 'New', pending: 'Pending', confirmed: 'Confirmed', sent: 'Sent to Client' } as any)[report.status] || '' : '';
  // Title = the linked Job's Specific Job (falls back to the generic name).
  const title = (report?.jobName || '').trim() || 'Due Diligence Report';
  useEffect(() => { if (report) document.title = title; }, [report, title]);
  return (
    // The three wrappers carry print hooks (dd-pp-*): their look is set INLINE, which no stylesheet
    // can override without them — which is exactly why printing used to paint the dark page surround
    // as thick grey bars down both edges of every sheet. See @media print in global.css.
    <div className="dd-pp-root" style={{ minHeight: '100vh', background: DARK, padding: 'clamp(8px, 3vw, 30px) clamp(6px, 3vw, 16px)', fontFamily: RC, overflowX: 'hidden', boxSizing: 'border-box' }}>
      <div className="dd-pp-card" style={{ maxWidth: CARD, width: '100%', margin: '0 auto', background: '#fff', boxShadow: '0 2px 20px rgba(0,0,0,.3)', boxSizing: 'border-box' }}>
        <div className="dd-pp-head" style={{ background: BLUE, color: '#fff', padding: 'clamp(18px, 5vw, 30px) clamp(16px, 5vw, 40px)' }}>
          <div style={{ fontSize: 'clamp(20px, 5vw, 30px)', fontWeight: 400, lineHeight: 1.2, overflowWrap: 'anywhere' }}>{title}</div>
          {report && <div style={{ fontSize: 14, opacity: .92, marginTop: 5 }}>Due Diligence Report · {report.report_no}{statusLabel ? ` · ${statusLabel}` : ''}</div>}
        </div>
        {/* No body gutter on the report itself — the dark .dd-client-report block runs edge-to-edge
            under the blue header, PDF-viewer style; sheets get their width from it. */}
        <div className="dd-pp-body" style={{ padding: 0 }}>
          {state === 'load' && <div style={{ textAlign: 'center', color: '#999', padding: '60px 0', fontFamily: RC }}><i className="ti ti-loader" style={{ fontSize: 26 }} /></div>}
          {state === 'err' && <div style={{ textAlign: 'center', color: '#999', padding: '60px 0', fontFamily: RC }}><i className="ti ti-link-off" style={{ fontSize: 28, display: 'block', marginBottom: 10 }} />This link is invalid or the report is no longer available.</div>}
          {state === 'ok' && report && <DDView data={report.data || {}} publicToken={token} />}
        </div>
      </div>
    </div>
  );
}

// Human labels for every extractable path, so a conflict can be shown as "Seller: Tax ID" not
// "seller.companies.0.taxId".
// Uploaded documents can't speak to the Parameters Yes/No gates, but a full OLD-report import can —
// so the Cognito import schema INCLUDES parameters while the source-document schema does not.
const DDOC_DATA_SECTIONS = ['buyer', 'seller', 'property', 'site', 'buildingPermit', 'houseBook', 'titledeed', 'access', 'purchaseAgreement', 'landChronology', 'liens'];
const DDOC_IMPORT_SECTIONS = ['parameters', ...DDOC_DATA_SECTIONS, 'requirements', 'comments'];
const DDOC_LABELS: Record<string, string> = Object.fromEntries(ocrSchemaForSections(DDOC_IMPORT_SECTIONS).map(f => [f.path, f.label]));
// Full-path → allowed option strings, so extracted enum values can be validated (see coerceEnum).
const DDOC_OPTIONS: Record<string, string[]> = Object.fromEntries(ocrSchemaForSections(DDOC_IMPORT_SECTIONS).filter(f => f.options && f.options.length).map(f => [f.path, f.options as string[]]));

// Where each kind of imported ATTACHMENT belongs, so an old-system import drops every file straight
// into the right upload widget instead of one pile. `path` = a single/stacking field; `slots` = fixed
// numbered slots filled in order; `pathTemplate`+`countSection` = a repeated block (index substituted
// for {i}, and the section's instance count is bumped up so the block renders). The server classifies
// each file into one of these `kind`s (or leaves it in the internal Source Documents archive). Only
// always-visible targets are listed — conditionally-shown fields (liens agreements, seller ID, which
// depend on other choices) are intentionally left to the archive to avoid a filed-but-hidden file.
// `gateParam` / `gateValueAny` guard targets whose upload widget only renders under some condition, so a
// file is never filed where it would be invisible: a target is used only when the imported DATA already
// satisfies the gate (the section's parameter is 'Yes', or a required value mapped) — otherwise the file
// stays in the always-visible Source Documents archive. This keys visibility to real data, never flipping
// a report parameter on the strength of one possibly-misclassified file.
// maxIndex = how many instance blocks the section's count field can offer (its option-list length); files
// past that overflow to the archive rather than being filed into a block that can never render.
// `match` = regex tested (case-insensitive) against the OLD form's own field label for the attachment
// (breadcrumbed, e.g. "Title Deeds › Title Deed 1"). A match places the file DETERMINISTICALLY — the old
// system tells us which box it was in, and a trailing number in the label picks the exact instance/slot.
// The AI `label` description is only the fallback for files the old form doesn't identify.
const DDOC_UPLOAD_TARGETS: any[] = [
  { kind: 'title_deed', match: '›\\s*Title Deed\\s*\\d*$', label: 'Land title deed / Chanote / Nor Sor Sam / Sor Kor 1 (the land-ownership document, often named "Chanote" or by a deed number)', pathTemplate: 'titledeed.{i}.titleDeed', countSection: 'titledeed', maxIndex: 7 },
  { kind: 'building_permit', match: '›\\s*Building Permit\\s*\\d*$', label: 'Building / construction permit', pathTemplate: 'buildingPermit.{i}.buildingPermit', countSection: 'buildingPermit', gateParam: 'buildingPermit', maxIndex: 3 },
  { kind: 'house_book', match: '›\\s*House Book\\s*\\d*$', label: 'House registration book (Tabien Baan — the blue or yellow house book)', pathTemplate: 'houseBook.{i}.houseBook', countSection: 'houseBook', gateParam: 'houseBooks', maxIndex: 6 },
  { kind: 'purchase_agreement', match: '›\\s*Purchase Agreement \\(Land Office\\)\\s*\\d*$', label: 'Land Office sale/purchase agreement (Tor Dor 13 / Sor Kor)', pathTemplate: 'purchaseAgreement.{i}.purchaseAgreement', countSection: 'purchaseAgreement', gateParam: 'purchaseAgreement', maxIndex: 3 },
  // Sellers can be TWO individuals or TWO companies — each person's documents must land in THEIR OWN
  // block. Instance comes from the old label's number or, failing that, from which old section the file
  // sat in (ref grouping server-side) — never piled into block 0.
  { kind: 'company_docs', match: '›\\s*Company Documents\\s*\\d*$', label: 'Seller company documents — DBD certificate, company affidavit, registration, shareholder list, VAT/PP20', pathTemplate: 'seller.co.{i}.docs', maxIndex: 2, gateSellerCompany: true },
  // Old-form lien agreement uploads land in their own slot (owner 2026-09-05): any file — PDF or
  // photo — attached under the old "Superficies Agreement" field belongs to liens.superficiesAgreement.
  { kind: 'superficies_agreement', match: 'Superficies', label: 'Registered superficies agreement / contract document (สิทธิเหนือพื้นดิน)', path: 'liens.superficiesAgreement', gateEffEquals: { path: 'liens.type', value: 'superficies' } },
  { kind: 'seller_id', match: 'Seller Details Individual\\s*\\d*\\s*›\\s*ID Card', label: "An individual SELLER's identity document or portrait — Thai ID card, passport, or a photo of the seller person (named after a person, not a company)", pathTemplate: 'seller.ind.{i}.idcard', maxIndex: 6, gateSellerIndividual: true },
  { kind: 'site_photo', match: '›\\s*Site Photo\\s*\\d+$', label: 'Photograph of the actual land, house, or site', slots: ['site.sitePhoto1', 'site.sitePhoto2', 'site.sitePhoto3', 'site.sitePhoto4', 'site.sitePhoto5', 'site.sitePhoto6'] },
  { kind: 'property_map', match: '›\\s*Property Map\\s*$', label: 'Property location map, cadastral map, or colour land-use / zoning map', path: 'property.map' },
  { kind: 'road_map', match: '›\\s*Road Map\\s*$', label: 'Access road map — the route from the main government road to the property', path: 'access.roadMap' },
  // Deterministic-only targets (no AI fallback description would be safe for these — they exist because
  // the OLD form labels them explicitly).
  { kind: 'access_doc', match: '›\\s*Access\\s*\\d+$', label: 'Access road title deed / servitude document for a specific access road', pathTemplate: 'access.{i}.access', countSection: 'access', maxIndex: 12, gateEffAny: ['access.items.{i}.deedType'] },
  { kind: 'private_road_reg', match: '›\\s*Private Road Registration\\s*\\d*$', label: 'Private road registration document', pathTemplate: 'access.{i}.privateRoadRegistration', countSection: 'access', maxIndex: 12, gateEffAny: ['access.items.{i}.deedType'] },
  { kind: 'public_road_reg', match: '›\\s*Public Road Registration\\s*\\d*$', label: 'Public road registration document', pathTemplate: 'access.{i}.publicRoadRegistration', countSection: 'access', maxIndex: 12, gateEffAny: ['access.items.{i}.startGovernmentRoad'] },
  { kind: 'land_chronology_doc', match: 'Upgrade/Subdivide/Combine\\s*\\d+$', label: 'Land upgrade / subdivision / combination document', slots: ['landChronology.doc1', 'landChronology.doc2', 'landChronology.doc3', 'landChronology.doc4', 'landChronology.doc5'], gateParam: 'landUpgraded', gateCountAtLeast: 'landChronology' },
];

// #31 — one internal upload that auto-fills the whole report. Sits at the TOP of page 1, never shown
// to the client. Each file is tagged with its document type (so only the relevant sections are asked
// for), archived to dd_files under `source.<type>`, then read; values FILL-EMPTY-ONLY across the
// report and any disagreement with what's already there is surfaced for the operator to check.
function SourceDocsPanel({ reportId, onApply, onUploaded }: { reportId: string; onApply: (values: Record<string, string>) => { applied: number; conflicts: { path: string; current: string; incoming: string }[]; dropped: number }; onUploaded: () => void }) {
  const [items, setItems] = useState<{ file: File; type: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [result, setResult] = useState<{ applied: number; dropped: number; conflicts: { path: string; current: string; incoming: string }[]; crossDoc: { path: string; a: string; b: string }[] } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const addList = (chosen: File[]) => {
    const ok = chosen.filter(f => f.size <= 10 * 1024 * 1024).map(f => ({ file: f, type: 'spa' }));
    if (ok.length < chosen.length) setMsg('Some files exceeded 10 MB and were skipped.');
    setItems(prev => [...prev, ...ok]); setResult(null);
  };
  const addFiles = (list: FileList | null) => { if (list) addList(Array.from(list)); };
  // Paste a screenshot (⌘/Ctrl+V) anywhere on page 1 — same as the POA intake. Text pastes into
  // inputs are untouched; only real file/image clipboard items are captured.
  const busyRef = useRef(busy); busyRef.current = busy;
  React.useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (busyRef.current) return;
      const its = e.clipboardData?.items; if (!its) return;
      const files: File[] = [];
      for (let i = 0; i < its.length; i++) {
        const it = its[i];
        if (it.kind !== 'file') continue;
        const f = it.getAsFile(); if (!f) continue;
        if (!(f.type.startsWith('image/') || f.type === 'application/pdf')) continue;
        const named = (!f.name || f.name === 'image.png') ? new File([f], `screenshot-${new Date().toISOString().slice(0, 19).replace(/[T:]/g, '-')}.png`, { type: f.type }) : f;
        files.push(named);
      }
      if (files.length) { e.preventDefault(); addList(files); }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async () => {
    if (!items.length || busy) return;
    setBusy(true); setMsg('Reading document(s) — this can take a minute…'); setResult(null);
    try {
      const merged: Record<string, string> = {};
      const crossDoc: { path: string; a: string; b: string }[] = [];
      for (const it of items) {
        const doc = DDOC_TYPES.find(d => d.key === it.type) || DDOC_TYPES[1];
        try { await api.ddUpload(reportId, `source.${it.type}`, it.file); } catch { /* archival is best-effort */ }
        const r: any = await api.ddOcrSection(reportId, it.file, ocrSchemaForSections(doc.sections));
        const vals = r.values || {};
        Object.entries(vals).forEach(([p, v]) => {
          const s = v == null ? '' : String(v).trim();
          if (!s) return;
          if (!merged[p]) merged[p] = s;
          else if (merged[p].toLowerCase() !== s.toLowerCase()) crossDoc.push({ path: p, a: merged[p], b: s });   // two documents disagree
        });
      }
      // Apply against the LIVE report state (not a stale snapshot captured at click time).
      const res = onApply(merged); onUploaded();
      setResult({ applied: res.applied, dropped: res.dropped, conflicts: res.conflicts, crossDoc });
      setItems([]); setMsg('');
    } catch (e: any) { setMsg(e?.message || 'Extraction failed.'); }
    finally { setBusy(false); }
  };
  const sel: React.CSSProperties = { padding: '5px 8px', border: '1px solid #d6d6d6', borderRadius: 6, fontSize: 12.5, fontFamily: RC, background: '#fff' };
  return (
    <div
      onDragOver={e => { e.preventDefault(); if (!drag) setDrag(true); }}
      onDragLeave={e => { if (e.currentTarget === e.target) setDrag(false); }}
      onDrop={e => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer?.files || null); }}
      style={{ margin: '0 0 24px', padding: '16px 18px', background: drag ? '#eaf4ff' : '#f4f8fc', border: drag ? '2px dashed #2a80ce' : '1px dashed #b9d3ec', borderRadius: 10, fontFamily: RC, transition: 'background .15s, border-color .15s' }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: BLUE, display: 'flex', alignItems: 'center', gap: 7 }}>
        <i className="ti ti-file-upload" />Source Documents — auto-fill the report <span style={{ fontSize: 11.5, fontWeight: 700, color: '#8a97a4', background: '#e6edf4', padding: '1px 8px', borderRadius: 20 }}>INTERNAL</span>
      </div>
      <div style={{ fontSize: 13, color: '#5b6b79', margin: '6px 0 12px', lineHeight: 1.55 }}>
        Upload a <strong>PDF or image</strong>, <strong>drag files</strong> onto this box, or <strong>paste a screenshot</strong> (Ctrl / ⌘+V) — tag each one — and we’ll read them and fill in the matching fields across every page. Existing entries are never overwritten; anything that disagrees is flagged for you to check. Not shown to the client.
      </div>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
          <i className="ti ti-file-text" style={{ color: '#6b7c8d' }} />
          <span style={{ fontSize: 13, color: INK, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.file.name}</span>
          <select value={it.type} onChange={e => setItems(prev => prev.map((x, j) => j === i ? { ...x, type: e.target.value } : x))} style={sel} disabled={busy}>
            {DDOC_TYPES.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
          <button type="button" onClick={() => setItems(prev => prev.filter((_, j) => j !== i))} disabled={busy} style={{ border: 'none', background: 'none', color: '#B42318', cursor: 'pointer', fontSize: 15 }}><i className="ti ti-x" /></button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
        <input ref={inputRef} type="file" multiple accept="image/*,application/pdf" style={{ display: 'none' }} onChange={e => { addFiles(e.target.files); if (e.target) e.target.value = ''; }} />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} style={toolBtn}><i className="ti ti-plus" style={{ fontSize: 15 }} />Add document</button>
        {items.length > 0 && (
          <button type="button" onClick={run} disabled={busy} style={{ ...toolBtn, background: BLUE, color: '#fff', border: 'none', opacity: busy ? 0.6 : 1 }}>
            <i className={`ti ${busy ? 'ti-loader-2' : 'ti-sparkles'}`} style={{ fontSize: 15 }} />{busy ? 'Reading…' : `Upload & auto-fill (${items.length})`}</button>
        )}
      </div>
      {msg && <div style={{ fontSize: 13, color: busy ? '#555' : '#B42318', marginTop: 10 }}>{msg}</div>}
      {result && (
        <div style={{ marginTop: 12, fontSize: 13, color: INK }}>
          <div style={{ fontWeight: 700, color: '#15803D' }}><i className="ti ti-check" style={{ marginRight: 5 }} />Filled {result.applied} field(s). Please review each page.</div>
          {result.dropped > 0 && <div style={{ marginTop: 4, color: '#8a97a4' }}>{result.dropped} extracted value(s) didn’t match a valid dropdown option and were skipped for you to enter.</div>}
          {result.conflicts.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 700, color: '#B45309' }}>{result.conflicts.length} value(s) differed from what you’d already entered — left unchanged for you to check:</div>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                {result.conflicts.slice(0, 12).map((c, i) => (
                  <li key={i} style={{ marginBottom: 3 }}><strong>{DDOC_LABELS[c.path] || c.path}</strong>: you have “{c.current}”, the document says “{c.incoming}”.</li>
                ))}
                {result.conflicts.length > 12 && <li>…and {result.conflicts.length - 12} more.</li>}
              </ul>
            </div>
          )}
          {result.crossDoc.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontWeight: 700, color: '#B42318' }}>⚠ {result.crossDoc.length} value(s) DISAGREE between the uploaded documents — the first was used, please verify:</div>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                {result.crossDoc.slice(0, 12).map((c, i) => (
                  <li key={i} style={{ marginBottom: 3 }}><strong>{DDOC_LABELS[c.path] || c.path}</strong>: one document says “{c.a}”, another “{c.b}”.</li>
                ))}
                {result.crossDoc.length > 12 && <li>…and {result.crossDoc.length - 12} more.</li>}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// The archived source documents, listed on the last page as an internal reference so a reviewer can
// check the original against the extracted values ("was it the document or the reading that was
// wrong?"). Internal only — never rendered in the client report.
function SourceDocsRef({ reportId, refreshKey }: { reportId: string; refreshKey?: number }) {
  const [files, setFiles] = useState<any[]>([]);
  useEffect(() => {
    if (!reportId) return;
    api.ddAllFiles(reportId).then((r: any) => setFiles((r.files || []).filter((f: any) => String(f.field_path || '').startsWith('source.')))).catch(() => {});
  }, [reportId, refreshKey]);
  if (!files.length) return null;
  const typeOf = (fp: string) => ddocLabel(String(fp || '').replace(/^source\./, ''));
  return (
    <div style={{ marginTop: 28, paddingTop: 18, borderTop: '2px solid var(--border, #e5e5e5)', fontFamily: RC }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Source Documents (internal reference)</div>
      <div style={{ fontSize: 12.5, color: '#888', marginBottom: 12 }}>The documents the report was auto-filled from. Internal use only — never shown to the client.</div>
      {files.map(f => (
        <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, fontSize: 13, color: INK }}>
          <i className="ti ti-file-text" style={{ color: '#6b7c8d' }} />
          <span style={{ fontWeight: 700, color: BLUE, minWidth: 190 }}>{typeOf(f.field_path)}</span>
          <button type="button" onClick={() => api.ddFileView(f.id).catch(() => {})} style={{ border: 'none', background: 'none', color: '#2563EB', cursor: 'pointer', fontSize: 13, textDecoration: 'underline', padding: 0 }}>{f.filename || 'view'}</button>
        </div>
      ))}
    </div>
  );
}

// True if the report has ANY operator-entered content (so we know whether an import can overwrite
// freely or must ask). A brand-new report is {} → false.
function dataHasContent(data: any): boolean {
  const any = (o: any): boolean => {
    if (o == null) return false;
    if (typeof o === 'string') return o.trim() !== '';
    if (typeof o === 'number' || typeof o === 'boolean') return true;
    if (Array.isArray(o)) return o.some(any);
    if (typeof o === 'object') return Object.values(o).some(any);
    return false;
  };
  return any(data);
}

// Paste an OLD-system (Cognito Forms) edit link and pull the whole entry across. Empty report → fills
// straight away; otherwise asks whether to overwrite everything or only fill the blanks.
function CognitoImportBox({ reportId, hasInput, onApply, onCounts, onReport, onUploaded }: { reportId: string; hasInput: () => boolean; onApply: (values: Record<string, string>, overwrite: boolean) => { applied: number; conflicts: any[]; dropped: number }; onCounts: (counts: Record<string, number>) => void; onReport: () => Promise<{ tab: string; items: string[] }[]>; onUploaded: () => void }) {
  const [url, setUrl] = useState('');
  const [modal, setModal] = useState(false);
  const [prog, setProg] = useState<{ phase: string; message: string; current?: number; total?: number } | null>(null);
  const [err, setErr] = useState('');
  const [fetched, setFetched] = useState<{ values: Record<string, string>; filesImported: number; filed: number; counts: Record<string, number>; skipped: { name: string; sizeMB: number; reason?: string }[]; coverage: { section: string; targets: number; mapped: number; sourceLines: number; warn: boolean }[] } | null>(null);
  const [pending, setPending] = useState<Record<string, string> | null>(null);
  const [result, setResult] = useState<{ applied: number; dropped: number; filed: number; archived: number; skipped: { name: string; sizeMB: number; reason?: string }[]; missing: { tab: string; items: string[] }[]; coverage: { section: string; targets: number; mapped: number; sourceLines: number; warn: boolean }[] } | null>(null);

  // Apply the extracted values, then raise section counts so the filed attachments render. Counts run
  // AFTER onApply and up-only, so an overwrite-mode fill can never drop a block below what its files need.
  // Finally tally the required fields the old system could NOT provide, so we can flag them to the operator.
  const finalize = async (vals: Record<string, string>, overwrite: boolean, base: NonNullable<typeof fetched>) => {
    const r = onApply(vals, overwrite);
    onCounts(base.counts);
    onUploaded();
    setPending(null); setProg({ phase: 'report', message: 'Checking what still needs your input…' });
    let missing: { tab: string; items: string[] }[] = [];
    try { missing = await onReport(); } catch { /* report is best-effort */ }
    setProg(null);
    setResult({ applied: r.applied, dropped: r.dropped, filed: base.filed, archived: base.filesImported - base.filed, skipped: base.skipped, missing, coverage: base.coverage || [] });
  };
  const load = async () => {
    if (!url.trim()) return;
    setModal(true); setErr(''); setFetched(null); setPending(null); setResult(null);
    setProg({ phase: 'open', message: 'Opening the old report…' });
    try {
      let done: any = null; let streamErr = '';
      await api.ddImportCognitoStream(reportId, url.trim(), ocrSchemaForSections(DDOC_IMPORT_SECTIONS), DDOC_UPLOAD_TARGETS, (e: any) => {
        if (e.phase === 'error') { streamErr = e.message || 'Import failed.'; setErr(streamErr); }
        else if (e.phase === 'done') done = e;
        else setProg({ phase: e.phase, message: e.message || '', current: e.current, total: e.total });
      });
      // If the connection dropped before the final result (a slow run over a flaky link), retrying is safe:
      // attachments already saved are de-duplicated, so nothing doubles up.
      if (!done) { if (!streamErr) setErr('The import was interrupted before it finished — please click Load again. It’s safe to retry; already-downloaded files won’t be duplicated.'); return; }
      const vals: Record<string, string> = done.values || {};
      const base = { values: vals, filesImported: done.filesImported || 0, filed: done.filed || 0, counts: done.counts || {}, skipped: done.skipped || [], coverage: done.coverage || [] };
      setFetched(base);
      onUploaded();   // surface the plainly-filed files (photos, maps, company docs) right away
      if (!Object.keys(vals).length && !base.filesImported) { setErr('No data could be read from that link — check it is an edit link.'); return; }
      if (!hasInput()) await finalize(vals, true, base);   // empty report — apply everything outright
      else { setProg(null); setPending(vals); }            // existing data — ask how to merge the fields
    } catch (e: any) { setErr(e?.message || 'Import failed.'); }
  };
  const decide = (overwrite: boolean) => {
    if (!pending || !fetched) return;
    void finalize(pending, overwrite, fetched);
  };
  const close = () => { setModal(false); setProg(null); setPending(null); setResult(null); setErr(''); setFetched(null); setUrl(''); };
  const pct = prog?.total ? Math.round(((prog.current || 0) / prog.total) * 100) : null;
  const running = modal && !err && !pending && !result;

  return (
    <div style={{ margin: '0 0 24px', padding: '16px 18px', background: '#f4f8fc', border: '1px dashed #b9d3ec', borderRadius: 10, fontFamily: RC }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: BLUE, display: 'flex', alignItems: 'center', gap: 7 }}>
        <i className="ti ti-download" />Import from the old system <span style={{ fontSize: 11.5, fontWeight: 700, color: '#8a97a4', background: '#e6edf4', padding: '1px 8px', borderRadius: 20 }}>INTERNAL</span>
      </div>
      <div style={{ fontSize: 13, color: '#5b6b79', margin: '6px 0 12px', lineHeight: 1.55 }}>
        Paste the old Cognito Forms Due Diligence <strong>edit link</strong> — we pull the whole entry across, including every attachment (large scans included). Any dropdown values that don’t match are listed for you to handle.
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={url} onChange={e => setUrl(e.target.value)} disabled={modal} placeholder="https://www.cognitoforms.com/<account>/<form>#…"
          style={{ flex: 1, minWidth: 280, padding: '8px 11px', fontSize: 13, fontFamily: RC, border: '1px solid #cdddee', borderRadius: 7, background: '#fff', boxSizing: 'border-box' }} />
        <button type="button" onClick={load} disabled={modal || !url.trim()} style={{ ...toolBtn, background: BLUE, color: '#fff', border: 'none', opacity: (modal || !url.trim()) ? 0.6 : 1 }}>
          <i className="ti ti-cloud-download" style={{ fontSize: 15 }} />Load</button>
      </div>

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1200, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '80px 16px', overflow: 'auto' }}>
          <div style={{ width: 520, maxWidth: '100%', background: '#fff', borderRadius: 10, padding: '24px 28px', fontFamily: RC }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: BLUE, marginBottom: 16 }}>Importing from the old system</div>

            {running && (
              <div style={{ textAlign: 'center' }}>
                <style>{`@keyframes cogScan{0%{top:2%;opacity:0}12%{opacity:1}88%{opacity:1}100%{top:82%;opacity:0}}
.cog-scanwrap{position:relative;width:88px;height:88px;margin:2px auto 0;overflow:hidden;border-radius:12px}
.cog-scanline{position:absolute;left:6%;right:6%;height:8px;border-radius:6px;background:linear-gradient(90deg,transparent,#48c9ff,transparent);box-shadow:0 0 14px 2px #48c9ff;animation:cogScan 1.5s ease-in-out infinite}`}</style>
                <div className="cog-scanwrap">
                  <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 88, height: 88, objectFit: 'contain', display: 'block' }} />
                  <div className="cog-scanline" />
                </div>
                <div style={{ fontSize: 14, color: INK, margin: '14px 0 12px', minHeight: 20 }}>{prog?.message || 'Working…'}</div>
                <div style={{ height: 10, background: '#eef2f6', borderRadius: 6, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: pct != null ? `${pct}%` : '35%', background: BLUE, borderRadius: 6, transition: 'width .3s', ...(pct == null ? { animation: 'spin 1s linear infinite', opacity: 0.6 } : {}) }} />
                </div>
                {pct != null && <div style={{ fontSize: 12, color: '#8a97a4', marginTop: 6, textAlign: 'right' }}>{prog?.current}/{prog?.total} · {pct}%</div>}
              </div>
            )}

            {err && <div style={{ fontSize: 14, color: '#B42318', marginBottom: 16 }}><i className="ti ti-alert-circle" style={{ marginRight: 6 }} />{err}</div>}

            {pending && (
              <>
                <div style={{ fontSize: 14, color: INK, marginBottom: 12 }}>Loaded. This report already has data — how should the imported values be applied?</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => decide(true)} style={{ ...toolBtn, background: '#B42318', color: '#fff', border: 'none' }}><i className="ti ti-replace" style={{ fontSize: 15 }} />Overwrite everything</button>
                  <button type="button" onClick={() => decide(false)} style={{ ...toolBtn, background: BLUE, color: '#fff', border: 'none' }}><i className="ti ti-square-plus" style={{ fontSize: 15 }} />Only fill empty fields</button>
                </div>
              </>
            )}

            {result && (
              <div style={{ fontSize: 13.5, color: INK, lineHeight: 1.7 }}>
                <div style={{ fontWeight: 700, color: '#15803D' }}><i className="ti ti-check" style={{ marginRight: 5 }} />Imported {result.applied} field(s){result.dropped ? `, ${result.dropped} skipped (no matching option)` : ''}.</div>
                {/* Per-section mapping coverage (owner 2026-09-05, after DD-00050): a section the old
                    entry clearly had data for but that mapped NOTHING is flagged loudly, never silent. */}
                {result.coverage.length > 0 && (
                  <div style={{ marginTop: 6 }}>
                    <div style={{ fontWeight: 700, color: '#5b6b79', fontSize: 12.5 }}>Field coverage by section:</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                      {result.coverage.map((c, i) => (
                        <span key={i} style={{ fontSize: 12, fontWeight: 700, padding: '2px 9px', borderRadius: 999,
                          background: c.warn ? '#FEF3F2' : c.mapped > 0 ? '#EAF6EF' : '#f1f4f7',
                          color: c.warn ? '#B42318' : c.mapped > 0 ? '#15803D' : '#8a97a4',
                          border: `1px solid ${c.warn ? '#FDA29B' : c.mapped > 0 ? '#A7D8BC' : '#dde5ec'}` }}>
                          {c.warn && <i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />}{c.section}: {c.mapped}
                        </span>
                      ))}
                    </div>
                    {result.coverage.some(c => c.warn) && (
                      <div style={{ marginTop: 6, padding: '8px 10px', background: '#FEF3F2', border: '1px solid #FDA29B', borderRadius: 6, color: '#B42318', fontSize: 12.5 }}>
                        <i className="ti ti-alert-circle" style={{ marginRight: 5 }} />The old entry has data in the flagged section(s) but nothing mapped across — click <strong>Load</strong> to retry, or enter those fields by hand.
                      </div>
                    )}
                  </div>
                )}
                {result.filed > 0 && <div><i className="ti ti-paperclip" style={{ marginRight: 5, color: BLUE }} />{result.filed} attachment(s) filed straight into the report's upload fields — no re-uploading needed.</div>}
                {result.archived > 0 && <div>{result.archived} other attachment(s) kept in Source Documents (internal, listed on the last page).</div>}
                {result.skipped.filter(s => s.reason === 'too_large').length > 0 && (
                  <div style={{ marginTop: 8, color: '#B45309' }}>
                    {result.skipped.filter(s => s.reason === 'too_large').length} file(s) were too large (over 300&nbsp;MB) to import — download these from the old system manually:
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{result.skipped.filter(s => s.reason === 'too_large').slice(0, 10).map((s, i) => <li key={i}>{s.name} ({s.sizeMB} MB)</li>)}</ul>
                  </div>
                )}
                {result.skipped.filter(s => s.reason !== 'too_large').length > 0 && (
                  <div style={{ marginTop: 8, padding: '8px 10px', background: '#FEF3F2', border: '1px solid #FDA29B', borderRadius: 6, color: '#B42318' }}>
                    <i className="ti ti-alert-circle" style={{ marginRight: 5 }} />{result.skipped.filter(s => s.reason !== 'too_large').length} attachment(s) couldn’t be downloaded from the old system this time. Click <strong>Load</strong> again to retry — it’s safe (nothing duplicates). If it keeps failing, upload these manually:
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>{result.skipped.filter(s => s.reason !== 'too_large').slice(0, 12).map((s, i) => <li key={i}>{s.name}</li>)}</ul>
                  </div>
                )}
                {result.missing.length > 0 ? (
                  <div style={{ marginTop: 12, padding: '10px 12px', background: '#FEF3F2', border: '1px solid #FDA29B', borderRadius: 8 }}>
                    <div style={{ fontWeight: 700, color: '#B42318' }}><i className="ti ti-alert-triangle" style={{ marginRight: 5 }} />The old system didn’t provide everything — these required fields still need your input:</div>
                    <div style={{ marginTop: 6 }}>{result.missing.map((g, i) => (
                      <div key={i} style={{ marginTop: 4 }}><strong>{g.tab}:</strong> {g.items.join(', ')}</div>
                    ))}</div>
                  </div>
                ) : (
                  <div style={{ marginTop: 10, color: '#15803D', fontWeight: 600 }}><i className="ti ti-circle-check" style={{ marginRight: 5 }} />All required fields are filled.</div>
                )}
                <div style={{ marginTop: 8, color: '#8a97a4' }}>Please review each page — the filed documents appear in their own upload boxes.</div>
              </div>
            )}

            {(err || result || pending) && (
              <div style={{ marginTop: 18, textAlign: 'right' }}>
                <button type="button" onClick={close} style={{ ...toolBtn }}>{result ? 'Done' : 'Close'}</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportEditor({ id, onBack }: { id: string; onBack: () => void }) {
  // The tenant's site inspectors fill the Conducted By picker (and the import's allowed values) before the
  // sections render; the zoning essay / service links are read where they are printed.
  syncInspectors(useDdContent().inspectors);
  const { t } = useTranslation();
  const [report, setReport] = useState<any>(null);
  const [data, setData] = useState<any>({});
  // Live mirror of `data`, so an auto-fill that finishes after the operator kept editing applies
  // against the CURRENT state instead of the snapshot captured when extraction began.
  const dataRef = useRef<any>({});
  useEffect(() => { dataRef.current = data; }, [data]);
  const [status, setStatus] = useState<Status>('new');
  const [tab, setTab] = useState<string>('parameters');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [err, setErr] = useState('');
  const [validating, setValidating] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [copied, setCopied] = useState<'' | 'edit' | 'view'>('');
  const [emailVal, setEmailVal] = useState('');
  const [recipName, setRecipName] = useState('');
  // Client + property labels for the report-link email — pre-filled from the report, editable here.
  const [clientName, setClientName] = useState('');
  const [propName, setPropName] = useState('');
  // Which of the two links gets emailed. Saving progress is a "resume my work" action, so the
  // staff edit link is the default — the client link is the deliberate choice.
  const [sendMode, setSendMode] = useState<'edit' | 'view'>('edit');
  // Deep-link back into the editor for this exact report (same param DueDiligence already handles).
  const editUrl = `${window.location.origin}/m/slf_property?v=due_diligence&open=${id}`;
  const [emailState, setEmailState] = useState<'idle' | 'sending' | 'sent' | 'err'>('idle');
  const [emailMsg, setEmailMsg] = useState('');
  // The link email itself, editable before it goes (owner 2026-10-03: every outbound email opens an
  // editor first). The server drafts it from the boxes above exactly as the send builds it; a client
  // link is a placeholder (ddPh) until Send mints it. Once the operator edits the email, the boxes
  // above stop rebuilding it — "Rebuild" does that on request.
  const [ddSubj, setDdSubj] = useState('');
  const [ddBody, setDdBody] = useState('');
  const [ddPh, setDdPh] = useState('');
  const [ddCc, setDdCc] = useState('');
  const [ddEdited, setDdEdited] = useState(false);
  const [ddDrafting, setDdDrafting] = useState(false);
  const [ddDraftErr, setDdDraftErr] = useState('');
  const ddBodyRef = useRef<DraftBodyHandle | null>(null);
  // Bumped by "Rebuild": remounts the body editor, so the operator's edits go even when the rebuilt
  // email is the same string as before (the editor only rewrites its content when that string changes).
  const [ddBodyKey, setDdBodyKey] = useState(0);
  useEffect(() => {
    if (!shareOpen || ddEdited) return;
    let alive = true;
    setDdDrafting(true);
    const timer = setTimeout(() => {
      popupsApi.ddLinkPrepare(id, { mode: sendMode, name: recipName.trim(), clientName: clientName.trim(), propName: propName.trim() })
        .then(d => { if (!alive) return; setDdSubj(d.subject || ''); setDdBody(d.bodyHtml || ''); setDdPh(d.linkPlaceholder || ''); setDdCc((d.cc || []).join(', ')); setDdDraftErr(''); })
        .catch((e: any) => { if (alive) setDdDraftErr(e?.message || 'Failed to prepare the email.'); })
        .finally(() => { if (alive) setDdDrafting(false); });
    }, 350);
    return () => { alive = false; clearTimeout(timer); setDdDrafting(false); };
  }, [shareOpen, ddEdited, id, sendMode, recipName, clientName, propName]);
  const [refreshKey, setRefreshKey] = useState(0);
  // Which field paths currently have an uploaded file — used to validate required file fields.
  // null = not loaded yet (so required-file checks are skipped until we actually know).
  const [filePaths, setFilePaths] = useState<Set<string> | null>(null);
  const fetchFilePaths = React.useCallback(async (): Promise<Set<string>> => {
    try { const r: any = await api.ddAllFiles(id); const set = new Set<string>((r.files || []).map((f: any) => String(f.field_path))); setFilePaths(set); return set; }
    catch { return filePaths || new Set(); }
  }, [id, filePaths]);
  useEffect(() => { void fetchFilePaths(); }, [id, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyList, setCopyList] = useState<any[]>([]);
  const [copyQ, setCopyQ] = useState('');
  const [copySel, setCopySel] = useState<any>(null);
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyMsg, setCopyMsg] = useState('');

  useEffect(() => {
    api.ddGet(id).then((r: any) => { setReport(r); setData(r.data || {}); setStatus(r.status || 'new'); revRef.current = Number(r.data_rev || 0); }).catch((e: any) => setErr(e.message));
  }, [id]);
  // Save = persist + open the "progress saved" dialog with the shareable link.
  const saveAndShare = async () => {
    await persist(data);
    try { const r: any = await api.ddShare(id); setShareUrl(r.url); } catch { /* ignore */ }
    // Pre-fill the greeting (Title + first + last) and the recipient email from the buyer (the report's client).
    const b = data.buyer || {}; const ind = (b.individuals || [])[0]; const co = (b.companies || [])[0];
    const buyerName = ddName(ind?.name) || (co?.companyName || '');
    setRecipName(buyerName);
    // Client name = the buyer; property name already reads "Name - Number" on real reports.
    setClientName(buyerName);
    setPropName(String((data.property || {}).name || '').trim());
    setCopied(''); setSendMode('edit'); setEmailVal(String(ind?.email || co?.email || '').trim()); setEmailState('idle'); setEmailMsg('');
    setDdEdited(false); setDdSubj(''); setDdBody(''); setShareOpen(true);
  };
  // Preview = persist + ensure the public share link exists, then open the read-only overlay (which
  // now shows that unique /dd/view link so it can be copied & shared without login).
  const openPreview = async () => {
    setPreviewOpen(true);
    await persist(data);
    if (!shareUrl) { try { const r: any = await api.ddShare(id); setShareUrl(r.url); } catch { /* ignore */ } }
  };
  const copyTo = (url: string, which: 'edit' | 'view') => { navigator.clipboard?.writeText(url).then(() => { setCopied(which); setTimeout(() => setCopied(''), 2000); }).catch(() => {}); };
  const sendLink = async () => {
    // Every box is mandatory: the subject and body are built from them.
    const missing = [
      !recipName.trim() && 'Recipient name', !clientName.trim() && 'Client name',
      !propName.trim() && 'Property name & number', !emailVal.trim() && 'Email',
    ].filter(Boolean);
    if (missing.length) { setEmailState('err'); setEmailMsg(`Please fill in: ${missing.join(', ')}.`); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal.trim())) { setEmailState('err'); setEmailMsg('Please enter a valid email.'); return; }
    // The email that goes is the one in the editor below — never one the operator has not seen.
    if (ddDrafting || !ddBody) return;
    if (!ddSubj.trim()) { setEmailState('err'); setEmailMsg(t('pop_need_subject')); return; }
    setEmailState('sending'); setEmailMsg('');
    try {
      const r: any = await popupsApi.ddLinkSend(id, {
        email: emailVal.trim(), name: recipName.trim(), clientName: clientName.trim(), propName: propName.trim(), mode: sendMode,
        subject: ddSubj, bodyHtml: ddBodyRef.current ? ddBodyRef.current.get() : ddBody,
      });
      setEmailState('sent'); setEmailMsg(`${sendMode === 'edit' ? 'Edit' : 'Client'} link sent to ${r.sent} (cc ${r.cc}).`);
    } catch (e: any) { setEmailState('err'); setEmailMsg(e?.message || 'Failed to send.'); }
  };
  useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [tab]);

  // Serialize saves through one chain so two rapid persists (e.g. import applies fields then bumps counts)
  // always commit in call order — never race and let an earlier, staler write land last.
  // `revRef` is the optimistic lock: every save carries the revision this browser last saw, and the
  // server refuses (409) if somebody else moved the report — at which point autosave STOPS, because
  // every further save would try to overwrite their work with this browser's stale copy.
  const persistChain = useRef<Promise<any>>(Promise.resolve());
  const revRef = useRef(0);
  const conflictRef = useRef(false);
  const persist = (nextData: any, nextStatus?: Status) => {
    if (conflictRef.current) return persistChain.current;   // frozen until the operator reloads
    setSaveStatus('saving'); setErr('');
    persistChain.current = persistChain.current.catch(() => { /* ignore prior failure */ }).then(async () => {
      if (conflictRef.current) return;
      try {
        const r: any = await api.ddUpdate(id, { data: nextData, baseRev: revRef.current, ...(nextStatus ? { status: nextStatus } : {}) });
        if (r && r.rev !== undefined) revRef.current = Number(r.rev);
        setSaveStatus('saved');
      } catch (e: any) {
        if (e?.status === 409 || /Reload the page/.test(String(e?.message || ''))) conflictRef.current = true;
        setErr(e.message); setSaveStatus('idle');
      }
    });
    return persistChain.current;
  };
  // Apply extracted {sectionKey.path: value} against the LIVE report (dataRef), fill-empty by default
  // or overwrite when the operator chose to. Shared by the Source Documents upload and the old-system
  // (Cognito) import.
  const applyFromValues = (values: Record<string, string>, overwrite = false) => {
    const res = applyWholeReport(dataRef.current, values, overwrite);
    dataRef.current = res.data; setData(res.data); persist(res.data);
    return { applied: res.applied.length, conflicts: res.conflicts, dropped: res.dropped };
  };
  // When an import files attachments into repeated blocks (e.g. two title deeds), raise that section's
  // instance count so the blocks — and therefore the just-attached files — actually render. Up-only:
  // it never shrinks a count the operator set, and never deletes data.
  const applyImportCounts = (counts: Record<string, number>) => {
    if (!counts || !Object.keys(counts).length) return;
    const next = JSON.parse(JSON.stringify(dataRef.current || {}));
    let changed = false;
    Object.entries(counts).forEach(([section, needed]) => {
      const cf = ((SECTIONS[section] as any)?.top || []).flat().find((f: any) => f.k === 'count');
      if (!cf?.opts?.length) return;
      const cur = WORDNUM[String(next[section]?.count || '').toLowerCase()] || 0;
      const tgt = Math.min(needed, cf.opts.length);   // clamp: files past the max overflow to the archive server-side
      if (tgt > cur) { next[section] = { ...(next[section] || {}), count: cf.opts[tgt - 1] }; changed = true; }
    });
    if (changed) { dataRef.current = next; setData(next); persist(next); }
  };
  // After an import, list every REQUIRED field that is still empty — grouped by page — so the operator is
  // told up-front what the old system couldn't provide, instead of discovering blockers page by page.
  const importReport = async (): Promise<{ tab: string; items: string[] }[]> => {
    let paths: Set<string> | null = filePaths;
    try { paths = await fetchFilePaths(); } catch { /* keep what we have */ }   // fresh: includes just-filed attachments
    const d = dataRef.current || {};
    const params = d.parameters || {};
    const visible = ALL_SECTIONS.filter(s => !s.cond || s.cond(params));
    const PARAM_LABELS: Record<string, string> = { buyingPropertyFrom: 'Buying Property From', buildingPermit: 'Has Building Permit(s)?', houseBooks: 'Has House Book(s)?', landUpgraded: 'Land Upgraded / Subdivided?', purchaseAgreement: 'Has Land Office Purchase Agreement(s)?', privateRoad: 'Has Private Road Registration(s)?' };
    const out: { tab: string; items: string[] }[] = [];
    for (const s of visible) {
      const errs = computeFieldErrors(s.key, d, paths);
      const items = Array.from(new Set(Object.entries(errs).map(([k, m]) =>
        m === 'This field is required.' ? (PARAM_LABELS[k] || k) : m.replace(/ is required\.$/, ''))));
      if (items.length) out.push({ tab: s.tab, items });
    }
    return out;
  };
  const setField = (section: string) => (key: string, value: any) => {
    setData((prev: any) => { const next = { ...prev, [section]: { ...(prev[section] || {}), [key]: value } }; persist(next); return next; });
  };
  const setSection = (section: string) => (patch: any) => {
    setData((prev: any) => { const next = { ...prev, [section]: { ...(prev[section] || {}), ...patch } }; persist(next); return next; });
  };
  const changeStatus = (s: Status) => { setStatus(s); persist(data, s); };

  if (!report) return <div style={{ flex: 1, background: DARK, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}><i className="ti ti-loader" style={{ fontSize: 24 }} /></div>;

  // Visible tabs depend on the Parameters answers (conditional sections).
  const params = data.parameters || {};
  const visible = ALL_SECTIONS.filter(s => !s.cond || s.cond(params));
  const curTab = visible.some(s => s.key === tab) ? tab : 'parameters';
  const stepIndex = Math.max(0, visible.findIndex(s => s.key === curTab));
  const cur = visible[stepIndex];
  // Navigation is blocked whenever the current page has validation errors. The
  // inline per-field messages stay live (recomputed each render) while validating.
  const fieldErrs = validating ? computeFieldErrors(curTab, data, filePaths) : {};
  const goto = async (k: string) => {
    // Going BACK is always allowed. The form autosaves, so nothing is lost, and trapping someone on
    // a half-filled page they cannot leave is worse than letting them revisit an earlier one.
    // Only moving FORWARD is gated on the current page being valid.
    const target = visible.findIndex(s => s.key === k);
    if (target >= 0 && target < stepIndex) { setValidating(false); setTab(k); return; }
    // Re-fetch the uploaded-file list first, so a file just added is recognised even if its widget
    // hasn't notified us yet — the forward gate always validates against the true current state.
    const paths = await fetchFilePaths();
    const errs = computeFieldErrors(curTab, data, paths);
    if (Object.keys(errs).length) { setValidating(true); scrollRef.current?.scrollTo({ top: 0 }); return; }
    setValidating(false); setTab(k);
  };
  const next = () => goto(visible[stepIndex + 1].key);

  // OCR auto-fill from an uploaded photo/file (Buyer + Seller…last).
  // Per-page "Add by photo/file" OCR shortcuts were removed (#31) — all document ingestion now goes
  // through the single Source Documents upload on page 1, to avoid scattered, error-prone imports.
  // Copy section(s) from another DD report (Seller…last).
  const allIdx = Math.max(0, ALL_SECTIONS.findIndex(s => s.key === curTab));
  const openCopy = async () => { setCopyOpen(true); setCopySel(null); setCopyMsg(''); setCopyQ(''); try { const d: any = await api.ddList('', '', 1); setCopyList((d.rows || []).filter((r: any) => r.id !== id).slice(0, 10)); } catch { /* ignore */ } };
  const searchCopy = async (q: string) => { setCopyQ(q); try { const d: any = await api.ddList(q, '', 1); setCopyList((d.rows || []).filter((r: any) => r.id !== id).slice(0, 10)); } catch { /* ignore */ } };
  const wholeCopy = curTab === 'parameters';   // page 1 = copy the ENTIRE report (minus the exclusions)
  const doCopy = async (toEnd: boolean) => {
    if (!copySel) return;
    // Whole-report copy deliberately skips the Buyer section (a different client — including their Job IDs)
    // and keeps this report's own Property name.
    const sections = wholeCopy
      ? ALL_SECTIONS.filter(s => s.key !== 'buyer').map(s => s.key)
      : (toEnd ? ALL_SECTIONS.slice(allIdx) : [ALL_SECTIONS[allIdx]]).map(s => s.key);
    setCopyBusy(true); setCopyMsg('');
    try {
      await api.ddCopyFrom(id, copySel.id, sections, wholeCopy ? ['property.name'] : []);
      const r: any = await api.ddGet(id); setData(r.data || {}); revRef.current = Number(r.data_rev || 0); setRefreshKey(k => k + 1); setValidating(false);
      setCopyMsg(`Copied ${sections.length} section(s) from ${copySel.report_no}.`);
      setTimeout(() => setCopyOpen(false), 1100);
    } catch (e: any) { setCopyMsg(e?.message || 'Copy failed.'); }
    finally { setCopyBusy(false); }
  };
  const showCopy = allIdx >= 2 || curTab === 'parameters';   // Seller onward + page 1 (whole-report copy)

  return (
    <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', background: DARK, fontFamily: RC }}>
      {/* thin app toolbar on the dark backdrop */}
      <div style={{ maxWidth: CARD, margin: '0 auto', padding: '12px 4px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ border: '1px solid rgba(255,255,255,.25)', background: 'transparent', borderRadius: 7, padding: '5px 11px', cursor: 'pointer', color: '#e8e8e8', fontSize: 12.5, fontFamily: RC }}><i className="ti ti-arrow-left" style={{ marginRight: 5 }} />{t('dd_back')}</button>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>{report.report_no}</span>
        <div style={{ flex: 1 }} />
        {saveStatus !== 'idle' && (
          <span style={{ fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4, color: saveStatus === 'saving' ? '#bdbdbd' : '#7fd6a8' }}>
            <i className={`ti ti-${saveStatus === 'saving' ? 'loader-2' : 'circle-check'}`} />{saveStatus === 'saving' ? t('dd_saving') : t('dd_saved')}</span>
        )}
        <label style={{ fontSize: 12, color: '#bdbdbd' }}>{t('dd_status')}</label>
        <select value={status} onChange={e => changeStatus(e.target.value as Status)} style={{ padding: '6px 10px', fontSize: 13, borderRadius: 7, border: '1px solid rgba(255,255,255,.25)', background: '#4a4a4a', color: '#fff', fontFamily: RC }}>
          {STATUSES.map(s => <option key={s} value={s} style={{ color: '#000' }}>{t('dd_status_' + s)}</option>)}
        </select>
      </div>

      {/* the Cognito card */}
      <div style={{ maxWidth: CARD, margin: '0 auto 48px', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,.35)' }}>
        {/* blue header band */}
        <div style={{ background: BLUE, color: '#fff', textAlign: 'center', padding: '34px 20px', fontSize: 31, fontWeight: 400, letterSpacing: 0.3 }}>Due Diligence Report</div>
        {/* numbered stepper */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 18px', padding: '16px 26px', borderBottom: '1px solid #ececec' }}>
          {visible.map((s, i) => {
            const active = s.key === curTab;
            return (
              <button key={s.key} onClick={() => goto(s.key)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                {/* The step's number, trimmed to its cap height so the flex box centres the digits themselves (Bugify #836d4443, 2nd opening) */}
                <span style={{ width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  background: active ? '#fff' : '#e3e3e3', color: active ? BLUE : '#666', border: active ? `2px solid ${BLUE}` : '2px solid transparent', boxSizing: 'border-box' }}>
                  <span style={{ textBoxTrim: 'trim-both', textBoxEdge: 'cap alphabetic' }}>{i + 1}</span>
                </span>
                <span style={{ fontSize: 14, fontFamily: RC, color: active ? BLUE : INK, fontWeight: active ? 700 : 400 }}>{s.tab}</span>
              </button>
            );
          })}
        </div>
        {/* section body */}
        <div style={{ padding: '26px 40px 48px' }}>
          {err && <div style={{ fontSize: 13, color: '#D0021B', background: '#FCEBEB', padding: '8px 12px', marginBottom: 16, fontFamily: RC }}>{err}</div>}
          {Object.keys(fieldErrs).length > 0 && (
            <div style={{ background: '#FCEBEB', border: '1px solid #f3c0c0', borderRadius: 4, padding: '12px 16px', marginBottom: 18, fontFamily: RC }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#D0021B' }}><i className="ti ti-alert-circle" style={{ marginRight: 6 }} />Please correct the highlighted field(s) below before continuing.</div>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <SubHead>{cur.title}</SubHead>
            {showCopy && (
              <div style={{ display: 'flex', gap: 8, paddingTop: 6, flexWrap: 'wrap' }}>
                <button onClick={openCopy} style={toolBtn}><i className="ti ti-copy" style={{ fontSize: 16 }} />Copy from another DD</button>
              </div>
            )}
          </div>
          <DDContext.Provider value={{ reportId: id, refreshKey, onFileChange: () => setRefreshKey(k => k + 1) }}>
          <DDErrors.Provider value={fieldErrs}>
          {curTab === 'parameters' && <CognitoImportBox reportId={id} hasInput={() => dataHasContent(dataRef.current)} onApply={(vals, ow) => applyFromValues(vals, ow)} onCounts={applyImportCounts} onReport={importReport} onUploaded={() => setRefreshKey(k => k + 1)} />}
          {curTab === 'parameters' && <SourceDocsPanel reportId={id} onApply={vals => applyFromValues(vals, false)} onUploaded={() => setRefreshKey(k => k + 1)} />}
          {curTab === 'parameters' ? <ParametersSection data={data} set={setField('parameters')} />
            : curTab === 'buyer' ? <BuyerSection buyer={data.buyer || {}} patch={setSection('buyer')} />
            : curTab === 'seller' ? <SellerSection seller={data.seller || {}} patch={setSection('seller')} />
            : curTab === 'property' ? <PropertySection property={data.property || {}} patch={setSection('property')} />
            : curTab === 'comments' ? <CommentsSection value={data.comments || {}} patch={setSection('comments')} root={data} />
            : SECTIONS[curTab] ? <GenericSection sectionKey={curTab} value={data[curTab] || {}} patch={setSection(curTab)} root={data} />
            : <div style={{ padding: '40px 0', textAlign: 'center', color: '#999', fontSize: 15, fontFamily: RC }}><i className="ti ti-tool" style={{ fontSize: 26, display: 'block', marginBottom: 10 }} />{t('dd_coming_soon')}</div>}
          </DDErrors.Provider>
          </DDContext.Provider>

          {/* INTERNAL QA sign-off — last page only, and only here in the staff editor. It is never
              part of the report the client sees (DDView does not render it at all). */}
          {stepIndex === visible.length - 1 && (
            <div style={{ marginTop: 40, paddingTop: 22, borderTop: '2px solid var(--border, #e5e5e5)' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6, fontFamily: RC }}>Internal QA Sign-off</div>
              <div style={{ fontSize: 12.5, color: '#888', marginBottom: 16, fontFamily: RC }}>Internal use only — this section never appears in the report sent to the client.</div>
              <SignoffChain reportId={id} />
              <SourceDocsRef reportId={id} refreshKey={refreshKey} />
            </div>
          )}

          {/* Next / Back / Preview / Save — like the original Cognito form */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 44 }}>
            {stepIndex > 0 && <button onClick={() => goto(visible[stepIndex - 1].key)} style={navBtn(false)}><i className="ti ti-chevron-left" />Back</button>}
            {stepIndex < visible.length - 1 && <button onClick={next} style={navBtn(true)}>Next <i className="ti ti-chevron-right" /></button>}
            <div style={{ flex: 1 }} />
            <button onClick={openPreview} style={navBtn(false)}><i className="ti ti-eye" />Preview</button>
            <button onClick={saveAndShare} style={navBtn(false)}>Save</button>
          </div>
          <div style={{ textAlign: 'right', fontSize: 13, color: '#9a9a9a', marginTop: 10, fontFamily: RC }}>{stepIndex + 1}</div>
        </div>
      </div>

      {/* Full-report preview overlay */}
      {previewOpen && (
        <div style={{ position: 'fixed', inset: 0, background: DARK, zIndex: 1000, overflow: 'auto' }}>
          <div style={{ position: 'sticky', top: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', background: BLUE, color: '#fff', zIndex: 2, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 18, fontFamily: RC, flexShrink: 0 }}><i className="ti ti-eye" style={{ marginRight: 8 }} />Preview — Due Diligence Report</div>
            <div style={{ flex: 1, minWidth: 12 }} />
            {shareUrl && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 1, minWidth: 0 }} title="Public link — anyone with it can view this report, no login required">
                <i className="ti ti-link" style={{ fontSize: 16, flexShrink: 0 }} />
                <input readOnly value={shareUrl} onFocus={e => e.target.select()} style={{ width: 300, maxWidth: '38vw', padding: '7px 10px', borderRadius: 5, border: 'none', fontSize: 12.5, fontFamily: RC, color: INK }} />
                <button onClick={() => copyTo(shareUrl, 'view')} style={{ ...navBtn(false), background: '#fff', flexShrink: 0 }}>{copied === 'view' ? <><i className="ti ti-check" />Copied</> : <><i className="ti ti-copy" />Copy link</>}</button>
              </div>
            )}
            <button onClick={() => setPreviewOpen(false)} style={{ ...navBtn(false), background: '#fff', flexShrink: 0 }}><i className="ti ti-x" />Close</button>
          </div>
          {/* Same dress as the client page: the report block carries its own dark frame and white
              sheets — an extra white box here would put a stray border around it. */}
          <div style={{ maxWidth: CARD, margin: '18px auto 44px' }}>
            <DDView data={data} reportId={id} />
          </div>
        </div>
      )}

      {/* "Your progress has been saved" dialog with shareable link + email */}
      {shareOpen && (
        <div onClick={() => setShareOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 16px', overflow: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: '100%', background: '#fff', borderRadius: 6, padding: '28px 32px 32px', fontFamily: RC, position: 'relative' }}>
            <button onClick={() => setShareOpen(false)} style={{ position: 'absolute', top: 14, right: 16, border: 'none', background: 'none', fontSize: 22, color: '#888', cursor: 'pointer' }}>×</button>
            <div style={{ fontSize: 26, fontWeight: 700, color: BLUE, marginBottom: 12 }}>Your progress has been saved.</div>
            <div style={{ fontSize: 15, color: INK, lineHeight: 1.5, marginBottom: 22 }}>Two links: the <strong>edit</strong> link re-opens this report so you (or a colleague) can carry on working on it, the <strong>client</strong> link is the read-only version to send to the buyer.</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>Edit link — resume this report (staff, login required):</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
              <input readOnly value={editUrl} onFocus={e => e.target.select()} style={{ ...inStyle, flex: 1, background: '#f1f1f1' }} />
              <button onClick={() => copyTo(editUrl, 'edit')} style={{ ...navBtn(true) }}>{copied === 'edit' ? <><i className="ti ti-check" />Copied</> : <><i className="ti ti-copy" />Copy</>}</button>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>Client link — read-only, no login needed:</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 22 }}>
              <input readOnly value={shareUrl} onFocus={e => e.target.select()} style={{ ...inStyle, flex: 1, background: '#f1f1f1' }} />
              <button onClick={() => copyTo(shareUrl, 'view')} style={{ ...navBtn(false) }}>{copied === 'view' ? <><i className="ti ti-check" />Copied</> : <><i className="ti ti-copy" />Copy</>}</button>
            </div>
            {/* Client name + property are pulled from the report and drive the email subject and
                body. All four boxes are mandatory — a blank one mails a half-labelled report. */}
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>Recipient name: <span style={{ color: '#D0021B' }}>*</span></div>
            <input placeholder="e.g. Jane" value={recipName} onChange={e => { setRecipName(e.target.value); setEmailState('idle'); }} style={{ ...inStyle, marginBottom: 16, ...(emailState === 'err' && !recipName.trim() ? shareErrInput : {}) }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>Client name: <span style={{ color: '#D0021B' }}>*</span></div>
            <input placeholder="e.g. John Smith" value={clientName} onChange={e => { setClientName(e.target.value); setEmailState('idle'); }} style={{ ...inStyle, marginBottom: 16, ...(emailState === 'err' && !clientName.trim() ? shareErrInput : {}) }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>Property name &amp; number: <span style={{ color: '#D0021B' }}>*</span></div>
            <input placeholder="e.g. Peony - Plot 51" value={propName} onChange={e => { setPropName(e.target.value); setEmailState('idle'); }} style={{ ...inStyle, marginBottom: 16, ...(emailState === 'err' && !propName.trim() ? shareErrInput : {}) }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>Which link to email:</div>
            <div style={{ display: 'flex', gap: 18, marginBottom: 14, fontSize: 14.5, color: INK }}>
              {([['edit', 'Edit link (resume work)'], ['view', 'Client link (read-only)']] as const).map(([m, lbl]) => (
                <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input type="radio" checked={sendMode === m} onChange={() => { setSendMode(m); setEmailState('idle'); }} />{lbl}
                </label>
              ))}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>{t('pop_dd_email')}</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{t('pop_subject')}</div>
            <input value={ddSubj} onChange={e => { setDdSubj(e.target.value); setDdEdited(true); setEmailState('idle'); }} style={{ ...inStyle, marginBottom: 10 }} />
            <div style={{ fontSize: 13, color: '#666', marginBottom: 4 }}>{t('pop_body')}</div>
            <DraftBody key={ddBodyKey} ref={ddBodyRef} html={ddBody} onEdit={() => { setDdEdited(true); setEmailState('idle'); }}
              style={{ background: '#fff', color: INK, fontFamily: RC, fontSize: 14, minHeight: 150, maxHeight: 300, border: '1px solid #ccc', borderRadius: 4 }} />
            <div style={{ fontSize: 12.5, color: '#666', marginTop: 6, lineHeight: 1.5 }}>
              {ddPh && <div>{t('pop_dd_link_hint', { ph: ddPh })}</div>}
              {ddCc && <div>{t('pop_cc')}: {ddCc}</div>}
              {ddDrafting && !ddEdited && <div>{t('pop_loading')}</div>}
              {ddEdited && (
                <div>{t('pop_dd_edited')}{' '}
                  <button type="button" onClick={() => { setDdEdited(false); setDdBody(''); setDdBodyKey(k => k + 1); setEmailState('idle'); }}
                    style={{ border: 'none', background: 'none', padding: 0, color: BLUE, cursor: 'pointer', textDecoration: 'underline', fontFamily: RC, fontSize: 12.5 }}>{t('pop_dd_rebuild')}</button>
                </div>
              )}
              {ddDraftErr && <div style={{ color: '#D0021B' }}>{ddDraftErr}</div>}
            </div>
            <div style={{ height: 16 }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: '#000', marginBottom: 6 }}>Email the link to: <span style={{ color: '#D0021B' }}>*</span></div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="email" placeholder="name@example.com" value={emailVal} onChange={e => { setEmailVal(e.target.value); setEmailState('idle'); }} style={{ ...inStyle, flex: 1, ...(emailState === 'err' && !emailVal.trim() ? shareErrInput : {}) }} />
              <button onClick={sendLink} disabled={emailState === 'sending' || ddDrafting || !ddBody} style={{ ...navBtn(true), opacity: emailState === 'sending' || ddDrafting || !ddBody ? .6 : 1 }}>{emailState === 'sending' ? 'Sending…' : 'Send'}</button>
            </div>
            {emailMsg && <div style={{ marginTop: 10, fontSize: 13.5, color: emailState === 'sent' ? '#15803D' : '#D0021B' }}>{emailMsg}</div>}
          </div>
        </div>
      )}

      {/* Copy from another DD report */}
      {copyOpen && (
        <div onClick={() => setCopyOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '60px 16px', overflow: 'auto' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: '100%', background: '#fff', borderRadius: 6, padding: '26px 30px 30px', fontFamily: RC, position: 'relative' }}>
            <button onClick={() => setCopyOpen(false)} style={{ position: 'absolute', top: 14, right: 16, border: 'none', background: 'none', fontSize: 22, color: '#888', cursor: 'pointer' }}>×</button>
            <div style={{ fontSize: 22, fontWeight: 700, color: BLUE, marginBottom: 6 }}>Copy from another Due Diligence</div>
            <div style={{ fontSize: 14, color: INK, marginBottom: 16 }}>{wholeCopy
              ? <>Pick a report to copy the <strong>whole report</strong> from — text, dropdowns and attachments come across exactly, <strong>except Buyer details (incl. Job ID) and the Property name</strong>, which stay as they are here.</>
              : <>Pick a report to copy <strong>{cur.title}</strong> from. Text, dropdowns and attachments are copied exactly.</>}</div>
            <input autoFocus placeholder="Search by report no, buyer or property…" value={copyQ} onChange={e => searchCopy(e.target.value)} style={{ ...inStyle, marginBottom: 12 }} />
            <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid #e6e6e6', borderRadius: 4 }}>
              {copyList.length === 0 && <div style={{ padding: '18px', textAlign: 'center', color: '#999', fontSize: 14 }}>No reports found.</div>}
              {copyList.map(r => {
                const sel = copySel?.id === r.id;
                return (
                  <button key={r.id} onClick={() => { setCopySel(r); setCopyMsg(''); }} style={{ display: 'block', width: '100%', textAlign: 'left', border: 'none', borderBottom: '1px solid #f0f0f0', background: sel ? '#eaf2fb' : '#fff', cursor: 'pointer', padding: '10px 14px', fontFamily: RC }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: sel ? BLUE : INK }}>{r.report_no}{sel && <i className="ti ti-check" style={{ marginLeft: 6 }} />}</div>
                    <div style={{ fontSize: 12.5, color: '#888' }}>{[r.buyer_name, r.property_name].filter(Boolean).join(' · ') || '—'}</div>
                  </button>
                );
              })}
            </div>
            {copySel && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 13.5, color: INK, marginBottom: 10 }}>Copy from <strong>{copySel.report_no}</strong> into this report:</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {wholeCopy
                    ? <button onClick={() => doCopy(true)} disabled={copyBusy} style={{ ...navBtn(true), opacity: copyBusy ? .6 : 1 }}><i className="ti ti-files" />Copy the whole report</button>
                    : <>
                        <button onClick={() => doCopy(false)} disabled={copyBusy} style={{ ...navBtn(false), opacity: copyBusy ? .6 : 1 }}><i className="ti ti-file" />This page only</button>
                        <button onClick={() => doCopy(true)} disabled={copyBusy} style={{ ...navBtn(true), opacity: copyBusy ? .6 : 1 }}><i className="ti ti-files" />This page → last page</button>
                      </>}
                </div>
              </div>
            )}
            {copyMsg && <div style={{ marginTop: 12, fontSize: 13.5, color: /fail/i.test(copyMsg) ? '#D0021B' : '#15803D' }}>{copyBusy ? <i className="ti ti-loader-2" style={{ marginRight: 6 }} /> : null}{copyMsg}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Standalone page (/m/due_diligence) ───────────────────────────────────────
export default function DueDiligence({ embedded }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openId, setOpenId] = useState<string | null>(null);

  // Deep-links: ?open=<id> opens that report; ?newJob=<jobNo> creates a fresh
  // report with the Job ID pre-filled (legacy path — Jobs now create + link first).
  const [params, setParams] = useSearchParams();
  const handledRef = useRef(false);
  useEffect(() => {
    if (handledRef.current) return;
    const openParam = params.get('open');
    if (openParam) {
      handledRef.current = true;
      setOpenId(openParam);
      params.delete('open'); setParams(params, { replace: true });
      return;
    }
    const newJob = params.get('newJob');
    if (!newJob) return;
    handledRef.current = true;
    (async () => {
      try {
        const r: any = await api.ddCreate();
        await api.ddUpdate(r.id, { data: { buyer: { numberOfBuyers: 'One', buyerType: 'Individual', individuals: [{ jobId: newJob }], companies: [{ jobId: newJob }] } } });
        setOpenId(r.id);
      } catch { /* ignore */ }
      params.delete('newJob'); setParams(params, { replace: true });
    })();
  }, [params]); // eslint-disable-line
  return (
    <div style={{ ...(embedded ? { flex: 1, minHeight: 0 } : { height: '100vh' }), display: 'flex', flexDirection: 'column', background: 'var(--bg-app)' }}>
      {!embedded && <header style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <button onClick={() => navigate('/')} title={brandOr('Home')} style={{ width: 30, height: 30, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}>
          <img className="app-logo" src="/logo.png" alt={brandOr('Logo')} style={{ width: 28, height: 28, objectFit: 'contain' }} />
        </button>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', fontFamily: RC }}>{t('mod_due_diligence')}</div>
      </header>}
      {openId ? <ReportEditor id={openId} onBack={() => setOpenId(null)} /> : <ReportList onOpen={setOpenId} />}
    </div>
  );
}
